import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { QRCodeCanvas } from 'qrcode.react';
import { deviceApi, caseApi, supportQuestionsApi, tsfadApi, messageApi, notificationApi } from '../services/api';
import { getBaseUrl } from '../services/baseUrl';
import { changeOwnPassword, deactivateOwnPortalAccess, storeLoginNotice } from '../services/auth';
import {
  fetchOwnAccountProfile,
  fetchSettings,
  updateOwnAccountProfile,
  updateUserSettings,
  type AccountProfilePayload,
  type SettingsPayload
} from '../services/settings';
import type { Device, Customer, Case, CaseServiceReport, CaseSurveySummary, CaseTask } from '../types';
import { PortalMessage, PortalMessageAttachment } from '../utils/portalMessages';
import { buildMeaningfulTimeline, formatTimelineEventLabel } from '../utils/caseTimeline';
import { getIncludedQuoteLineColumns, getQuoteLineColumnDisplayValue } from '../utils/quoteLineColumns';
import { buildQuoteTotalDisplayRowsFromSummary, formatQuoteTotalRowValue } from '../utils/quoteTotals';
import RepairServiceReportModal from './RepairServiceReportModal';
import {
  CustomerFacingSlaReportView,
  normalizeCustomerFacingSlaReport
} from './CustomerFacingSlaReportView';
import { useAuth } from '../context/AuthContext';
import { useResponsiveTaskLayout } from '../hooks/useResponsiveTaskLayout';
import {
  getCurrentDeviceSlaAssignment,
  getDeviceSlaLabel,
  hasSavedDeviceSlaReport,
  resolveDeviceSlaEndDate
} from '../utils/deviceSla';
import {
  getDeviceWarrantyChipLabel,
  getDeviceWarrantyChipStyle,
  getDeviceWarrantyFirstMonthChipStyle,
  getDeviceWarrantyStatus,
  getDeviceWarrantySummaryLabel
} from '../utils/deviceWarranty';
import { mergeCaseTaskUpdate } from '../utils/caseTaskMerge';
import { AppNotificationPopups, type AppNotification, type AppNotificationAttachment } from './AppNotificationPopups';
import SupportTicketLauncher from './SupportTicketLauncher';
import MySupportTicketsPanel from './MySupportTicketsPanel';
import { setUserActivityPage } from '../services/userActivity';

interface DeviceInfo {
  id: string;
  modelNumber: string;
  serialNumber: string;
  assetNumber?: string | null;
  customerId?: string;
  customerEmail?: string;
  isCustom?: boolean;
}

interface DeviceModel {
  id: string;
  modelNumber: string;
  manufacturer: string;
  modelName?: string;
  description?: string | null;
  deviceType?: string | null;
}

interface DeviceHistoryMeta {
  serial: string;
  model?: string;
}

type PortalSurveyStatus = 'AVAILABLE' | 'COMPLETED';

interface PortalSurveyCaseContext {
  id: string;
  caseNumber?: string | null;
  caseCode?: string | null;
  status?: string | null;
  closedAt?: string | null;
  workTypeCode?: string | null;
  workTypeLabel?: string | null;
  device?: {
    id: string;
    modelNumber?: string | null;
    manufacturer?: string | null;
    serialNumber?: string | null;
    assetNumber?: string | null;
  } | null;
}

interface PortalSurveySummary {
  id: string;
  caseId: string;
  surveyName: string;
  status: PortalSurveyStatus;
  questionCount: number;
  releasedAt?: string | null;
  completedAt?: string | null;
  scoreEarned?: number | null;
  scorePossible?: number | null;
  technicianName?: string | null;
  case?: PortalSurveyCaseContext | null;
}

interface PortalSurveySection {
  questionKey: string;
  order: number;
  title: string;
  description?: string | null;
  scaleType: 'ZERO_TO_TEN' | 'ZERO_TO_HUNDRED' | 'STARS_5' | 'STARS_10' | string;
  minScore: number;
  maxScore: number;
  rawScore?: number | null;
}

interface PortalSurveyDetail extends PortalSurveySummary {
  commentsText: string;
  notesPrompt: string;
  sections: PortalSurveySection[];
}

type PortalSurveyScoreBand = 'red' | 'amber' | 'green' | 'darkGreen';

interface PortalSurveyScoreDisplay {
  earned: number;
  possible: number;
  percentage: number;
  band: PortalSurveyScoreBand;
  scoreLabel: string;
  percentageLabel: string;
}

type PortalOverviewQueueKey = 'surveys' | 'schedules' | 'quotes' | 'open';

interface PortalCaseScheduleOverview {
  task: CaseTask | null;
  plannedStartAt: Date | null;
  plannedEndAt: Date | null;
  requestedStartAt: Date | null;
  requestedEndAt: Date | null;
  hasCommittedSchedule: boolean;
  hasScheduleRequest: boolean;
  scheduleConfirmationStatus: string;
  isConfirmed: boolean;
  displayStartAt: Date | null;
  displayEndAt: Date | null;
  durationLabel: string;
  plannerStatusLabel: string;
}

interface DynamicQuestion {
  id: string;
  label: string;
}

type QuickAnswerValue = 'YES' | 'NO' | 'SKIP';

const BRAND_WORDMARK_SRC = `${process.env.PUBLIC_URL}/bonzaiq-logo..png`;
const BRAND_TEXT = '#123F43';
const BRAND_ACCENT = '#D99A33';
const BRAND_SURFACE = '#F2EEE7';
const BRAND_SURFACE_SOFT = '#FBF8F2';
const BRAND_BORDER = '#DDD4C8';
const BRAND_MUTED = '#64716F';
const BRAND_SIDEBAR_ACTIVE = 'rgba(242, 238, 231, 0.12)';
const PORTAL_CURRENCY_OPTIONS = ['USD', 'EUR', 'GBP', 'AUD', 'SGD', 'JPY'];
const PORTAL_UNIT_OPTIONS = [
  { value: 'METRIC', label: 'Metric (SI)' },
  { value: 'IMPERIAL', label: 'Imperial/US' }
];
const PORTAL_DATE_OPTIONS = ['YYYY-MM-DD', 'DD/MM/YYYY', 'MM/DD/YYYY'];
const DEFAULT_PORTAL_SETTINGS = Object.freeze({
  currency: 'USD',
  units: 'METRIC',
  dateFormat: 'YYYY-MM-DD'
});
const PORTAL_SURVEY_LIST_CACHE_MS = 30000;

function getPortalSurveyScoreDisplay(
  survey?: { scoreEarned?: number | null; scorePossible?: number | null } | null
): PortalSurveyScoreDisplay | null {
  const earned = Number(survey?.scoreEarned);
  const possible = Number(survey?.scorePossible);
  if (!Number.isFinite(earned) || !Number.isFinite(possible) || possible <= 0) return null;

  const percentage = Math.max(0, Math.min(100, Math.floor((earned / possible) * 100)));
  const band: PortalSurveyScoreBand =
    percentage <= 69 ? 'red' : percentage <= 84 ? 'amber' : percentage <= 94 ? 'green' : 'darkGreen';

  return {
    earned,
    possible,
    percentage,
    band,
    scoreLabel: `${earned}/${possible}`,
    percentageLabel: `${percentage}%`
  };
}

function getPortalSurveyPercentageBadgeStyle(display: PortalSurveyScoreDisplay): React.CSSProperties {
  switch (display.band) {
    case 'red':
      return { color: '#B91C1C', backgroundColor: '#FEF2F2', borderColor: '#FECACA' };
    case 'amber':
      return { color: '#92400E', backgroundColor: '#FEF3C7', borderColor: '#FCD34D' };
    case 'green':
      return { color: '#25624D', backgroundColor: '#E7F2EC', borderColor: '#BDD4C7' };
    case 'darkGreen':
    default:
      return { color: '#14532D', backgroundColor: '#DCFCE7', borderColor: '#86EFAC' };
  }
}

function padPortalDatePart(value: number): string {
  return String(value).padStart(2, '0');
}

function formatDateForPreference(date: Date, dateFormat: string): string {
  const year = date.getFullYear();
  const month = padPortalDatePart(date.getMonth() + 1);
  const day = padPortalDatePart(date.getDate());

  switch (String(dateFormat || '').toUpperCase()) {
    case 'DD/MM/YYYY':
      return `${day}/${month}/${year}`;
    case 'MM/DD/YYYY':
      return `${month}/${day}/${year}`;
    case 'YYYY-MM-DD':
    default:
      return `${year}-${month}-${day}`;
  }
}

function formatPortalDateValue(value: string | null | undefined, dateFormat: string, fallback = 'Unknown date'): string {
  if (!value) return fallback;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return fallback;
  return formatDateForPreference(date, dateFormat);
}

function formatPortalDateTimeValue(
  value: string | null | undefined,
  dateFormat: string,
  fallback = 'Unknown date'
): string {
  if (!value) return fallback;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return fallback;
  const dateLabel = formatDateForPreference(date, dateFormat);
  const timeLabel = date.toLocaleTimeString([], {
    hour: 'numeric',
    minute: '2-digit'
  });
  return `${dateLabel} ${timeLabel}`;
}

function formatPortalCurrencyValue(value: number | null | undefined, currency: string): string {
  const amount = Number(value);
  if (!Number.isFinite(amount)) return 'Not available';
  const normalizedCurrency = String(currency || DEFAULT_PORTAL_SETTINGS.currency).trim().toUpperCase() || DEFAULT_PORTAL_SETTINGS.currency;

  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: normalizedCurrency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  } catch {
    return `${normalizedCurrency} ${amount.toFixed(2)}`;
  }
}

function getPortalUnitLabel(value: string | null | undefined): string {
  const normalized = String(value || DEFAULT_PORTAL_SETTINGS.units).trim().toUpperCase();
  const matched = PORTAL_UNIT_OPTIONS.find(option => option.value === normalized);
  return matched?.label || normalized || DEFAULT_PORTAL_SETTINGS.units;
}

function normalizePortalProfileEmail(value: string | null | undefined): string {
  return String(value || '').trim().toLowerCase();
}

function getPortalCaseTaskAssigneeLabel(task?: CaseTask | null): string {
  if (!task) return '';
  const assignees = Array.isArray(task.assignees) ? task.assignees.filter(Boolean) : [];
  if (assignees.length > 0) {
    const labels = Array.from(
      new Set(
        assignees
          .map(entry => String(entry.fullName || entry.username || entry.id || '').trim())
          .filter(Boolean)
      )
    );
    if (labels.length > 0) return labels.join(', ');
  }
  return String(task.assignedUserName || '').trim();
}

function getPortalCaseTechnicianLabel(entry: Case): string {
  const taskCandidates = [entry.activeTask, entry.displayTask, ...(Array.isArray(entry.tasks) ? entry.tasks : [])];
  for (const task of taskCandidates) {
    const label = getPortalCaseTaskAssigneeLabel(task);
    if (label) return label;
  }

  const reportOwner = (Array.isArray(entry.serviceReports) ? entry.serviceReports : [])
    .map(report => String(report?.savedByName || '').trim())
    .find(Boolean);

  return reportOwner || 'Support team';
}

const DEFAULT_PORTAL_SCHEDULE_REQUEST_DURATION_MINUTES = 8 * 60;

function parsePortalScheduleDate(value?: string | null) {
  const parsed = value ? new Date(value) : null;
  return parsed && !Number.isNaN(parsed.getTime()) ? parsed : null;
}

function formatPortalScheduleDuration(startAt: Date | null, endAt: Date | null) {
  if (!startAt || !endAt || endAt.getTime() <= startAt.getTime()) return 'Not set';
  const totalMinutes = Math.round((endAt.getTime() - startAt.getTime()) / 60000);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  const parts = [];
  if (hours > 0) parts.push(`${hours}h`);
  if (minutes > 0) parts.push(`${minutes}m`);
  return parts.length ? parts.join(' ') : '0m';
}

function toPortalScheduleDateInputValue(value: Date | null) {
  return value
    ? `${value.getFullYear()}-${`${value.getMonth() + 1}`.padStart(2, '0')}-${`${value.getDate()}`.padStart(2, '0')}`
    : '';
}

function toPortalScheduleTimeInputValue(value: Date | null) {
  return value ? `${`${value.getHours()}`.padStart(2, '0')}:${`${value.getMinutes()}`.padStart(2, '0')}` : '';
}

function combinePortalScheduleDateTimeInput(dateValue: string, timeValue: string) {
  if (!dateValue || !timeValue) return null;
  const combined = new Date(`${dateValue}T${timeValue}`);
  return Number.isNaN(combined.getTime()) ? null : combined;
}

function addPortalScheduleMinutes(value: Date, minutes: number) {
  return new Date(value.getTime() + minutes * 60000);
}

type PortalScheduleRequestDraft = {
  startDate: string;
  startTime: string;
  endDate: string;
  endTime: string;
  validationMessage: string | null;
};

function buildPortalScheduleRequestDraft(
  primaryWindowStart: Date | null,
  primaryWindowEnd: Date | null
): PortalScheduleRequestDraft {
  const fallbackStart = primaryWindowStart || new Date();
  const normalizedStart = new Date(fallbackStart);
  normalizedStart.setSeconds(0, 0);
  if (!primaryWindowStart) {
    normalizedStart.setHours(8, 0, 0, 0);
  }
  const primaryEnd =
    primaryWindowEnd && primaryWindowEnd.getTime() > normalizedStart.getTime()
      ? new Date(primaryWindowEnd)
      : addPortalScheduleMinutes(normalizedStart, DEFAULT_PORTAL_SCHEDULE_REQUEST_DURATION_MINUTES);
  const normalizedEnd = new Date(primaryEnd);
  normalizedEnd.setSeconds(0, 0);
  return {
    startDate: toPortalScheduleDateInputValue(normalizedStart),
    startTime: toPortalScheduleTimeInputValue(normalizedStart),
    endDate: toPortalScheduleDateInputValue(normalizedEnd),
    endTime: toPortalScheduleTimeInputValue(normalizedEnd),
    validationMessage: null
  };
}

function buildPortalScheduleRequestDraftRange(draft: PortalScheduleRequestDraft | null) {
  const startAt = combinePortalScheduleDateTimeInput(draft?.startDate || '', draft?.startTime || '');
  const endAt = combinePortalScheduleDateTimeInput(draft?.endDate || '', draft?.endTime || '');
  return { startAt, endAt };
}

function getPortalScheduleRequestDurationMinutes(draft: PortalScheduleRequestDraft | null) {
  const { startAt, endAt } = buildPortalScheduleRequestDraftRange(draft);
  if (!startAt || !endAt) return DEFAULT_PORTAL_SCHEDULE_REQUEST_DURATION_MINUTES;
  const durationMinutes = Math.round((endAt.getTime() - startAt.getTime()) / 60000);
  return durationMinutes > 0 ? durationMinutes : DEFAULT_PORTAL_SCHEDULE_REQUEST_DURATION_MINUTES;
}

function alignPortalScheduleRequestEndToStart(
  nextDraft: PortalScheduleRequestDraft,
  durationMinutes = DEFAULT_PORTAL_SCHEDULE_REQUEST_DURATION_MINUTES
): PortalScheduleRequestDraft {
  const { startAt } = buildPortalScheduleRequestDraftRange(nextDraft);
  if (!startAt) return nextDraft;
  const safeDurationMinutes = durationMinutes > 0 ? durationMinutes : DEFAULT_PORTAL_SCHEDULE_REQUEST_DURATION_MINUTES;
  const nextEnd = addPortalScheduleMinutes(startAt, safeDurationMinutes);
  return {
    ...nextDraft,
    endDate: toPortalScheduleDateInputValue(nextEnd),
    endTime: toPortalScheduleTimeInputValue(nextEnd)
  };
}

function getPortalCaseScheduleTask(entry?: Case | null): CaseTask | null {
  const tasks = entry && Array.isArray(entry.tasks) ? entry.tasks : [];
  const candidates = [entry?.activeTask, entry?.displayTask, ...tasks];
  const seen = new Set<string>();
  const uniqueCandidates = candidates.filter(task => {
    const taskId = String(task?.id || '').trim();
    if (!taskId || seen.has(taskId)) return false;
    seen.add(taskId);
    return true;
  });

  const requestedTask = uniqueCandidates.find(task =>
    Boolean(task?.scheduleRequestedStart || task?.scheduleRequestedDue)
  );
  if (requestedTask) return requestedTask;

  const scheduledTask = uniqueCandidates.find(task =>
    Boolean(task?.plannedStart || task?.plannedDue || task?.scheduleRequestedStart || task?.scheduleRequestedDue)
  );
  return scheduledTask || uniqueCandidates[0] || null;
}

function getPortalCaseScheduleOverview(entry?: Case | null): PortalCaseScheduleOverview {
  const task = getPortalCaseScheduleTask(entry);
  const plannedStartAt = parsePortalScheduleDate(task?.plannedStart);
  const plannedEndAt = parsePortalScheduleDate(task?.plannedDue);
  const requestedStartAt = parsePortalScheduleDate(task?.scheduleRequestedStart);
  const requestedEndAt = parsePortalScheduleDate(task?.scheduleRequestedDue);
  const hasCommittedSchedule = Boolean(plannedStartAt || plannedEndAt);
  const hasScheduleRequest = Boolean(requestedStartAt || requestedEndAt);
  const scheduleConfirmationStatus = String(task?.scheduleConfirmationStatus || '')
    .trim()
    .toUpperCase();
  const isConfirmed =
    hasCommittedSchedule && !hasScheduleRequest && scheduleConfirmationStatus === 'CONFIRMED';
  const displayStartAt = hasScheduleRequest ? requestedStartAt : plannedStartAt;
  const displayEndAt = hasScheduleRequest ? requestedEndAt : plannedEndAt;
  const durationLabel = hasScheduleRequest
    ? formatPortalScheduleDuration(requestedStartAt, requestedEndAt)
    : formatPortalScheduleDuration(plannedStartAt, plannedEndAt);
  const plannerStatusLabel =
    hasCommittedSchedule && hasScheduleRequest
      ? 'Change requested'
      : hasCommittedSchedule
      ? isConfirmed
        ? 'Confirmed'
        : 'Planned'
      : hasScheduleRequest
      ? 'Suggested schedule'
      : 'Not scheduled';

  return {
    task,
    plannedStartAt,
    plannedEndAt,
    requestedStartAt,
    requestedEndAt,
    hasCommittedSchedule,
    hasScheduleRequest,
    scheduleConfirmationStatus,
    isConfirmed,
    displayStartAt,
    displayEndAt,
    durationLabel,
    plannerStatusLabel
  };
}

function getPortalCaseIssuePreview(value?: string | null): string {
  const trimmed = String(value || '').replace(/\s+/g, ' ').trim();
  if (!trimmed) return 'No issue details recorded.';
  return trimmed.length > 96 ? `${trimmed.slice(0, 96).trim()}...` : trimmed;
}

function getPortalSurveyCaseLabel(value?: PortalSurveyCaseContext | null): string {
  if (!value) return 'Unknown case';
  return value.caseNumber || value.caseCode || value.id || 'Unknown case';
}

function getPortalSurveyDeviceLabel(value?: PortalSurveyCaseContext | null): string {
  const device = value?.device;
  if (!device) return 'Unknown device';
  const model = String(device.modelNumber || '').trim();
  const manufacturer = String(device.manufacturer || '').trim();
  if (model && manufacturer) return `${model} - ${manufacturer}`;
  return model || manufacturer || device.serialNumber || 'Unknown device';
}

function getPortalSurveyWorkTypeLabel(value?: PortalSurveyCaseContext | null): string {
  const label = String(value?.workTypeLabel || '').trim();
  if (label) return label;
  const code = String(value?.workTypeCode || '').trim();
  if (!code) return 'Unknown work type';
  return code
    .toLowerCase()
    .split('_')
    .map(part => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

function getPortalSurveyScaleLabel(section?: PortalSurveySection | null): string {
  const scaleType = String(section?.scaleType || '').trim().toUpperCase();
  if (scaleType === 'ZERO_TO_HUNDRED') return '0-100';
  if (scaleType === 'STARS_5') return '1-5 stars';
  if (scaleType === 'STARS_10') return '1-10 stars';
  return '0-10';
}

function getPortalSurveyStarValues(section?: PortalSurveySection | null): number[] {
  const scaleType = String(section?.scaleType || '').trim().toUpperCase();
  if (scaleType === 'STARS_5') return [1, 2, 3, 4, 5];
  if (scaleType === 'STARS_10') return [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];
  return [];
}

export default function CustomerPortal() {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const messageAttachmentInputRef = useRef<HTMLInputElement>(null);
  const { user, logout, customerPortalAccess } = useAuth();
  const isLitePortal =
    customerPortalAccess?.isLite === true || String(customerPortalAccess?.mode || '').toUpperCase() === 'LITE';
  const { isCompact, isTablet, isNarrowDesktop, pagePadding } = useResponsiveTaskLayout();
  
  // State management
  const [currentStep, setCurrentStep] = useState(1);
  const [deviceModels, setDeviceModels] = useState<DeviceModel[]>([]);
  const [selectedModel, setSelectedModel] = useState('');
  const [serialNumber, setSerialNumber] = useState('');
  const [assetNumber, setAssetNumber] = useState('');
  const [deviceInfo, setDeviceInfo] = useState<DeviceInfo | null>(null);
  const [problemDescription, setProblemDescription] = useState('');
  const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);
  const [quickAnswerMap, setQuickAnswerMap] = useState<Record<string, QuickAnswerValue>>({});
  const [additionalNotes, setAdditionalNotes] = useState('');
  type DynamicQuestionSource = 'llm' | 'fallback';
  type DynamicQuestionSet = {
    quickButtons: DynamicQuestion[];
    textQuestions: DynamicQuestion[];
    source: DynamicQuestionSource;
  };

  const [dynamicQuestions, setDynamicQuestions] = useState<DynamicQuestionSet>({
    quickButtons: [],
    textQuestions: [],
    source: 'fallback'
  });
  const [dynamicQuestionsLoading, setDynamicQuestionsLoading] = useState(false);
  const [dynamicQuestionsError, setDynamicQuestionsError] = useState('');
  const [dynamicQuestionsLoaded, setDynamicQuestionsLoaded] = useState(false);
  const [contactEmail, setContactEmail] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  const [supportRequestNotice, setSupportRequestNotice] = useState<{ type: 'error'; message: string } | null>(null);
  const [caseLabel, setCaseLabel] = useState('Case');
  const [caseNumber, setCaseNumber] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isCustomModel, setIsCustomModel] = useState(false);
  const [customModelNumber, setCustomModelNumber] = useState('');
  const [customManufacturer, setCustomManufacturer] = useState('');
  const [customerDevices, setCustomerDevices] = useState<Device[]>([]);
  const [portalCustomer, setPortalCustomer] = useState<Customer | null>(null);
  const [selectedAssignedModelKey, setSelectedAssignedModelKey] = useState('');
  const [selectedAssignedDeviceId, setSelectedAssignedDeviceId] = useState('');
  const [usingAssignedDevice, setUsingAssignedDevice] = useState(false);
  const [manualIdentifierEntry, setManualIdentifierEntry] = useState(false);
  const [isHistoryOpen, setIsHistoryOpen] = useState(false);
  const [isHistoryLoading] = useState(false);
  const [historyError] = useState('');
  const [deviceHistory] = useState<Case[]>([]);
  const [historyDeviceMeta] = useState<DeviceHistoryMeta>({ serial: '', model: '' });
  const [expandedHistoryCases, setExpandedHistoryCases] = useState<Record<string, boolean>>({});
  const [activePortalTab, setActivePortalTab] = useState<
    'overview' | 'messages' | 'support' | 'devices' | 'history' | 'surveys' | 'account'
  >('support');
  const [activeOverviewQueue, setActiveOverviewQueue] = useState<PortalOverviewQueueKey | null>(null);
  const [portalCases, setPortalCases] = useState<Case[]>([]);
  const [portalCasesLoading, setPortalCasesLoading] = useState(false);
  const [portalCasesError, setPortalCasesError] = useState('');
  const [portalSurveys, setPortalSurveys] = useState<PortalSurveySummary[]>([]);
  const [portalSurveysLoading, setPortalSurveysLoading] = useState(false);
  const [portalSurveysError, setPortalSurveysError] = useState('');
  const [selectedPortalSurveyId, setSelectedPortalSurveyId] = useState<string | null>(null);
  const [selectedPortalSurvey, setSelectedPortalSurvey] = useState<PortalSurveyDetail | null>(null);
  const [portalSurveyDetailLoading, setPortalSurveyDetailLoading] = useState(false);
  const [portalSurveyDetailError, setPortalSurveyDetailError] = useState('');
  const [portalSurveySubmitNotice, setPortalSurveySubmitNotice] = useState<
    { type: 'error' | 'success'; message: string } | null
  >(null);
  const [portalSurveySubmitting, setPortalSurveySubmitting] = useState(false);
  const [portalSurveyAnswers, setPortalSurveyAnswers] = useState<Record<string, string>>({});
  const [portalSurveyComments, setPortalSurveyComments] = useState('');
  const [surveySearchQuery, setSurveySearchQuery] = useState('');
  const [surveyStatusFilter, setSurveyStatusFilter] = useState<'all' | 'available' | 'completed'>('all');
  const [surveyCaseFilter, setSurveyCaseFilter] = useState('');
  const [surveyDeviceFilter, setSurveyDeviceFilter] = useState('');
  const [surveyWorkTypeFilter, setSurveyWorkTypeFilter] = useState('');
  const [surveySortOrder, setSurveySortOrder] = useState<'newest' | 'oldest'>('newest');
  const [historySearchQuery, setHistorySearchQuery] = useState('');
  const [historyStatusFilter, setHistoryStatusFilter] = useState<'all' | 'open' | 'closed'>('all');
  const [historyDeviceFilter, setHistoryDeviceFilter] = useState('all');
  const [selectedPortalDeviceId, setSelectedPortalDeviceId] = useState<string | null>(null);
  const [portalDeviceModalSection, setPortalDeviceModalSection] = useState<'details' | 'history' | 'qr'>('details');
  const [portalDeviceSlaReport, setPortalDeviceSlaReport] = useState<any | null>(null);
  const [portalDeviceSlaReportLoading, setPortalDeviceSlaReportLoading] = useState(false);
  const [portalDeviceSlaReportError, setPortalDeviceSlaReportError] = useState('');
  const [pendingDeviceModalId, setPendingDeviceModalId] = useState<string | null>(null);
  const [pendingDeviceQrIntent, setPendingDeviceQrIntent] = useState<{
    token: string;
    intent: 'support' | 'devices';
  } | null>(null);
  const [deviceQrTokens, setDeviceQrTokens] = useState<Record<string, string>>({});
  const [deviceQrLoadingId, setDeviceQrLoadingId] = useState<string | null>(null);
  const [deviceQrError, setDeviceQrError] = useState('');
  const handledDeviceQrIntentRef = useRef('');
  const [selectedPortalHistoryCaseId, setSelectedPortalHistoryCaseId] = useState<string | null>(null);
  const [pendingHistoryCaseId, setPendingHistoryCaseId] = useState<string | null>(null);
  const [portalScheduleActionBusy, setPortalScheduleActionBusy] = useState<null | 'confirm' | 'request'>(null);
  const [portalScheduleNotice, setPortalScheduleNotice] = useState<
    { type: 'error' | 'success'; message: string } | null
  >(null);
  const [portalScheduleRequestModalOpen, setPortalScheduleRequestModalOpen] = useState(false);
  const [portalScheduleRequestDraft, setPortalScheduleRequestDraft] = useState<PortalScheduleRequestDraft | null>(null);
  const [selectedPortalQuote, setSelectedPortalQuote] = useState<any | null>(null);
  const [selectedPortalQuoteCase, setSelectedPortalQuoteCase] = useState<Case | null>(null);
  const [selectedPortalServiceReport, setSelectedPortalServiceReport] = useState<CaseServiceReport | null>(null);
  const [selectedPortalServiceReportCase, setSelectedPortalServiceReportCase] = useState<Case | null>(null);
  const [portalQuoteLoading, setPortalQuoteLoading] = useState(false);
  const [portalQuoteResponding, setPortalQuoteResponding] = useState(false);
  const [portalQuoteError, setPortalQuoteError] = useState('');
  const [portalQuoteNotice, setPortalQuoteNotice] = useState('');
  const [portalQuoteAdjustOpen, setPortalQuoteAdjustOpen] = useState(false);
  const [portalQuoteAdjustNote, setPortalQuoteAdjustNote] = useState('');
  const [portalQuoteAdjustError, setPortalQuoteAdjustError] = useState('');
  const [portalApiBase, setPortalApiBase] = useState<string>('');
  const [portalMessages, setPortalMessages] = useState<PortalMessage[]>([]);
  const [messageFocusType, setMessageFocusType] = useState<'general' | 'device' | 'case'>('general');
  const [messageDeviceId, setMessageDeviceId] = useState('');
  const [messageCaseId, setMessageCaseId] = useState('');
  const [messageVisibleCount, setMessageVisibleCount] = useState(10);
  const [messageBody, setMessageBody] = useState('');
  const [messageAttachments, setMessageAttachments] = useState<PortalMessageAttachment[]>([]);
  const [messageSending, setMessageSending] = useState(false);
  const [selectedPortalMessageId, setSelectedPortalMessageId] = useState<string | null>(null);
  const [messageReplyBody, setMessageReplyBody] = useState('');
  const [messageReplyAttachments, setMessageReplyAttachments] = useState<PortalMessageAttachment[]>([]);
  const [showComposePanel, setShowComposePanel] = useState(false);
  const [messageFocusFilter, setMessageFocusFilter] = useState<'all' | 'general' | 'device' | 'case'>('all');
  const [messageCaseFilter, setMessageCaseFilter] = useState('');
  const [messageDeviceFilter, setMessageDeviceFilter] = useState('');
  const [messageSortOrder, setMessageSortOrder] = useState<'newest' | 'oldest'>('newest');
  const [messageView, setMessageView] = useState<'active' | 'archived' | 'all'>('active');
  const [readMessageIds, setReadMessageIds] = useState<string[]>([]);
  const [showUnreadOnly, setShowUnreadOnly] = useState(false);
  const [messageShowSent, setMessageShowSent] = useState(false);
  const [messageWorkspaceMode, setMessageWorkspaceMode] = useState<'messages' | 'notifications'>('messages');
  const [portalNotifications, setPortalNotifications] = useState<AppNotification[]>([]);
  const [portalNotificationPopups, setPortalNotificationPopups] = useState<AppNotification[]>([]);
  const [portalNotificationsLoading, setPortalNotificationsLoading] = useState(false);
  const [portalNotificationView, setPortalNotificationView] = useState<'active' | 'all' | 'dismissed'>('active');
  const [selectedPortalNotificationId, setSelectedPortalNotificationId] = useState<string | null>(null);
  const [portalSettings, setPortalSettings] = useState<SettingsPayload | null>(null);
  const [portalSettingsLoading, setPortalSettingsLoading] = useState(false);
  const [portalSettingsError, setPortalSettingsError] = useState('');
  const [accountProfile, setAccountProfile] = useState<AccountProfilePayload | null>(null);
  const [accountProfileLoading, setAccountProfileLoading] = useState(false);
  const [accountProfileError, setAccountProfileError] = useState('');
  const [accountProfileSaving, setAccountProfileSaving] = useState(false);
  const [accountProfileNotice, setAccountProfileNotice] = useState<
    { type: 'error' | 'success'; message: string } | null
  >(null);
  const [accountProfileForm, setAccountProfileForm] = useState({
    fullName: '',
    email: '',
    confirmEmail: '',
    currentPassword: '',
    phone: '',
    role: ''
  });
  const [accountPreferencesSaving, setAccountPreferencesSaving] = useState(false);
  const [accountPreferencesNotice, setAccountPreferencesNotice] = useState<
    { type: 'error' | 'success'; message: string } | null
  >(null);
  const [accountPreferences, setAccountPreferences] = useState({
    currency: '',
    units: '',
    dateFormat: ''
  });
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [passwordSaving, setPasswordSaving] = useState(false);
  const [passwordNotice, setPasswordNotice] = useState<
    { type: 'error' | 'success'; message: string } | null
  >(null);
  const [deactivateConfirmText, setDeactivateConfirmText] = useState('');
  const [deactivateSubmitting, setDeactivateSubmitting] = useState(false);
  const [deactivateNotice, setDeactivateNotice] = useState<
    { type: 'error' | 'success'; message: string } | null
  >(null);
  const [accountWorkspaceTab, setAccountWorkspaceTab] = useState<'profile' | 'preferences' | 'support' | 'security' | 'access'>(
    'profile'
  );
  const portalCasesLoadLevelRef = useRef<'none' | 'summary' | 'full'>('none');
  const portalSurveysLoadedRef = useRef(false);
  const portalSurveysLastLoadedAtRef = useRef(0);
  const historyCaseRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const supportRequestFingerprintRef = useRef('');
  const effectivePortalSettings = portalSettings?.effective || DEFAULT_PORTAL_SETTINGS;
  const globalPortalSettings = portalSettings?.global || DEFAULT_PORTAL_SETTINGS;

  // Load device models on component mount
  useEffect(() => {
    loadDeviceModels();
  }, []);

  useEffect(() => {
    if (customerDevices.length === 0) {
      setUsingAssignedDevice(false);
      setSelectedAssignedModelKey('');
      setSelectedAssignedDeviceId('');
      setManualIdentifierEntry(false);
    }
  }, [customerDevices]);

  useEffect(() => {
    if (portalCustomer) {
      if (portalCustomer.email) setContactEmail(prev => prev || portalCustomer.email || '');
      if (portalCustomer.phone) setContactPhone(prev => prev || portalCustomer.phone || '');
    }
  }, [portalCustomer]);

  const supportRequestFingerprint = [
    currentStep,
    serialNumber,
    assetNumber,
    manualIdentifierEntry,
    selectedAssignedModelKey,
    selectedAssignedDeviceId,
    selectedModel,
    customModelNumber,
    customManufacturer,
    problemDescription,
    contactEmail,
    contactPhone
  ].join('|');

  useEffect(() => {
    if (!supportRequestNotice) {
      supportRequestFingerprintRef.current = supportRequestFingerprint;
      return;
    }
    if (supportRequestFingerprintRef.current !== supportRequestFingerprint) {
      setSupportRequestNotice(null);
    }
    supportRequestFingerprintRef.current = supportRequestFingerprint;
  }, [supportRequestNotice, supportRequestFingerprint]);

  const applyPortalSettings = useCallback((nextSettings: SettingsPayload) => {
    setPortalSettings(nextSettings);
    setAccountPreferences({
      currency: nextSettings.user?.currency || '',
      units: nextSettings.user?.units || '',
      dateFormat: nextSettings.user?.dateFormat || ''
    });
  }, []);

  const applyAccountProfile = useCallback((profile: AccountProfilePayload) => {
    setAccountProfile(profile);
    setAccountProfileForm({
      fullName: profile.fullName || '',
      email: profile.email || '',
      confirmEmail: '',
      currentPassword: '',
      phone: profile.phone || '',
      role: profile.role || ''
    });
  }, []);

  const loadPortalSettings = useCallback(async () => {
    try {
      setPortalSettingsLoading(true);
      setPortalSettingsError('');
      const base = await getBaseUrl();
      const settings = await fetchSettings(base);
      applyPortalSettings(settings);
    } catch (error) {
      console.error('Failed to load portal settings:', error);
      setPortalSettingsError('Unable to load account preferences right now.');
    } finally {
      setPortalSettingsLoading(false);
    }
  }, [applyPortalSettings]);

  useEffect(() => {
    loadPortalSettings();
  }, [loadPortalSettings]);

  const loadAccountProfile = useCallback(async () => {
    try {
      setAccountProfileLoading(true);
      setAccountProfileError('');
      const base = await getBaseUrl();
      const profile = await fetchOwnAccountProfile(base);
      applyAccountProfile(profile);
    } catch (error) {
      console.error('Failed to load account profile:', error);
      setAccountProfileError('Unable to load your contact details right now.');
    } finally {
      setAccountProfileLoading(false);
    }
  }, [applyAccountProfile]);

  useEffect(() => {
    loadAccountProfile();
  }, [loadAccountProfile]);

  const loadPortalMessages = useCallback(async () => {
    if (isLitePortal) {
      setPortalMessages([]);
      setReadMessageIds([]);
      return;
    }
    try {
      const rows = (await messageApi.getAll({
        channelType: 'external',
        view: messageView
      })) as PortalMessage[];
      const next = Array.isArray(rows) ? rows : [];
      setPortalMessages(next);
      setReadMessageIds(next.filter(message => message.readByCurrentUser).map(message => message.id));
    } catch (error) {
      console.error('Failed to load portal messages:', error);
      setPortalMessages([]);
      setReadMessageIds([]);
    }
  }, [isLitePortal, messageView]);

  const loadPortalNotifications = useCallback(async () => {
    setPortalNotificationsLoading(true);
    try {
      const rows = await notificationApi.getMine({ view: portalNotificationView, take: 100 });
      setPortalNotifications(Array.isArray(rows) ? rows : []);
    } catch (error) {
      console.warn('Failed to load portal notifications:', error);
      setPortalNotifications([]);
    } finally {
      setPortalNotificationsLoading(false);
    }
  }, [portalNotificationView]);

  const loadPortalNotificationPopups = useCallback(async () => {
    if (!user?.id) return;
    try {
      const rows = await notificationApi.getUnread({ take: 10 });
      const nextRows = Array.isArray(rows) ? rows : [];
      setPortalNotificationPopups(nextRows);
      if (portalNotificationView === 'active') {
        setPortalNotifications(nextRows);
      }
    } catch (error) {
      console.warn('Failed to load portal notification popups:', error);
      setPortalNotificationPopups([]);
    }
  }, [portalNotificationView, user?.id]);

  const handleDismissPortalNotification = useCallback(
    async (notification: AppNotification) => {
      try {
        const updated = await notificationApi.dismiss(notification.id);
        setPortalNotificationPopups(prev => prev.filter(item => item.id !== notification.id));
        setPortalNotifications(prev => {
          if (portalNotificationView === 'active') {
            return prev.filter(item => item.id !== notification.id);
          }
          return prev.map(item => (item.id === notification.id ? { ...item, ...updated } : item));
        });
        if (selectedPortalNotificationId === notification.id && portalNotificationView === 'active') {
          setSelectedPortalNotificationId(null);
        }
      } catch (error) {
        console.warn('Failed to dismiss portal notification:', error);
      }
    },
    [portalNotificationView, selectedPortalNotificationId]
  );

  const handleMarkPortalNotificationRead = useCallback(async (notificationId: string) => {
    try {
      const updated = await notificationApi.markRead(notificationId);
      setPortalNotifications(prev => prev.map(item => (item.id === notificationId ? { ...item, ...updated } : item)));
      setPortalNotificationPopups(prev =>
        prev.map(item => (item.id === notificationId ? { ...item, readAt: updated?.readAt || new Date().toISOString() } : item))
      );
    } catch (error) {
      console.warn('Failed to mark portal notification read:', error);
    }
  }, []);

  useEffect(() => {
    const applyDeviceLink = () => {
      const hash = window.location.hash || '';
      const queryIndex = hash.indexOf('?');
      if (queryIndex === -1) return;
      const params = new URLSearchParams(hash.slice(queryIndex + 1));
      const deviceId = params.get('deviceId');
      const deviceToken = params.get('deviceToken') || params.get('qrToken');
      const intentParam = params.get('intent');
      const tab = params.get('tab');
      if (deviceToken) {
        setPendingDeviceQrIntent({
          token: deviceToken,
          intent: intentParam === 'devices' ? 'devices' : 'support'
        });
        setActivePortalTab(intentParam === 'devices' ? 'devices' : 'support');
        return;
      }
      if (tab === 'devices') {
        setActivePortalTab('devices');
      }
      if (deviceId) {
        setPortalDeviceModalSection('details');
        setPendingDeviceModalId(deviceId);
      }
    };

    applyDeviceLink();
    window.addEventListener('hashchange', applyDeviceLink);
    return () => window.removeEventListener('hashchange', applyDeviceLink);
  }, []);

  useEffect(() => {
    if (activePortalTab !== 'devices' || !pendingDeviceModalId) return;
    const deviceExists = customerDevices.some(device => device.id === pendingDeviceModalId);
    if (!deviceExists) return;
    setSelectedPortalDeviceId(pendingDeviceModalId);
    setPendingDeviceModalId(null);
  }, [activePortalTab, pendingDeviceModalId, customerDevices]);


  const loadDeviceModels = async () => {
    try {
      const base = await getBaseUrl();
      const response = await fetch(`${base}/api/device-models`);
      if (response.ok) {
        const models = await response.json();
        setDeviceModels(models);
      }
    } catch (error) {
      console.error('Failed to load device models:', error);
    }
  };

  const loadPortalCustomer = useCallback(async () => {
    try {
      const rows = (await deviceApi.getAll({ includeInactive: true, full: true })) as Device[];
      const devices = Array.isArray(rows) ? rows : [];
      setCustomerDevices(devices);

      const firstCustomerDevice = devices.find(device => device.customerId && device.customer);
      if (firstCustomerDevice?.customer) {
        const customer = firstCustomerDevice.customer as Customer;
        setPortalCustomer(customer);
      } else {
        const linkedCustomers = customerPortalAccess?.customers;
        const linkedCustomer = Array.isArray(linkedCustomers)
          ? linkedCustomers.find(customer => customer?.id)
          : null;
        setPortalCustomer(linkedCustomer ? (linkedCustomer as Customer) : null);
      }
    } catch (error) {
      console.error('Failed to load portal customer devices:', error);
      setPortalCustomer(null);
      setCustomerDevices([]);
    }
  }, []);

  useEffect(() => {
    loadPortalCustomer();
  }, [loadPortalCustomer]);

  useEffect(() => {
    if (activePortalTab !== 'devices' && activePortalTab !== 'overview') return;
    loadPortalCustomer();
  }, [activePortalTab, loadPortalCustomer]);

  useEffect(() => {
    const loadBase = async () => {
      try {
        const base = await getBaseUrl();
        setPortalApiBase(base);
      } catch (error) {
        console.error('Failed to resolve portal base URL:', error);
      }
    };
    loadBase();
  }, []);

  const loadPortalCases = useCallback(async (options: { full?: boolean; force?: boolean } = {}) => {
    const targetLevel: 'summary' | 'full' = options.full && !isLitePortal ? 'full' : 'summary';
    const currentLevel = portalCasesLoadLevelRef.current;
    if (!options.force) {
      if (currentLevel === 'full') return;
      if (currentLevel === 'summary' && targetLevel === 'summary') return;
    }
    try {
      setPortalCasesLoading(true);
      setPortalCasesError('');
      const raw = await caseApi.getAll(targetLevel === 'summary' ? { summary: true } : {});
      const cases = Array.isArray(raw) ? raw : Array.isArray((raw as any)?.data) ? (raw as any).data : [];
      // Backend already enforces customer-user scope via customer access mappings.
      // Keep portal rendering aligned with that scope and avoid brittle client-side filtering.
      setPortalCases(cases);
      portalCasesLoadLevelRef.current = targetLevel;
    } catch (error) {
      console.error('Failed to load portal cases:', error);
      setPortalCasesError('Unable to load service history right now.');
      setPortalCases([]);
      portalCasesLoadLevelRef.current = 'none';
    } finally {
      setPortalCasesLoading(false);
    }
  }, [isLitePortal]);

  const applyPortalCaseUpdate = useCallback((nextCase: Case | null) => {
    if (!nextCase?.id) return;
    setPortalCases(prev => {
      const index = prev.findIndex(entry => entry.id === nextCase.id);
      if (index === -1) return [nextCase, ...prev];
      const next = [...prev];
      next[index] = nextCase;
      return next;
    });
    portalCasesLoadLevelRef.current = 'full';
  }, []);

  const refreshPortalCaseRecord = useCallback(
    async (caseId: string) => {
      const normalizedCaseId = String(caseId || '').trim();
      if (!normalizedCaseId) return null;
      const refreshedCase = (await caseApi.getById(normalizedCaseId)) as Case;
      applyPortalCaseUpdate(refreshedCase);
      return refreshedCase;
    },
    [applyPortalCaseUpdate]
  );

  const applyPortalSurveyCompletionToCases = useCallback((survey: PortalSurveySummary) => {
    if (!survey?.id || !survey.caseId) return;

    const completedSurvey: CaseSurveySummary = {
      id: survey.id,
      caseId: survey.caseId,
      surveyName: survey.surveyName,
      status: 'COMPLETED',
      questionCount: survey.questionCount,
      releasedAt: survey.releasedAt,
      completedAt: survey.completedAt,
      scoreEarned: survey.scoreEarned,
      scorePossible: survey.scorePossible,
      technicianName: survey.technicianName
    };

    setPortalCases(prev =>
      prev.map(entry => {
        if (entry.id !== survey.caseId) return entry;
        const existingSurveys = Array.isArray(entry.caseSurveys) ? entry.caseSurveys : [];
        let foundSurvey = false;
        const nextSurveys = existingSurveys.map(existingSurvey => {
          if (existingSurvey.id !== survey.id) return existingSurvey;
          foundSurvey = true;
          return {
            ...existingSurvey,
            ...completedSurvey
          };
        });

        return {
          ...entry,
          caseSurveys: foundSurvey ? nextSurveys : [completedSurvey, ...existingSurveys]
        };
      })
    );
  }, []);

  useEffect(() => {
    if (!['overview', 'history', 'devices'].includes(activePortalTab)) return;
    loadPortalCases({ full: activePortalTab === 'history' || activePortalTab === 'overview' });
  }, [activePortalTab, loadPortalCases]);

  const loadPortalSurveys = useCallback(async (options: { force?: boolean } = {}) => {
    if (isLitePortal) {
      setPortalSurveys([]);
      setPortalSurveysError('');
      return;
    }
    if (
      !options.force &&
      portalSurveysLoadedRef.current &&
      Date.now() - portalSurveysLastLoadedAtRef.current < PORTAL_SURVEY_LIST_CACHE_MS
    ) {
      return;
    }
    try {
      setPortalSurveysLoading(true);
      setPortalSurveysError('');
      const rows = (await caseApi.getSurveys()) as PortalSurveySummary[];
      setPortalSurveys(Array.isArray(rows) ? rows : []);
      portalSurveysLoadedRef.current = true;
      portalSurveysLastLoadedAtRef.current = Date.now();
    } catch (error) {
      console.error('Failed to load portal surveys:', error);
      setPortalSurveys([]);
      setPortalSurveysError('Unable to load surveys right now.');
      portalSurveysLoadedRef.current = false;
      portalSurveysLastLoadedAtRef.current = 0;
    } finally {
      setPortalSurveysLoading(false);
    }
  }, []);

  const loadPortalSurveyDetail = useCallback(
    async (surveyId: string, options: { preserveNotice?: boolean } = {}) => {
      const targetId = String(surveyId || '').trim();
      if (!targetId) return;

      try {
        setSelectedPortalSurveyId(targetId);
        setPortalSurveyDetailLoading(true);
        setPortalSurveyDetailError('');
        if (!options.preserveNotice) {
          setPortalSurveySubmitNotice(null);
        }

        const survey = (await caseApi.getSurveyById(targetId)) as PortalSurveyDetail;
        const safeSurvey = survey && typeof survey === 'object' ? survey : null;
        setSelectedPortalSurvey(safeSurvey);

        const nextAnswers = (safeSurvey?.sections || []).reduce<Record<string, string>>((acc, section) => {
          acc[section.questionKey] =
            section.rawScore === null || section.rawScore === undefined ? '' : String(section.rawScore);
          return acc;
        }, {});
        setPortalSurveyAnswers(nextAnswers);
        setPortalSurveyComments(safeSurvey?.commentsText || '');
      } catch (error) {
        console.error('Failed to load portal survey detail:', error);
        setSelectedPortalSurvey(null);
        setPortalSurveyAnswers({});
        setPortalSurveyComments('');
        setPortalSurveyDetailError('Unable to load this survey right now.');
      } finally {
        setPortalSurveyDetailLoading(false);
      }
    },
    []
  );

  const closePortalSurveyModal = useCallback(() => {
    const shouldRefreshCase =
      selectedPortalSurvey?.caseId && String(selectedPortalSurvey.status || '').toUpperCase() === 'COMPLETED';
    const caseIdToRefresh = shouldRefreshCase ? selectedPortalSurvey.caseId : '';

    setSelectedPortalSurveyId(null);
    setSelectedPortalSurvey(null);
    setPortalSurveyDetailLoading(false);
    setPortalSurveyDetailError('');
    setPortalSurveySubmitNotice(null);
    setPortalSurveySubmitting(false);
    setPortalSurveyAnswers({});
    setPortalSurveyComments('');

    if (caseIdToRefresh) {
      void refreshPortalCaseRecord(caseIdToRefresh).catch(error => {
        console.error('Failed to refresh portal case after closing survey:', error);
      });
      void loadPortalSurveys({ force: true });
    }
  }, [selectedPortalSurvey, refreshPortalCaseRecord, loadPortalSurveys]);

  useEffect(() => {
    if (!['overview', 'surveys'].includes(activePortalTab)) return;
    loadPortalSurveys();
  }, [activePortalTab, loadPortalSurveys]);

  useEffect(() => {
    if (activePortalTab === 'overview') return;
    setActiveOverviewQueue(null);
  }, [activePortalTab]);

  useEffect(() => {
    setUserActivityPage({ surface: 'CUSTOMER_PORTAL', pageKey: activePortalTab });
  }, [activePortalTab]);

  useEffect(() => {
    loadPortalNotificationPopups();
  }, [loadPortalNotificationPopups]);

  useEffect(() => {
    if (!isLitePortal) return;
    if (['overview', 'history', 'surveys'].includes(activePortalTab)) {
      setActivePortalTab('support');
    }
    if (messageWorkspaceMode !== 'notifications') {
      setMessageWorkspaceMode('notifications');
    }
    if (portalDeviceModalSection === 'history') {
      setPortalDeviceModalSection('details');
    }
  }, [activePortalTab, isLitePortal, messageWorkspaceMode, portalDeviceModalSection]);

  useEffect(() => {
    if (activePortalTab !== 'messages' || messageWorkspaceMode !== 'notifications') return;
    loadPortalNotifications();
  }, [activePortalTab, messageWorkspaceMode, loadPortalNotifications]);

  useEffect(() => {
    if (activePortalTab !== 'messages') return;
    loadPortalMessages();
  }, [activePortalTab, loadPortalMessages]);

  useEffect(() => {
    if (activePortalTab !== 'messages') return;
    const timer = window.setInterval(() => {
      loadPortalMessages();
      if (messageWorkspaceMode === 'notifications') {
        loadPortalNotifications();
      }
    }, 15000);
    return () => window.clearInterval(timer);
  }, [activePortalTab, loadPortalMessages, loadPortalNotifications, messageWorkspaceMode]);

  const handleModelChange = (value: string) => {
    setUsingAssignedDevice(false);
    setSelectedAssignedModelKey(customerDevices.length > 0 ? 'MANUAL' : '');
    setSelectedAssignedDeviceId('');
    setManualIdentifierEntry(customerDevices.length > 0);
    setSelectedModel(value);
    if (value === 'OTHER') {
      setIsCustomModel(true);
      setCustomModelNumber('');
      setCustomManufacturer('');
    } else {
      setIsCustomModel(false);
      const model = deviceModels.find(m => m.id === value);
      if (model) {
        setCustomModelNumber(model.modelNumber);
        setCustomManufacturer(model.manufacturer);
      }
    }
  };

  const getAssignedDeviceModelKey = useCallback((device: Partial<Device>) => {
    return device.modelId || device.modelNumber || device.id || '';
  }, [customerPortalAccess?.customers]);

  const assignedModelOptions = useMemo(() => {
    const grouped = new Map<
      string,
      { key: string; label: string; modelId?: string | null; manufacturer?: string | null }
    >();

    customerDevices.forEach(device => {
      const key = getAssignedDeviceModelKey(device);
      if (!grouped.has(key)) {
        const number = device.modelNumber || device.model?.modelNumber || 'Unknown model';
        const manufacturer = device.manufacturer || device.model?.manufacturer || '';
        grouped.set(key, {
          key,
          label: manufacturer ? `${number} - ${manufacturer}` : number,
          modelId: device.modelId || device.model?.id || null,
          manufacturer
        });
      }
    });

    return Array.from(grouped.values());
  }, [customerDevices, getAssignedDeviceModelKey]);

  const assignedSerialOptions = useMemo(() => {
    if (!selectedAssignedModelKey) return [] as Device[];
    return customerDevices.filter(device => {
      const key = getAssignedDeviceModelKey(device);
      return key === selectedAssignedModelKey;
    });
  }, [customerDevices, selectedAssignedModelKey, getAssignedDeviceModelKey]);

  const selectedAssignedDevice = useMemo(() => {
    if (!selectedAssignedDeviceId) return null;
    return assignedSerialOptions.find(device => device.id === selectedAssignedDeviceId) || null;
  }, [assignedSerialOptions, selectedAssignedDeviceId]);

  const selectedAssignedAssetSelectValue = selectedAssignedDevice?.assetNumber
    ? selectedAssignedDevice.id
    : '';
  const selectedAssignedSerialSelectValue = selectedAssignedDevice?.serialNumber
    ? selectedAssignedDevice.id
    : '';

  const canContinue = useMemo(() => {
    const hasIdentifier = portalCustomer?.assetNumbersEnabled
      ? Boolean(serialNumber.trim() || assetNumber.trim())
      : Boolean(serialNumber.trim());
    if (!hasIdentifier) return false;
    if (usingAssignedDevice) {
      if (!selectedAssignedModelKey) return false;
      return manualIdentifierEntry ? hasIdentifier : Boolean(selectedAssignedDeviceId);
    }
    if (isCustomModel) {
      return customModelNumber.trim().length > 0 && customManufacturer.trim().length > 0;
    }
    return !!selectedModel;
  }, [
    serialNumber,
    assetNumber,
    portalCustomer?.assetNumbersEnabled,
    usingAssignedDevice,
    manualIdentifierEntry,
    selectedAssignedModelKey,
    selectedAssignedDeviceId,
    isCustomModel,
    customModelNumber,
    customManufacturer,
    selectedModel
  ]);

  const handleAssignedModelSelect = (value: string) => {
    if (!value) {
      setUsingAssignedDevice(false);
      setSelectedAssignedModelKey('');
      setSelectedAssignedDeviceId('');
      setIsCustomModel(false);
      setSelectedModel('');
      setCustomModelNumber('');
      setCustomManufacturer('');
      setSerialNumber('');
      setAssetNumber('');
      setManualIdentifierEntry(false);
      return;
    }

    if (value === 'MANUAL') {
      setUsingAssignedDevice(false);
      setSelectedAssignedModelKey('MANUAL');
      setSelectedAssignedDeviceId('');
      setIsCustomModel(false);
      setSelectedModel('');
      setCustomModelNumber('');
      setCustomManufacturer('');
      setSerialNumber('');
      setAssetNumber('');
      setManualIdentifierEntry(true);
      return;
    }

    setUsingAssignedDevice(true);
    setSelectedAssignedModelKey(value);
    setSelectedAssignedDeviceId('');
    setSerialNumber('');
    setAssetNumber('');
    setManualIdentifierEntry(false);
    const option = assignedModelOptions.find(opt => opt.key === value);

    if (option?.modelId) {
      setSelectedModel(option.modelId);
      setIsCustomModel(false);
      const [modelNumber, manufacturer] = option.label.split(' - ');
      setCustomModelNumber(modelNumber);
      setCustomManufacturer(manufacturer || option.manufacturer || '');
    } else {
      const [modelNumber, manufacturer] = option?.label.split(' - ') || ['', ''];
      setSelectedModel('OTHER');
      setIsCustomModel(true);
      setCustomModelNumber(modelNumber || '');
      setCustomManufacturer(manufacturer || option?.manufacturer || '');
    }
  };

  const applyAssignedDeviceSelection = useCallback((device: Device) => {
    if (device) {
      setSelectedAssignedDeviceId(device.id);
      setSerialNumber(device.serialNumber || '');
      setAssetNumber(device.assetNumber || '');
      setManualIdentifierEntry(false);
      if (device.modelId) {
        setSelectedModel(device.modelId);
        setIsCustomModel(false);
        setCustomModelNumber(device.modelNumber);
        setCustomManufacturer(device.manufacturer || '');
      }
      if (portalCustomer) {
        setContactEmail(portalCustomer.email || '');
        setContactPhone(portalCustomer.phone || '');
      }
    }
  }, [portalCustomer]);

  const clearAssignedIdentifierSelection = (manualEntry = false) => {
    setSelectedAssignedDeviceId('');
    setSerialNumber('');
    setAssetNumber('');
    setManualIdentifierEntry(manualEntry);
  };

  const handleAssignedSerialSelect = (value: string) => {
    if (!value) {
      clearAssignedIdentifierSelection(false);
      return;
    }
    if (value === 'MANUAL_SERIAL') {
      clearAssignedIdentifierSelection(true);
      return;
    }

    const device = customerDevices.find(d => d.id === value);
    if (device) applyAssignedDeviceSelection(device);
  };

  const handleAssignedAssetSelect = (value: string) => {
    if (!value) {
      clearAssignedIdentifierSelection(false);
      return;
    }
    if (value === 'MANUAL_ASSET') {
      clearAssignedIdentifierSelection(true);
      return;
    }

    const device = customerDevices.find(d => d.id === value);
    if (device) applyAssignedDeviceSelection(device);
  };

  useEffect(() => {
    if (!pendingDeviceQrIntent?.token || customerDevices.length === 0) return;

    const intentKey = `${pendingDeviceQrIntent.token}:${pendingDeviceQrIntent.intent}`;
    if (handledDeviceQrIntentRef.current === intentKey) return;
    handledDeviceQrIntentRef.current = intentKey;

    let cancelled = false;
    const applyQrIntent = async () => {
      try {
        const resolved = await deviceApi.resolveQrToken(pendingDeviceQrIntent.token);
        if (cancelled) return;
        const resolvedDeviceId = String(resolved?.deviceId || resolved?.id || '').trim();
        const linkedDevice = customerDevices.find(device => device.id === resolvedDeviceId);
        if (!resolvedDeviceId || !linkedDevice) {
          throw new Error('Device QR target is not available to this portal account.');
        }

        setUsingAssignedDevice(true);
        setSelectedAssignedModelKey(getAssignedDeviceModelKey(linkedDevice));
        applyAssignedDeviceSelection(linkedDevice);
        setDeviceInfo({
          id: linkedDevice.id,
          modelNumber: linkedDevice.modelNumber || linkedDevice.model?.modelNumber || '',
          serialNumber: linkedDevice.serialNumber || '',
          assetNumber: linkedDevice.assetNumber || null,
          customerId: linkedDevice.customerId || portalCustomer?.id || '',
          isCustom: false
        });
        setSupportRequestNotice(null);

        if (pendingDeviceQrIntent.intent === 'devices') {
          setActivePortalTab('devices');
          setPortalDeviceModalSection('details');
          setPendingDeviceModalId(resolvedDeviceId);
        } else {
          setActivePortalTab('support');
          setCurrentStep(1);
        }

        setPendingDeviceQrIntent(null);
      } catch (error) {
        if (cancelled) return;
        console.warn('Failed to resolve device QR link:', error);
        setActivePortalTab('support');
        setSupportRequestNotice({
          type: 'error',
          message: 'This QR code could not be opened for your portal account.'
        });
        setPendingDeviceQrIntent(null);
      }
    };

    void applyQrIntent();
    return () => {
      cancelled = true;
    };
  }, [
    pendingDeviceQrIntent,
    customerDevices,
    getAssignedDeviceModelKey,
    applyAssignedDeviceSelection,
    portalCustomer?.id
  ]);

  const handleDeviceLookup = async () => {
    const submittedSerialNumber = serialNumber.trim();
    const submittedAssetNumber = portalCustomer?.assetNumbersEnabled ? assetNumber.trim() : '';
    const lookupIdentifier = submittedSerialNumber || submittedAssetNumber;

    if (!lookupIdentifier) {
      setSupportRequestNotice({
        type: 'error',
        message: portalCustomer?.assetNumbersEnabled
          ? 'Enter a serial or asset number before continuing.'
          : 'Enter a serial number before continuing.'
      });
      return;
    }

    if (usingAssignedDevice && !selectedAssignedModelKey) {
      setSupportRequestNotice({
        type: 'error',
        message: 'Choose one of your registered devices or switch to manual entry.'
      });
      return;
    }

    if (isCustomModel) {
      if (!customModelNumber.trim() || !customManufacturer.trim()) {
        setSupportRequestNotice({
          type: 'error',
          message: 'Model number and manufacturer are required for custom devices.'
        });
        return;
      }
      setDeviceInfo({
        id: `custom-${Date.now()}`,
        modelNumber: customModelNumber,
        serialNumber: submittedSerialNumber,
        assetNumber: submittedAssetNumber || null,
        customerId: portalCustomer?.id || '',
        isCustom: true
      });
      if (portalCustomer) {
        setContactEmail(portalCustomer.email || '');
        setContactPhone(portalCustomer.phone || '');
      }
      setSupportRequestNotice(null);
      setCurrentStep(2);
      return;
    }

    if (usingAssignedDevice && selectedAssignedModelKey) {
      const matched = customerDevices.find(device => device.id === selectedAssignedDeviceId);
      if (matched) {
        setDeviceInfo({
          id: matched.id,
          modelNumber: matched.modelNumber,
          serialNumber: matched.serialNumber,
          assetNumber: matched.assetNumber || null,
          customerId: matched.customerId || portalCustomer?.id || '',
          isCustom: false
        });
        if (portalCustomer) {
          setContactEmail(portalCustomer.email || '');
          setContactPhone(portalCustomer.phone || '');
        }
        setSupportRequestNotice(null);
        setCurrentStep(2);
        setIsCustomModel(false);
        return;
      }

      const option = assignedModelOptions.find(opt => opt.key === selectedAssignedModelKey);
      if (option) {
        const [modelNumber] = option.label.split(' - ');
        setDeviceInfo({
          id: `assigned-${Date.now()}`,
          modelNumber,
          serialNumber: submittedSerialNumber,
          assetNumber: submittedAssetNumber || null,
          customerId: portalCustomer?.id || '',
          isCustom: false
        });
        if (portalCustomer) {
          setContactEmail(portalCustomer.email || '');
          setContactPhone(portalCustomer.phone || '');
        }
        setSupportRequestNotice(null);
        setCurrentStep(2);
        setIsCustomModel(false);
        return;
      }
    }

    if (!selectedModel) {
      setSupportRequestNotice({
        type: 'error',
        message: 'Select a device model or choose one of your registered devices before continuing.'
      });
      return;
    }

    try {
      const device = await deviceApi.getBySerial(lookupIdentifier);
      if (device) {
        setDeviceInfo({
          id: device.id,
          modelNumber: device.modelNumber,
          serialNumber: device.serialNumber,
          assetNumber: device.assetNumber || null,
          customerId: device.customerId || portalCustomer?.id || '',
          isCustom: false
        });
        setContactEmail(device.customer?.email || portalCustomer?.email || '');
        setContactPhone(device.customer?.phone || portalCustomer?.phone || '');
        setSupportRequestNotice(null);
        setCurrentStep(2);
        return;
      }
    } catch (error) {
      console.error('Error looking up device by serial', error);
    }

    const fallbackModel = deviceModels.find(m => m.id === selectedModel);
    if (fallbackModel) {
      setDeviceInfo({
        id: `new-${Date.now()}`,
        modelNumber: fallbackModel.modelNumber,
        serialNumber: submittedSerialNumber,
        assetNumber: submittedAssetNumber || null,
        customerId: portalCustomer?.id || '',
        isCustom: false
      });
      if (portalCustomer) {
        setContactEmail(portalCustomer.email || '');
        setContactPhone(portalCustomer.phone || '');
      }
      setSupportRequestNotice(null);
      setCurrentStep(2);
      return;
    }

    setSupportRequestNotice({
      type: 'error',
      message: 'Complete the device details before continuing.'
    });
  };

  const formatDateTime = (value?: string | null) =>
    formatPortalDateTimeValue(value, effectivePortalSettings.dateFormat, 'Not available');

  const getStatusBadgeStyles = (status?: string | null) => {
    const normalized = (status || '').toUpperCase();
    switch (normalized) {
      case 'OPEN':
        return { backgroundColor: '#dbeafe', color: '#1d4ed8' };
      case 'IN_PROGRESS':
      case 'ACTIVE':
        return { backgroundColor: '#fef3c7', color: '#92400e' };
      case 'WAITING_CUSTOMER':
      case 'WAITING_PARTS':
      case 'ON_HOLD':
        return { backgroundColor: '#ede9fe', color: '#5b21b6' };
      case 'RESOLVED':
      case 'CLOSED':
      case 'COMPLETE':
        return { backgroundColor: '#dcfce7', color: '#166534' };
      default:
        return { backgroundColor: '#e5e7eb', color: '#374151' };
    }
  };

  const formatPortalCaseStatusLabel = (status?: string | null, fallback = 'Unknown') => {
    const normalized = String(status || '').trim().toUpperCase();
    switch (normalized) {
      case 'NEW':
      case 'OPEN':
        return 'Open';
      case 'IN_PROGRESS':
      case 'ACTIVE':
        return 'In progress';
      case 'WAITING_CUSTOMER':
        return 'Waiting customer';
      case 'WAITING_PARTS':
        return 'Waiting parts';
      case 'ON_HOLD':
        return 'On hold';
      case 'RESOLVED':
        return 'Resolved';
      case 'CLOSED':
      case 'COMPLETE':
        return 'Closed';
      default:
        return normalized
          ? normalized
              .toLowerCase()
              .split('_')
              .filter(Boolean)
              .map(part => part.charAt(0).toUpperCase() + part.slice(1))
              .join(' ')
          : fallback;
    }
  };

  const formatDeviceLifecycle = (value?: string | null) => {
    const normalized = String(value || '').trim().toUpperCase();
    if (!normalized) return 'Unknown';
    return normalized
      .toLowerCase()
      .split('_')
      .filter(Boolean)
      .map(part => part[0].toUpperCase() + part.slice(1))
      .join(' ');
  };

  const getDeviceLifecycleBadgeStyles = (value?: string | null) => {
    const normalized = String(value || '').trim().toUpperCase();
    switch (normalized) {
      case 'DEPLOYED':
        return { backgroundColor: '#e8f3ef', borderColor: '#bfd8cb', color: '#1b5e48' };
      case 'INVENTORY':
        return { backgroundColor: '#eef5f4', borderColor: '#cfe0de', color: '#123F43' };
      case 'INACTIVE':
        return { backgroundColor: '#f5f5f4', borderColor: '#d6d3d1', color: '#57534e' };
      case 'RETIRED':
        return { backgroundColor: '#fff7f4', borderColor: '#f0c9c2', color: '#9f3f2d' };
      default:
        return { backgroundColor: BRAND_SURFACE, borderColor: BRAND_BORDER, color: BRAND_TEXT };
    }
  };

  const toggleHistoryCase = (caseId: string) => {
    setExpandedHistoryCases(prev => ({
      ...prev,
      [caseId]: !prev[caseId]
    }));
  };

  const closeHistoryPanel = () => {
    setIsHistoryOpen(false);
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files) {
      setUploadedFiles(prev => [...prev, ...Array.from(files)]);
    }
  };

  const handleRemoveFile = (index: number) => {
    setUploadedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleQuickAnswerChoice = (questionId: string, answer: QuickAnswerValue | null) => {
    setQuickAnswerMap(prev => {
      const next = { ...prev };
      if (!answer) {
        delete next[questionId];
        return next;
      }
      next[questionId] = answer;
      return next;
    });
  };

  const slugifyQuestion = (value: string) =>
    value
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '');

  const buildFallbackQuestions = useCallback(() => {
    const quickButtons: DynamicQuestion[] = [
      { id: 'power_on', label: 'Does the device power on?' },
      { id: 'error_message', label: 'Is an error message displayed?' },
      { id: 'unusual_noise', label: 'Is there unusual noise or vibration?' },
      { id: 'performance_drop', label: 'Is performance worse than normal?' },
      { id: 'connectivity', label: 'Is there a connectivity issue?' },
      { id: 'recent_change', label: 'Was anything changed recently?' }
    ];
    const textQuestions: DynamicQuestion[] = [
      { id: 'first_occurred', label: 'When did this problem first occur?' },
      { id: 'recent_changes', label: 'Has anything changed recently (maintenance, supplies, environment)?' },
      { id: 'error_codes', label: 'Are there any error codes or messages displayed?' },
      { id: 'intermittent', label: 'Is the issue constant or intermittent?' },
      { id: 'before_start', label: 'What was happening right before the issue started?' }
    ];

    return { quickButtons, textQuestions };
  }, []);

  const normaliseDynamicQuestions = useCallback(
    (payload: any): DynamicQuestionSet => {
      const fallback = buildFallbackQuestions();
      const toQuestion = (item: any) => {
        if (!item) return null;
        const label = String(item.label || item.question || item.text || item).trim();
        if (!label) return null;
        const withQuestion = label.endsWith('?') ? label : `${label}?`;
        const id = slugifyQuestion(String(item.id || withQuestion)) || slugifyQuestion(withQuestion);
        if (!id) return null;
        return { id, label: withQuestion };
      };

      const normalizeLabel = (value: string) =>
        value
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, ' ')
          .replace(/\s+/g, ' ')
          .trim();

      const buildList = (items: any[], fallbackItems: DynamicQuestion[], min: number, max: number) => {
        const next: DynamicQuestion[] = [];
        const seenIds = new Set<string>();
        const seenLabels = new Set<string>();
        const add = (entry: any) => {
          const normalized = toQuestion(entry);
          if (!normalized) return;
          const labelKey = normalizeLabel(normalized.label);
          if (seenIds.has(normalized.id) || (labelKey && seenLabels.has(labelKey))) return;
          seenIds.add(normalized.id);
          if (labelKey) seenLabels.add(labelKey);
          next.push(normalized);
        };

        if (Array.isArray(items)) items.forEach(add);
        const trimmed = next.slice(0, max);
        if (trimmed.length === 0) return fallbackItems.slice(0, max);
        return trimmed;
      };

      return {
        quickButtons: buildList(payload?.quickButtons || [], fallback.quickButtons, 3, 6),
        textQuestions: buildList(payload?.textQuestions || [], fallback.textQuestions, 2, 5),
        source: payload?.source === 'llm' ? 'llm' : 'fallback'
      };
    },
    [buildFallbackQuestions]
  );

  const fetchDynamicQuestions = useCallback(
    async (force = false) => {
      if (dynamicQuestionsLoading) return;
      if (dynamicQuestionsLoaded && !force) return;
      if (!problemDescription.trim()) {
        const fallback = buildFallbackQuestions();
        setDynamicQuestions({
          quickButtons: fallback.quickButtons,
          textQuestions: fallback.textQuestions,
          source: 'fallback'
        });
        setDynamicQuestionsLoaded(true);
        return;
      }
      if (!dynamicQuestionsLoaded) {
        const fallback = buildFallbackQuestions();
        setDynamicQuestions({
          quickButtons: fallback.quickButtons,
          textQuestions: fallback.textQuestions,
          source: 'fallback'
        });
      }
      setDynamicQuestionsLoading(true);
      setDynamicQuestionsError('');

      try {
        const modelRecord = deviceModels.find(model => model.id === selectedModel);
        const devicePayload = {
          modelNumber: deviceInfo?.modelNumber || customModelNumber || modelRecord?.modelNumber || '',
          manufacturer: customManufacturer || modelRecord?.manufacturer || '',
          modelName: modelRecord?.modelName || '',
          deviceType: modelRecord?.deviceType || '',
          serialNumber: deviceInfo?.serialNumber || serialNumber,
          assetNumber: deviceInfo?.assetNumber || assetNumber,
          description: modelRecord?.description || ''
        };
        const payload = await supportQuestionsApi.generate({
          device: devicePayload,
          problemDescription: problemDescription.trim()
        });
        const normalized = normaliseDynamicQuestions(payload);
        setDynamicQuestions(normalized);
        setDynamicQuestionsLoaded(true);
      } catch (error) {
        console.error('Failed to load dynamic questions', error);
        const fallback = buildFallbackQuestions();
        setDynamicQuestions({
          quickButtons: fallback.quickButtons,
          textQuestions: fallback.textQuestions,
          source: 'fallback'
        });
        setDynamicQuestionsLoaded(true);
        setDynamicQuestionsError('Using default questions.');
      } finally {
        setDynamicQuestionsLoading(false);
      }
    },
    [
      buildFallbackQuestions,
      customManufacturer,
      customModelNumber,
      deviceInfo,
      deviceModels,
      dynamicQuestionsLoaded,
      dynamicQuestionsLoading,
      normaliseDynamicQuestions,
      problemDescription,
      selectedModel,
      assetNumber,
      serialNumber
    ]
  );

  useEffect(() => {
    if (currentStep !== 3) return;
    fetchDynamicQuestions();
  }, [currentStep, fetchDynamicQuestions]);

  const resetSupportRequestForm = useCallback(
    (options: { clearCaseNumber?: boolean } = {}) => {
      setCurrentStep(1);
      setSelectedModel('');
      setSerialNumber('');
      setAssetNumber('');
      setDeviceInfo(null);
      setProblemDescription('');
      setUploadedFiles([]);
      setQuickAnswerMap({});
      setAdditionalNotes('');
      setDynamicQuestions({
        quickButtons: [],
        textQuestions: [],
        source: 'fallback'
      });
      setDynamicQuestionsLoading(false);
      setDynamicQuestionsError('');
      setDynamicQuestionsLoaded(false);
      setIsCustomModel(false);
      setCustomModelNumber('');
      setCustomManufacturer('');
      setSelectedAssignedModelKey('');
      setSelectedAssignedDeviceId('');
      setUsingAssignedDevice(false);
      setManualIdentifierEntry(false);
      setContactEmail(portalCustomer?.email || '');
      setContactPhone(portalCustomer?.phone || '');
      setSupportRequestNotice(null);
      if (options.clearCaseNumber) {
        setCaseLabel('Case');
        setCaseNumber('');
      }
    },
    [portalCustomer]
  );

  const handleSubmit = async () => {
    if (isSubmitting) return;

    if (!deviceInfo || !problemDescription) {
      setSupportRequestNotice({
        type: 'error',
        message: 'Provide device details and a description of the problem before submitting.'
      });
      return;
    }

    const formData = new FormData();
    formData.append('customerId', deviceInfo?.customerId || '');
    formData.append('serialNumber', deviceInfo?.serialNumber || '');
    if (deviceInfo?.assetNumber || assetNumber) {
      formData.append('assetNumber', deviceInfo?.assetNumber || assetNumber);
    }
    formData.append('deviceModel', deviceInfo?.modelNumber || '');
    formData.append('problemDescription', problemDescription);
    formData.append('isCustomModel', isCustomModel ? 'true' : 'false');
    if (customManufacturer) formData.append('customManufacturer', customManufacturer);
    if (contactEmail) formData.append('contactEmail', contactEmail);
    if (contactPhone) formData.append('contactPhone', contactPhone);
    
    const quickAnswers = dynamicQuestions.quickButtons
      .map(question => {
        const answer = quickAnswerMap[question.id];
        if (!answer || answer === 'SKIP') return null;
        return {
          question: question.label,
          answer: answer === 'YES' ? 'Yes' : 'No'
        };
      })
      .filter(Boolean) as Array<{ question: string; answer: string }>;

    const notes = additionalNotes.trim();
    if (notes) {
      quickAnswers.push({
        question: 'Additional notes',
        answer: notes
      });
    }

    if (quickAnswers?.length) {
      formData.append('customerAnswers', JSON.stringify(quickAnswers));
    }
    
    uploadedFiles.forEach(file => {
      formData.append('files', file);
    });

    setIsSubmitting(true);
    try {
      const result = await caseApi.submit(formData);
      const friendlyNumber =
        result.caseCode ||
        result.caseNumber ||
        result.case?.caseCode ||
        result.id ||
        '';
      const friendlyLabel =
        (typeof result.caseLabel === 'string' && result.caseLabel.trim()) ||
        (typeof result.case?.caseLabel === 'string' && result.case.caseLabel.trim()) ||
        'Case';
      setSupportRequestNotice(null);
      setCaseLabel(friendlyLabel);
      setCaseNumber(friendlyNumber);
      setCurrentStep(5);
    } catch (err: any) {
      console.error('Failed to submit case:', err);
      setSupportRequestNotice({
        type: 'error',
        message: err?.message || 'Failed to submit case. Please try again.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const isClosedStatus = (status?: string | null) => {
    const normalized = (status || '').toUpperCase();
    return ['CLOSED', 'COMPLETED', 'RESOLVED'].includes(normalized);
  };

  const formatPortalDate = (value?: string | null) =>
    formatPortalDateValue(value, effectivePortalSettings.dateFormat);

  const formatPortalDateTime = (value?: string | null) =>
    formatPortalDateTimeValue(value, effectivePortalSettings.dateFormat);

  const formatPortalCurrency = (value?: number | null, currency = effectivePortalSettings.currency) =>
    formatPortalCurrencyValue(value, currency || DEFAULT_PORTAL_SETTINGS.currency);

  const formatPortalLocation = (device?: Device | null) => {
    if (!device) return 'Location unknown';
    const location = device.location?.name || '';
    if (location) return location;
    return device.locationId ? 'Location on file' : 'Location unknown';
  };

  const getDeviceLabel = (device: Device) => {
    const model = device.modelNumber || device.model?.modelNumber || 'Unknown model';
    const manufacturer = device.manufacturer || device.model?.manufacturer || '';
    return manufacturer ? `${model} - ${manufacturer}` : model;
  };

  const formatDeviceIdentifier = (device?: Pick<Device, 'serialNumber' | 'assetNumber'> | DeviceInfo | null) => {
    const serial = String(device?.serialNumber || '').trim() || 'Not recorded';
    const asset = String((device as any)?.assetNumber || '').trim();
    if (portalCustomer?.assetNumbersEnabled && asset) {
      return `Asset ${asset} | Serial ${serial}`;
    }
    return `Serial ${serial}`;
  };

  const getDeviceInitials = (device: Device) => {
    const label = getDeviceLabel(device);
    const parts = label.split(/[^A-Za-z0-9]+/).filter(Boolean);
    const initials = parts
      .slice(0, 2)
      .map(part => part[0])
      .join('');
    return initials.toUpperCase() || 'DV';
  };

  const getDeviceImageSrc = (device: Device) => {
    const rawUrl =
      (device as any)?.imageUrl ||
      (device.model as any)?.imageUrl ||
      null;
    if (!rawUrl) return '';
    if (/^https?:\/\//i.test(rawUrl)) return rawUrl;
    if (!portalApiBase) return rawUrl;
    const normalised = String(rawUrl).replace(/\\\\/g, '/').replace(/\\/g, '/');
    const trimmed = normalised.startsWith('/') ? normalised.slice(1) : normalised;
    const prefix = portalApiBase.endsWith('/') ? portalApiBase : `${portalApiBase}/`;
    return `${prefix}${trimmed}`;
  };

  const getDeviceQrUrl = (deviceQrToken: string) => {
    const base = window.location.href.split('#')[0] || window.location.href;
    return `${base}#/?deviceToken=${encodeURIComponent(deviceQrToken)}`;
  };

  const handlePrintQr = (deviceId: string, deviceQrToken: string) => {
    if (!deviceQrToken) return;
    const canvas = document.getElementById(`qr-canvas-${deviceId}`) as HTMLCanvasElement | null;
    if (!canvas) return;
    const dataUrl = canvas.toDataURL('image/png');
    const popup = window.open('', '_blank');
    if (!popup) return;
    popup.document.write(`<!doctype html><html><head><title>Device QR</title></head><body style="margin:24px;font-family:Arial, sans-serif;">`);
    popup.document.write(`<img src="${dataUrl}" style="width:240px;height:240px;display:block;margin-bottom:12px;" />`);
    popup.document.write(`<div>${getDeviceQrUrl(deviceQrToken)}</div>`);
    popup.document.write(`</body></html>`);
    popup.document.close();
    popup.focus();
    popup.print();
  };

  const ensureDeviceQrToken = useCallback(async (deviceId: string) => {
    const normalizedDeviceId = String(deviceId || '').trim();
    if (!normalizedDeviceId) return '';
    const existing = deviceQrTokens[normalizedDeviceId];
    if (existing) return existing;

    try {
      setDeviceQrLoadingId(normalizedDeviceId);
      setDeviceQrError('');
      const payload = await deviceApi.ensureQrToken(normalizedDeviceId);
      const token = String(payload?.qrToken || '').trim();
      if (!token) throw new Error('Device QR token was not returned.');
      setDeviceQrTokens(prev => ({ ...prev, [normalizedDeviceId]: token }));
      return token;
    } catch (error) {
      setDeviceQrError(error instanceof Error ? error.message : 'Failed to prepare the QR link.');
      return '';
    } finally {
      setDeviceQrLoadingId(current => (current === normalizedDeviceId ? null : current));
    }
  }, [deviceQrTokens]);

  const handleAccountPreferenceChange = (
    field: 'currency' | 'units' | 'dateFormat',
    value: string
  ) => {
    setAccountPreferencesNotice(null);
    setPortalSettingsError('');
    setAccountPreferences(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleAccountProfileFieldChange = (
    field: 'fullName' | 'email' | 'confirmEmail' | 'currentPassword' | 'phone' | 'role',
    value: string
  ) => {
    setAccountProfileNotice(null);
    setAccountProfileError('');
    setAccountProfileForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSaveAccountProfile = async () => {
    if (!accountProfile) {
      setAccountProfileNotice({
        type: 'error',
        message: 'Your linked contact details are not loaded yet.'
      });
      return;
    }

    const fullName = accountProfileForm.fullName.trim();
    const email = normalizePortalProfileEmail(accountProfileForm.email);
    const confirmEmail = normalizePortalProfileEmail(accountProfileForm.confirmEmail);
    const currentPassword = accountProfileForm.currentPassword;
    const emailChanged = email !== normalizePortalProfileEmail(accountProfile.email);

    if (!fullName) {
      setAccountProfileNotice({
        type: 'error',
        message: 'Full name is required.'
      });
      return;
    }
    if (!email) {
      setAccountProfileNotice({
        type: 'error',
        message: 'Email is required.'
      });
      return;
    }
    if (emailChanged && confirmEmail !== email) {
      setAccountProfileNotice({
        type: 'error',
        message: 'Confirm email must match the new email address.'
      });
      return;
    }
    if (emailChanged && !currentPassword) {
      setAccountProfileNotice({
        type: 'error',
        message: 'Current password is required before changing your email address.'
      });
      return;
    }

    try {
      setAccountProfileSaving(true);
      setAccountProfileNotice(null);
      setAccountProfileError('');
      const base = await getBaseUrl();
      const profile = await updateOwnAccountProfile(base, {
        fullName,
        email,
        phone: accountProfileForm.phone.trim() || null,
        role: accountProfileForm.role.trim() || null,
        confirmEmail: emailChanged ? confirmEmail : null,
        currentPassword: emailChanged ? currentPassword : null
      });
      applyAccountProfile(profile);
      setAccountProfileNotice({
        type: 'success',
        message: emailChanged ? 'Contact details and portal email updated.' : 'Contact details updated.'
      });
    } catch (error: any) {
      console.error('Failed to update account profile:', error);
      setAccountProfileNotice({
        type: 'error',
        message: error?.message || 'Unable to update your contact details right now.'
      });
    } finally {
      setAccountProfileSaving(false);
    }
  };

  const handleSaveAccountPreferences = async () => {
    if (!user?.id) {
      setAccountPreferencesNotice({
        type: 'error',
        message: 'You must be signed in to save preferences.'
      });
      return;
    }

    try {
      setAccountPreferencesSaving(true);
      setAccountPreferencesNotice(null);
      setPortalSettingsError('');
      const base = await getBaseUrl();
      await updateUserSettings(base, user.id, {
        currency: accountPreferences.currency || null,
        units: accountPreferences.units || null,
        dateFormat: accountPreferences.dateFormat || null
      });
      const refreshedSettings = await fetchSettings(base);
      applyPortalSettings(refreshedSettings);
      setAccountPreferencesNotice({
        type: 'success',
        message: 'Account display preferences saved.'
      });
    } catch (error: any) {
      console.error('Failed to save account preferences:', error);
      setAccountPreferencesNotice({
        type: 'error',
        message: error?.message || 'Unable to save account preferences right now.'
      });
    } finally {
      setAccountPreferencesSaving(false);
    }
  };

  const handlePasswordFieldChange = (
    field: 'currentPassword' | 'newPassword' | 'confirmPassword',
    value: string
  ) => {
    setPasswordNotice(null);
    setPasswordForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleChangePassword = async () => {
    const { currentPassword, newPassword, confirmPassword } = passwordForm;

    if (!currentPassword || !newPassword || !confirmPassword) {
      setPasswordNotice({
        type: 'error',
        message: 'Enter your current password, new password, and confirmation.'
      });
      return;
    }
    if (newPassword.length < 10) {
      setPasswordNotice({
        type: 'error',
        message: 'New password must be at least 10 characters.'
      });
      return;
    }
    if (newPassword !== confirmPassword) {
      setPasswordNotice({
        type: 'error',
        message: 'New password and confirmation do not match.'
      });
      return;
    }
    if (currentPassword === newPassword) {
      setPasswordNotice({
        type: 'error',
        message: 'New password must be different from your current password.'
      });
      return;
    }

    try {
      setPasswordSaving(true);
      setPasswordNotice(null);
      await changeOwnPassword({
        currentPassword,
        newPassword,
        confirmPassword
      });
      setPasswordForm({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
      setPasswordNotice({
        type: 'success',
        message: 'Password updated successfully.'
      });
    } catch (error: any) {
      console.error('Failed to change password:', error);
      setPasswordNotice({
        type: 'error',
        message: error?.message || 'Unable to change password right now.'
      });
    } finally {
      setPasswordSaving(false);
    }
  };

  const handleDeactivateOwnAccount = async () => {
    if (deactivateConfirmText.trim().toUpperCase() !== 'DEACTIVATE') {
      setDeactivateNotice({
        type: 'error',
        message: 'Type DEACTIVATE to confirm account deactivation.'
      });
      return;
    }

    try {
      setDeactivateSubmitting(true);
      setDeactivateNotice(null);
      await deactivateOwnPortalAccess();
      storeLoginNotice('Portal access was deactivated for this account. Contact your service provider to have portal access restored.');
      await logout();
    } catch (error: any) {
      console.error('Failed to deactivate portal access:', error);
      setDeactivateNotice({
        type: 'error',
        message: error?.message || 'Unable to deactivate portal access right now.'
      });
    } finally {
      setDeactivateSubmitting(false);
    }
  };

  const sortedPortalCases = useMemo(() => {
    return [...portalCases].sort((a, b) => {
      const aTime = new Date(a.closedAt || a.updatedAt || a.createdAt || 0).getTime();
      const bTime = new Date(b.closedAt || b.updatedAt || b.createdAt || 0).getTime();
      return bTime - aTime;
    });
  }, [portalCases]);

  const availablePortalSurveys = useMemo(
    () =>
      portalSurveys
        .filter(survey => survey.status === 'AVAILABLE')
        .slice()
        .sort((a, b) => {
          const aTime = new Date(a.releasedAt || a.completedAt || 0).getTime();
          const bTime = new Date(b.releasedAt || b.completedAt || 0).getTime();
          return bTime - aTime;
        }),
    [portalSurveys]
  );

  const pendingPortalSurveyCount = useMemo(
    () => availablePortalSurveys.length,
    [availablePortalSurveys]
  );

  const completedPortalSurveyCount = useMemo(
    () => portalSurveys.filter(survey => survey.status === 'COMPLETED').length,
    [portalSurveys]
  );

  const surveyCaseOptions = useMemo(() => {
    const optionMap = new Map<string, string>();
    portalSurveys.forEach(survey => {
      const caseId = String(survey.caseId || survey.case?.id || '').trim();
      if (!caseId) return;
      const label = getPortalSurveyCaseLabel(survey.case);
      if (!optionMap.has(caseId)) {
        optionMap.set(caseId, label);
      }
    });
    return Array.from(optionMap.entries())
      .map(([value, label]) => ({ value, label }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [portalSurveys]);

  const surveyDeviceOptions = useMemo(() => {
    const optionMap = new Map<string, string>();
    portalSurveys.forEach(survey => {
      const deviceId = String(survey.case?.device?.id || '').trim();
      if (!deviceId) return;
      const label = getPortalSurveyDeviceLabel(survey.case);
      if (!optionMap.has(deviceId)) {
        optionMap.set(deviceId, label);
      }
    });
    return Array.from(optionMap.entries())
      .map(([value, label]) => ({ value, label }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [portalSurveys]);

  const surveyWorkTypeOptions = useMemo(() => {
    const optionMap = new Map<string, string>();
    portalSurveys.forEach(survey => {
      const workTypeCode = String(survey.case?.workTypeCode || '').trim();
      const workTypeLabel = getPortalSurveyWorkTypeLabel(survey.case);
      if (!workTypeCode || !workTypeLabel) return;
      if (!optionMap.has(workTypeCode)) {
        optionMap.set(workTypeCode, workTypeLabel);
      }
    });
    return Array.from(optionMap.entries())
      .map(([value, label]) => ({ value, label }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [portalSurveys]);

  const filteredPortalSurveys = useMemo(() => {
    const search = surveySearchQuery.trim().toLowerCase();
    const rows = portalSurveys.filter(survey => {
      const surveyStatus = String(survey.status || '').trim().toUpperCase();
      if (surveyStatusFilter === 'available' && surveyStatus !== 'AVAILABLE') return false;
      if (surveyStatusFilter === 'completed' && surveyStatus !== 'COMPLETED') return false;
      if (surveyCaseFilter && String(survey.caseId || survey.case?.id || '').trim() !== surveyCaseFilter) return false;
      if (surveyDeviceFilter && String(survey.case?.device?.id || '').trim() !== surveyDeviceFilter) return false;
      if (surveyWorkTypeFilter && String(survey.case?.workTypeCode || '').trim() !== surveyWorkTypeFilter) return false;
      if (!search) return true;

      const searchable = [
        survey.surveyName,
        getPortalSurveyCaseLabel(survey.case),
        getPortalSurveyDeviceLabel(survey.case),
        getPortalSurveyWorkTypeLabel(survey.case),
        survey.technicianName || ''
      ]
        .join(' ')
        .toLowerCase();
      return searchable.includes(search);
    });

    return rows.sort((a, b) => {
      const aTime = new Date(a.completedAt || a.releasedAt || 0).getTime();
      const bTime = new Date(b.completedAt || b.releasedAt || 0).getTime();
      return surveySortOrder === 'oldest' ? aTime - bTime : bTime - aTime;
    });
  }, [
    portalSurveys,
    surveySearchQuery,
    surveyStatusFilter,
    surveyCaseFilter,
    surveyDeviceFilter,
    surveyWorkTypeFilter,
    surveySortOrder
  ]);

  const filteredPendingPortalSurveys = useMemo(
    () => filteredPortalSurveys.filter(survey => survey.status === 'AVAILABLE'),
    [filteredPortalSurveys]
  );

  const filteredCompletedPortalSurveys = useMemo(
    () => filteredPortalSurveys.filter(survey => survey.status === 'COMPLETED'),
    [filteredPortalSurveys]
  );

  const answeredPortalSurveyQuestions = useMemo(() => {
    return Object.values(portalSurveyAnswers).filter(value => String(value || '').trim().length > 0).length;
  }, [portalSurveyAnswers]);

  const canSubmitSelectedPortalSurvey = useMemo(() => {
    if (!selectedPortalSurvey || selectedPortalSurvey.status !== 'AVAILABLE') return false;
    if (selectedPortalSurvey.sections.length === 0) return false;
    return selectedPortalSurvey.sections.every(section => {
      const value = String(portalSurveyAnswers[section.questionKey] || '').trim();
      return value.length > 0;
    });
  }, [selectedPortalSurvey, portalSurveyAnswers]);

  const selectedPortalSurveyScoreDisplay = useMemo(
    () => getPortalSurveyScoreDisplay(selectedPortalSurvey),
    [selectedPortalSurvey]
  );

  const handlePortalSurveyAnswerChange = useCallback((questionKey: string, value: string) => {
    const normalizedKey = String(questionKey || '').trim();
    if (!normalizedKey) return;
    setPortalSurveySubmitNotice(null);
    setPortalSurveyAnswers(prev => ({
      ...prev,
      [normalizedKey]: value
    }));
  }, []);

  const handleSubmitPortalSurvey = useCallback(async () => {
    if (!selectedPortalSurvey || selectedPortalSurvey.status !== 'AVAILABLE') {
      return;
    }

    const answers = selectedPortalSurvey.sections.map(section => ({
      questionKey: section.questionKey,
      rawScore: Number(portalSurveyAnswers[section.questionKey])
    }));

    try {
      setPortalSurveySubmitting(true);
      setPortalSurveySubmitNotice(null);
      const response = await caseApi.submitSurvey(selectedPortalSurvey.id, {
        commentsText: portalSurveyComments.trim() || null,
        answers
      });
      const completedAt =
        typeof response?.survey?.completedAt === 'string' ? response.survey.completedAt : new Date().toISOString();
      const scoreEarned =
        Number.isFinite(Number(response?.survey?.scoreEarned)) ? Number(response.survey.scoreEarned) : null;
      const scorePossible =
        Number.isFinite(Number(response?.survey?.scorePossible)) ? Number(response.survey.scorePossible) : null;
      const completedSurveySummary: PortalSurveySummary = {
        ...selectedPortalSurvey,
        status: 'COMPLETED',
        completedAt,
        scoreEarned,
        scorePossible,
        questionCount: selectedPortalSurvey.questionCount || selectedPortalSurvey.sections.length
      };

      setSelectedPortalSurvey(prev => {
        if (!prev) return prev;
        return {
          ...prev,
          status: 'COMPLETED',
          completedAt,
          commentsText: portalSurveyComments.trim(),
          scoreEarned,
          scorePossible,
          sections: prev.sections.map(section => ({
            ...section,
            rawScore: Number(portalSurveyAnswers[section.questionKey])
          }))
        };
      });
      setPortalSurveys(prev => {
        const index = prev.findIndex(survey => survey.id === selectedPortalSurvey.id);
        if (index === -1) return [completedSurveySummary, ...prev];
        const next = [...prev];
        next[index] = {
          ...next[index],
          status: 'COMPLETED',
          completedAt,
          scoreEarned,
          scorePossible
        };
        return next;
      });
      portalSurveysLoadedRef.current = true;
      portalSurveysLastLoadedAtRef.current = Date.now();
      applyPortalSurveyCompletionToCases(completedSurveySummary);
      setPortalSurveySubmitNotice({
        type: 'success',
        message: 'Survey submitted. Thank you for your feedback.'
      });
      await Promise.all([
        loadPortalSurveys({ force: true }),
        selectedPortalSurvey.caseId
          ? refreshPortalCaseRecord(selectedPortalSurvey.caseId).catch(error => {
              console.error('Failed to refresh portal case after survey submit:', error);
              return null;
            })
          : Promise.resolve(null)
      ]);
    } catch (error: any) {
      console.error('Failed to submit portal survey:', error);
      setPortalSurveySubmitNotice({
        type: 'error',
        message: error?.message || 'Unable to submit your survey right now.'
      });
    } finally {
      setPortalSurveySubmitting(false);
    }
  }, [
    selectedPortalSurvey,
    portalSurveyAnswers,
    portalSurveyComments,
    applyPortalSurveyCompletionToCases,
    loadPortalSurveys,
    refreshPortalCaseRecord
  ]);

  const getDeviceCases = useCallback(
    (deviceId: string) =>
      sortedPortalCases.filter(entry => entry.deviceId === deviceId || entry.device?.id === deviceId),
    [sortedPortalCases]
  );

  const getCaseDisplay = (entry: Case) => entry.caseNumber || entry.caseCode || entry.id;

  const isQuoteTimelineEvent = useCallback((eventType?: string | null) => {
    const normalized = String(eventType || '').trim().toUpperCase();
    return normalized === 'QUOTE_SHARED';
  }, []);

  const isQuoteDecisionTimelineEvent = useCallback((eventType?: string | null) => {
    const normalized = String(eventType || '').trim().toUpperCase();
    return (
      normalized === 'QUOTE_ACCEPTED' ||
      normalized === 'QUOTE_DECLINED' ||
      normalized === 'QUOTE_ADJUST_REQUESTED'
    );
  }, []);

  const isQuoteVisibilityTimelineEvent = useCallback((eventType?: string | null) => {
    const normalized = String(eventType || '').trim().toUpperCase();
    return normalized === 'QUOTE_INACTIVATED' || normalized === 'QUOTE_REACTIVATED';
  }, []);

  const resolveQuoteEventMeta = useCallback((event: any) => {
    const metadata = (event?.metadata && typeof event.metadata === 'object') ? event.metadata as Record<string, any> : {};
    const quoteMeta = (metadata.quote && typeof metadata.quote === 'object') ? metadata.quote as Record<string, any> : {};
    const totalsMeta = (quoteMeta.totals && typeof quoteMeta.totals === 'object') ? quoteMeta.totals as Record<string, any> : {};
    const analysisId =
      typeof metadata.analysisId === 'string' && metadata.analysisId.trim()
        ? metadata.analysisId.trim()
        : '';
    const quoteId =
      typeof quoteMeta.quoteId === 'string' && quoteMeta.quoteId.trim()
        ? quoteMeta.quoteId.trim()
        : typeof metadata.quoteId === 'string' && metadata.quoteId.trim()
        ? metadata.quoteId.trim()
        : '';
    const title =
      typeof quoteMeta.title === 'string' && quoteMeta.title.trim()
        ? quoteMeta.title.trim()
        : typeof metadata.title === 'string' && metadata.title.trim()
        ? metadata.title.trim()
        : 'Service quote';
    const grandTotal =
      typeof totalsMeta.grandTotal === 'number' && Number.isFinite(totalsMeta.grandTotal)
        ? totalsMeta.grandTotal
        : null;
    const quoteSourceType =
      typeof metadata.quoteSourceType === 'string' && metadata.quoteSourceType.trim()
        ? metadata.quoteSourceType.trim()
        : typeof quoteMeta.quoteSourceType === 'string' && quoteMeta.quoteSourceType.trim()
          ? quoteMeta.quoteSourceType.trim()
          : analysisId
            ? 'TSFAD'
            : '';
    const caseWorkflowSnapshotId =
      typeof metadata.caseWorkflowSnapshotId === 'string' && metadata.caseWorkflowSnapshotId.trim()
        ? metadata.caseWorkflowSnapshotId.trim()
        : typeof quoteMeta.caseWorkflowSnapshotId === 'string' && quoteMeta.caseWorkflowSnapshotId.trim()
          ? quoteMeta.caseWorkflowSnapshotId.trim()
          : '';
    return {
      metadata,
      quoteMeta,
      analysisId,
      quoteId,
      title,
      grandTotal,
      quoteSourceType,
      caseWorkflowSnapshotId
    };
  }, [isLitePortal]);

  const toQuoteEventKey = useCallback((value: any): string => {
    return typeof value === 'string' && value.trim() ? value.trim().toLowerCase() : '';
  }, []);

  const getQuoteTimelineKeys = useCallback((event: any): string[] => {
    const metadata = (event?.metadata && typeof event.metadata === 'object') ? event.metadata as Record<string, any> : {};
    const quoteMeta = (metadata.quote && typeof metadata.quote === 'object') ? metadata.quote as Record<string, any> : {};
    const rawQuoteId =
      typeof quoteMeta.quoteId === 'string' && quoteMeta.quoteId.trim()
        ? quoteMeta.quoteId.trim()
        : typeof metadata.quoteId === 'string' && metadata.quoteId.trim()
        ? metadata.quoteId.trim()
        : '';
    const rawAnalysisId =
      typeof metadata.analysisId === 'string' && metadata.analysisId.trim()
        ? metadata.analysisId.trim()
        : '';
    const rawTitle =
      typeof quoteMeta.title === 'string' && quoteMeta.title.trim()
        ? quoteMeta.title.trim()
        : typeof metadata.title === 'string' && metadata.title.trim()
        ? metadata.title.trim()
        : '';

    const keys = new Set<string>();
    const quoteKey = toQuoteEventKey(rawQuoteId);
    if (quoteKey) {
      keys.add(`quote:${quoteKey}`);
      return Array.from(keys);
    }

    const analysisKey = toQuoteEventKey(rawAnalysisId);
    const titleKey = toQuoteEventKey(rawTitle);
    if (analysisKey && titleKey) {
      keys.add(`analysis-title:${analysisKey}:${titleKey}`);
    } else if (titleKey) {
      keys.add(`title:${titleKey}`);
    }

    return Array.from(keys);
  }, [toQuoteEventKey]);

  const buildQuoteDecisionLookup = useCallback((events: any[]): Map<string, any> => {
    const lookup = new Map<string, any>();
    (Array.isArray(events) ? events : [])
      .slice()
      .sort((a, b) => new Date(a?.occurredAt || 0).getTime() - new Date(b?.occurredAt || 0).getTime())
      .forEach(event => {
        const eventType = String(event?.eventType || '').trim().toUpperCase();
        if (!isQuoteVisibilityTimelineEvent(eventType) && !isQuoteDecisionTimelineEvent(eventType)) {
          return;
        }

        const keys = getQuoteTimelineKeys(event);
        if (keys.length === 0) return;

        const state =
          eventType === 'QUOTE_INACTIVATED'
            ? {
                decision: 'INACTIVE',
                occurredAt: event?.occurredAt || null,
                actorName: event?.actorName || 'Support team'
              }
            : eventType === 'QUOTE_REACTIVATED'
            ? {
                decision: 'PENDING',
                occurredAt: event?.occurredAt || null,
                actorName: event?.actorName || 'Support team'
              }
            : eventType === 'QUOTE_ACCEPTED'
            ? {
                decision: 'ACCEPT',
                occurredAt: event?.occurredAt || null,
                actorName: event?.actorName || 'Customer'
              }
            : eventType === 'QUOTE_DECLINED'
            ? {
                decision: 'DENY',
                occurredAt: event?.occurredAt || null,
                actorName: event?.actorName || 'Customer'
              }
            : eventType === 'QUOTE_ADJUST_REQUESTED'
            ? {
                decision: 'ADJUST',
                occurredAt: event?.occurredAt || null,
                actorName: event?.actorName || 'Customer'
              }
            : null;

        if (!state) return;
        keys.forEach(key => {
          lookup.set(key, state);
        });
      });
    return lookup;
  }, [getQuoteTimelineKeys, isQuoteDecisionTimelineEvent, isQuoteVisibilityTimelineEvent]);

  const getQuoteDecisionState = useCallback((lookup: Map<string, any>, event: any) => {
    const keys = getQuoteTimelineKeys(event);
    for (const key of keys) {
      const match = lookup.get(key);
      if (match) return match;
    }
    return null;
  }, [getQuoteTimelineKeys]);

  const resolveQuoteDecisionStatus = useCallback((entry: Case, quoteEvent: any): 'PENDING' | 'ACCEPT' | 'DENY' | 'ADJUST' | 'INACTIVE' => {
    const quoteDecision = getQuoteDecisionState(buildQuoteDecisionLookup(entry.events || []), quoteEvent);
    return quoteDecision?.decision || 'PENDING';
  }, [buildQuoteDecisionLookup, getQuoteDecisionState]);

  const quoteDecisionBadge = (
    decision: 'PENDING' | 'ACCEPT' | 'DENY' | 'ADJUST' | 'INACTIVE'
  ): { label: string; style: React.CSSProperties } => {
    switch (decision) {
      case 'INACTIVE':
        return {
          label: 'Quote inactive',
          style: { ...styles.quoteDecisionPill, ...styles.quoteDecisionDenied }
        };
      case 'ACCEPT':
        return {
          label: 'Quote approved',
          style: { ...styles.quoteDecisionPill, ...styles.quoteDecisionApproved }
        };
      case 'DENY':
        return {
          label: 'Quote denied',
          style: { ...styles.quoteDecisionPill, ...styles.quoteDecisionDenied }
        };
      case 'ADJUST':
        return {
          label: 'Adjust requested',
          style: { ...styles.quoteDecisionPill, ...styles.quoteDecisionAdjust }
        };
      default:
        return {
          label: 'Quote awaiting response',
          style: { ...styles.quoteDecisionPill, ...styles.quoteDecisionPending }
        };
    }
  };

  const openPortalServiceReport = useCallback((report: CaseServiceReport, entry: Case) => {
    if (!report?.document || typeof report.document !== 'object') {
      return;
    }
    setSelectedPortalServiceReport(report);
    setSelectedPortalServiceReportCase(entry);
  }, []);

  const openPortalQuote = useCallback(async (event: any, entry: Case) => {
    if (isLitePortal) return;
    const quoteEventMeta = resolveQuoteEventMeta(event);
    const analysisId = quoteEventMeta.analysisId;
    const quoteId = quoteEventMeta.quoteId;
    const quoteSourceType = quoteEventMeta.quoteSourceType;
    const isWorkflowQuote = Boolean(quoteEventMeta.caseWorkflowSnapshotId || quoteSourceType === 'SAFETY_UPGRADE_WORKFLOW');

    setPortalQuoteError('');
    setPortalQuoteNotice('');
    setPortalQuoteLoading(true);
    setSelectedPortalQuoteCase(entry);
    try {
      if (isWorkflowQuote) {
        const payload = await caseApi.listSafetyWorkflowQuotes(entry.id);
        const quotes = Array.isArray(payload?.quotes) ? payload.quotes : [];
        const matched = quotes.find((quote: any) => String(quote?.quoteId || '') === quoteId) || null;

        if (!matched) {
          setSelectedPortalQuote({
            title: quoteEventMeta.title || 'Service quote',
            actorName: event?.actorName || 'Support team',
            occurredAt: event?.occurredAt || null,
            totals: quoteEventMeta.quoteMeta?.totals || {},
            lineItems: [],
            status: 'INACTIVE',
            quoteSourceType: 'WORKFLOW',
            notes: 'Quote data is no longer available.'
          });
          return;
        }

        setSelectedPortalQuote({
          ...matched,
          quoteSourceType: 'WORKFLOW',
          caseId: entry.id,
          actorName: matched.actorName || event?.actorName || 'Support team',
          occurredAt: matched.savedAt || event?.occurredAt || null
        });
        return;
      }

      if (!analysisId) {
        setSelectedPortalQuote({
          title: quoteEventMeta.title || 'Service quote',
          actorName: event?.actorName || 'Support team',
          occurredAt: event?.occurredAt || null,
          totals: quoteEventMeta.quoteMeta?.totals || {},
          lineItems: [],
          status: 'INACTIVE',
          quoteSourceType: quoteSourceType || null,
          notes: 'Full quote details are not available for this record.'
        });
        return;
      }

      const payload = await tsfadApi.listQuotes(analysisId);
      const quotes = Array.isArray(payload?.quotes) ? payload.quotes : [];
      const matched = quotes.find((quote: any) => String(quote?.quoteId || '') === quoteId) || null;

      if (!matched) {
        setSelectedPortalQuote({
          title: quoteEventMeta.title || 'Service quote',
          actorName: event?.actorName || 'Support team',
          occurredAt: event?.occurredAt || null,
          totals: quoteEventMeta.quoteMeta?.totals || {},
          lineItems: [],
          status: 'INACTIVE',
          notes: 'Quote data is no longer available.'
        });
        return;
      }

      setSelectedPortalQuote({
        ...matched,
        quoteSourceType: quoteSourceType || 'TSFAD',
        actorName: matched.actorName || event?.actorName || 'Support team',
        occurredAt: matched.savedAt || event?.occurredAt || null
      });
    } catch (error: any) {
      setPortalQuoteError(error?.message || 'Unable to open quote');
      setSelectedPortalQuote(null);
    } finally {
      setPortalQuoteLoading(false);
    }
  }, [isLitePortal, resolveQuoteEventMeta]);

  const handlePortalQuoteDecision = useCallback(async (decision: 'ACCEPT' | 'DENY' | 'ADJUST', decisionNote = '') => {
    if (isLitePortal) return;
    if (!selectedPortalQuote) return;
    const quoteSourceType =
      typeof selectedPortalQuote.quoteSourceType === 'string' ? selectedPortalQuote.quoteSourceType.trim() : '';
    const analysisId =
      typeof selectedPortalQuote.analysisId === 'string' && selectedPortalQuote.analysisId.trim()
        ? selectedPortalQuote.analysisId.trim()
        : '';
    const caseId =
      typeof selectedPortalQuote.caseId === 'string' && selectedPortalQuote.caseId.trim()
        ? selectedPortalQuote.caseId.trim()
        : typeof selectedPortalQuoteCase?.id === 'string' && selectedPortalQuoteCase.id.trim()
          ? selectedPortalQuoteCase.id.trim()
          : '';
    const quoteId =
      typeof selectedPortalQuote.quoteId === 'string' && selectedPortalQuote.quoteId.trim()
        ? selectedPortalQuote.quoteId.trim()
        : '';
    const isWorkflowQuote = quoteSourceType === 'WORKFLOW' || quoteSourceType === 'SAFETY_UPGRADE_WORKFLOW';
    if ((!analysisId && !isWorkflowQuote) || !quoteId || (isWorkflowQuote && !caseId)) {
      setPortalQuoteError('Quote response is unavailable for this record.');
      return;
    }

    const note = decision === 'ADJUST' ? String(decisionNote || '').trim() : '';
    if (decision === 'ADJUST' && !note) {
      setPortalQuoteAdjustError('Please provide the requested adjustments.');
      return;
    }

    setPortalQuoteError('');
    setPortalQuoteNotice('');
    setPortalQuoteAdjustError('');
    setPortalQuoteResponding(true);
    try {
      const responsePayload = {
        decision,
        note,
        customerApprovalName:
          selectedPortalQuote.customerApprovalName || portalCustomer?.companyName || portalCustomer?.company || 'Customer',
        customerApprovalSignature:
          selectedPortalQuote.customerApprovalSignature || (decision === 'ACCEPT' ? 'Approved electronically' : '')
      };
      if (isWorkflowQuote) {
        await caseApi.respondSafetyWorkflowQuote(caseId, quoteId, responsePayload);
      } else {
        await tsfadApi.respondQuote(analysisId, quoteId, responsePayload);
      }
      await loadPortalCases({ full: true, force: true });
      setSelectedPortalQuote((prev: any) => ({
        ...(prev || {}),
        customerDecision: decision,
        decisionAt: new Date().toISOString(),
        decisionBy: portalCustomer?.companyName || portalCustomer?.company || portalCustomer?.email || 'Customer',
        customerApprovalName:
          selectedPortalQuote.customerApprovalName || portalCustomer?.companyName || portalCustomer?.company || 'Customer',
        customerApprovalSignature:
          selectedPortalQuote.customerApprovalSignature || (decision === 'ACCEPT' ? 'Approved electronically' : '')
      }));
      setPortalQuoteAdjustOpen(false);
      setPortalQuoteAdjustNote('');
      setPortalQuoteAdjustError('');
      setPortalQuoteNotice(
        decision === 'ACCEPT'
          ? 'Quote marked as accepted.'
          : decision === 'DENY'
          ? 'Quote marked as denied.'
          : 'Adjustment request sent to support team.'
      );
    } catch (error: any) {
      setPortalQuoteError(error?.message || 'Failed to submit quote response');
    } finally {
      setPortalQuoteResponding(false);
    }
  }, [isLitePortal, selectedPortalQuote, selectedPortalQuoteCase, portalCustomer, loadPortalCases]);

  const openRequestsCount = useMemo(
    () => portalCases.filter(entry => !isClosedStatus(entry.status)).length,
    [portalCases]
  );

  const recentActivityItems = useMemo(() => {
    return sortedPortalCases
      .map(entry => {
        const timeline = buildMeaningfulTimeline((entry.events || []).slice(), 'customer')
          .slice()
          .sort((a, b) => new Date(b?.occurredAt || 0).getTime() - new Date(a?.occurredAt || 0).getTime());

        const pendingQuoteEvent =
          timeline.find(
            event =>
              isQuoteTimelineEvent(event.eventType) &&
              resolveQuoteDecisionStatus(entry, event) === 'PENDING'
          ) || null;
        const rawLatestCustomerVisibleEvent = timeline[0] || null;
        const rawLatestEventType = String(rawLatestCustomerVisibleEvent?.eventType || '').trim().toUpperCase();
        const latestCustomerVisibleEvent =
          rawLatestEventType === 'QUOTE_INACTIVATED' && pendingQuoteEvent
            ? pendingQuoteEvent
            : rawLatestCustomerVisibleEvent;
        const latestEventType = String(latestCustomerVisibleEvent?.eventType || '').trim().toUpperCase();
        const isQuoteEvent = latestCustomerVisibleEvent
          ? isQuoteTimelineEvent(latestCustomerVisibleEvent.eventType)
          : false;
        const quoteDecision = isQuoteEvent
          ? resolveQuoteDecisionStatus(entry, latestCustomerVisibleEvent)
          : latestEventType === 'QUOTE_ACCEPTED'
          ? 'ACCEPT'
          : latestEventType === 'QUOTE_DECLINED'
          ? 'DENY'
          : latestEventType === 'QUOTE_ADJUST_REQUESTED'
          ? 'ADJUST'
          : null;

        const eventTime =
          latestCustomerVisibleEvent?.occurredAt || entry.updatedAt || entry.closedAt || entry.createdAt || null;

        return {
          entry,
          event: latestCustomerVisibleEvent,
          isQuoteEvent,
          quoteDecision,
          sortTime: new Date(eventTime || 0).getTime()
        };
      })
      .sort((a, b) => b.sortTime - a.sortTime);
  }, [sortedPortalCases, isQuoteTimelineEvent, resolveQuoteDecisionStatus]);

  const lastServiceCase = useMemo(() => sortedPortalCases[0] || null, [sortedPortalCases]);

  const sharedServiceReportsCount = useMemo(
    () =>
      portalCases.reduce((count, entry) => {
        const reports = Array.isArray(entry.serviceReports) ? entry.serviceReports : [];
        return (
          count +
          reports.filter(report => String(report?.status || '').trim().toUpperCase() === 'SENT').length
        );
      }, 0),
    [portalCases]
  );

  const overviewOpenCases = useMemo(
    () => sortedPortalCases.filter(entry => !isClosedStatus(entry.status)),
    [sortedPortalCases]
  );

  const overviewScheduleReviewCases = useMemo(
    () =>
      overviewOpenCases
        .map(entry => ({
          entry,
          schedule: getPortalCaseScheduleOverview(entry)
        }))
        .filter(
          ({ schedule }) =>
            Boolean(schedule.task) &&
            (schedule.hasScheduleRequest || (schedule.hasCommittedSchedule && !schedule.isConfirmed))
        ),
    [overviewOpenCases]
  );

  const overviewQuoteReviewCases = useMemo(
    () =>
      overviewOpenCases
        .map(entry => {
          const pendingQuoteEvent = buildMeaningfulTimeline((entry.events || []).slice(), 'customer')
            .slice()
            .sort((a, b) => new Date(b?.occurredAt || 0).getTime() - new Date(a?.occurredAt || 0).getTime())
            .find(
              event =>
                isQuoteTimelineEvent(event.eventType) && resolveQuoteDecisionStatus(entry, event) === 'PENDING'
            );
          return pendingQuoteEvent ? { entry, event: pendingQuoteEvent } : null;
        })
        .filter(Boolean) as Array<{ entry: Case; event: any }>,
    [overviewOpenCases, isQuoteTimelineEvent, resolveQuoteDecisionStatus]
  );

  const devicesWithHistoryCount = useMemo(() => {
    const deviceIds = new Set(
      sortedPortalCases
        .map(entry => entry.deviceId || entry.device?.id || '')
        .filter(Boolean)
    );
    return customerDevices.filter(device => deviceIds.has(device.id)).length;
  }, [customerDevices, sortedPortalCases]);

  const devicesWithOpenRequestsCount = useMemo(() => {
    const openIds = new Set(
      sortedPortalCases
        .filter(entry => !isClosedStatus(entry.status))
        .map(entry => entry.deviceId || entry.device?.id || '')
        .filter(Boolean)
    );
    return customerDevices.filter(device => openIds.has(device.id)).length;
  }, [customerDevices, sortedPortalCases]);

  const deviceLocationsCount = useMemo(() => {
    const locationKeys = new Set(
      customerDevices
        .map(device => device.location?.id || device.locationId || '')
        .filter(Boolean)
    );
    return locationKeys.size;
  }, [customerDevices]);

  const selectedPortalDevice = useMemo(
    () => customerDevices.find(device => device.id === selectedPortalDeviceId) || null,
    [customerDevices, selectedPortalDeviceId]
  );

  const selectedPortalDeviceCases = useMemo(
    () => (selectedPortalDevice ? getDeviceCases(selectedPortalDevice.id) : []),
    [selectedPortalDevice, getDeviceCases]
  );
  const selectedPortalDeviceOpenCases = useMemo(
    () => selectedPortalDeviceCases.filter(entry => !isClosedStatus(entry.status)),
    [selectedPortalDeviceCases]
  );

  const selectedPortalHistoryCase = useMemo(
    () => sortedPortalCases.find(entry => entry.id === selectedPortalHistoryCaseId) || null,
    [sortedPortalCases, selectedPortalHistoryCaseId]
  );
  const selectedPortalScheduleOverview = useMemo(
    () => getPortalCaseScheduleOverview(selectedPortalHistoryCase),
    [selectedPortalHistoryCase]
  );
  const selectedPortalHistoryTask = selectedPortalScheduleOverview.task;
  const selectedPortalScheduledStartAt = selectedPortalScheduleOverview.plannedStartAt;
  const selectedPortalScheduledEndAt = selectedPortalScheduleOverview.plannedEndAt;
  const selectedPortalRequestedStartAt = selectedPortalScheduleOverview.requestedStartAt;
  const selectedPortalRequestedEndAt = selectedPortalScheduleOverview.requestedEndAt;
  const selectedPortalHasCommittedSchedule = selectedPortalScheduleOverview.hasCommittedSchedule;
  const selectedPortalHasScheduleRequest = selectedPortalScheduleOverview.hasScheduleRequest;
  const selectedPortalScheduleConfirmed = selectedPortalScheduleOverview.isConfirmed;
  const selectedPortalScheduleDisplayStartAt = selectedPortalScheduleOverview.displayStartAt;
  const selectedPortalScheduleDisplayEndAt = selectedPortalScheduleOverview.displayEndAt;
  const selectedPortalScheduleDurationLabel = selectedPortalScheduleOverview.durationLabel;
  const selectedPortalPlannerStatusLabel = selectedPortalScheduleOverview.plannerStatusLabel;
  const canPortalConfirmSchedule =
    Boolean(selectedPortalHistoryCase?.id) &&
    Boolean(selectedPortalHistoryTask?.id) &&
    selectedPortalHasCommittedSchedule &&
    !selectedPortalHasScheduleRequest &&
    !selectedPortalScheduleConfirmed &&
    !isClosedStatus(selectedPortalHistoryCase?.status);
  const canPortalRequestSchedule =
    Boolean(selectedPortalHistoryCase?.id) &&
    Boolean(selectedPortalHistoryTask?.id) &&
    !isClosedStatus(selectedPortalHistoryCase?.status);

  const selectedPortalDeviceOpenRequestCount = useMemo(
    () => selectedPortalDeviceCases.filter(entry => !isClosedStatus(entry.status)).length,
    [selectedPortalDeviceCases]
  );

  const selectedPortalDeviceLastCase = useMemo(
    () => selectedPortalDeviceCases[0] || null,
    [selectedPortalDeviceCases]
  );

  const selectedPortalDeviceLifecycleLabel = useMemo(
    () => (selectedPortalDevice ? formatDeviceLifecycle(selectedPortalDevice.lifecycleStatus) : ''),
    [selectedPortalDevice]
  );

  const selectedPortalDeviceLifecycleBadgeStyle = useMemo(
    () => (selectedPortalDevice ? getDeviceLifecycleBadgeStyles(selectedPortalDevice.lifecycleStatus) : null),
    [selectedPortalDevice]
  );
  const selectedPortalDeviceSlaAssignment = useMemo(
    () => getCurrentDeviceSlaAssignment(selectedPortalDevice?.slaAssignments),
    [selectedPortalDevice?.slaAssignments]
  );
  const selectedPortalDeviceSlaHasSavedReport = useMemo(
    () => hasSavedDeviceSlaReport(selectedPortalDeviceSlaAssignment),
    [selectedPortalDeviceSlaAssignment]
  );
  const selectedPortalDeviceSlaStartLabel = selectedPortalDeviceSlaAssignment?.startDate
    ? formatPortalDate(selectedPortalDeviceSlaAssignment.startDate)
    : 'Not set';
  const selectedPortalDeviceSlaEndLabel = (() => {
    const resolvedEndDate = resolveDeviceSlaEndDate(selectedPortalDeviceSlaAssignment);
    return resolvedEndDate ? formatPortalDate(resolvedEndDate.toISOString()) : 'Open ended';
  })();
  const selectedPortalDeviceWarrantyStatus = useMemo(
    () => getDeviceWarrantyStatus(selectedPortalDevice),
    [selectedPortalDevice?.warrantyEndAt, selectedPortalDevice?.warrantyStartAt]
  );

  const openPortalDeviceModal = useCallback(
    (deviceId: string, section: 'details' | 'history' | 'qr' = 'details') => {
      void loadPortalCustomer();
      setSelectedPortalDeviceId(deviceId);
      setPortalDeviceModalSection(section);
      setPortalDeviceSlaReport(null);
      setPortalDeviceSlaReportLoading(false);
      setPortalDeviceSlaReportError('');
    },
    [loadPortalCustomer]
  );

  const closePortalDeviceModal = useCallback(() => {
    setSelectedPortalDeviceId(null);
    setPortalDeviceModalSection('details');
    setPortalDeviceSlaReport(null);
    setPortalDeviceSlaReportLoading(false);
    setPortalDeviceSlaReportError('');
  }, []);

  const closePortalDeviceSlaReport = useCallback(() => {
    setPortalDeviceSlaReport(null);
    setPortalDeviceSlaReportLoading(false);
    setPortalDeviceSlaReportError('');
  }, [customerPortalAccess?.customers]);

  const selectedPortalDeviceQrToken = selectedPortalDevice?.id
    ? deviceQrTokens[selectedPortalDevice.id] || selectedPortalDevice.qrToken || ''
    : '';
  const selectedPortalDeviceQrUrl = selectedPortalDeviceQrToken
    ? getDeviceQrUrl(selectedPortalDeviceQrToken)
    : '';

  useEffect(() => {
    if (portalDeviceModalSection !== 'qr' || !selectedPortalDevice?.id || selectedPortalDeviceQrToken) return;
    void ensureDeviceQrToken(selectedPortalDevice.id);
  }, [portalDeviceModalSection, selectedPortalDevice?.id, selectedPortalDeviceQrToken, ensureDeviceQrToken]);

  const openPortalDeviceSlaReport = useCallback(async () => {
    const deviceId = String(selectedPortalDevice?.id || '').trim();
    const assignmentId = String(selectedPortalDeviceSlaAssignment?.id || '').trim();
    if (!deviceId || !assignmentId || !selectedPortalDeviceSlaHasSavedReport) return;
    try {
      setPortalDeviceSlaReportLoading(true);
      setPortalDeviceSlaReportError('');
      const payload = await deviceApi.getSlaReport(deviceId, { assignmentId });
      setPortalDeviceSlaReport(normalizeCustomerFacingSlaReport(payload));
    } catch (error) {
      setPortalDeviceSlaReportError(
        error instanceof Error ? error.message : 'Failed to load the saved SLA agreement.'
      );
    } finally {
      setPortalDeviceSlaReportLoading(false);
    }
  }, [selectedPortalDevice?.id, selectedPortalDeviceSlaAssignment?.id, selectedPortalDeviceSlaHasSavedReport]);

  const openPortalHistoryCaseModal = useCallback((caseId: string) => {
    setSelectedPortalHistoryCaseId(caseId);
  }, []);

  const closePortalHistoryCaseModal = useCallback(() => {
    setSelectedPortalHistoryCaseId(null);
  }, []);

  const openOverviewCaseModal = useCallback(
    (caseId: string) => {
      setActiveOverviewQueue(null);
      openPortalHistoryCaseModal(caseId);
    },
    [openPortalHistoryCaseModal]
  );

  const openOverviewSurveyModal = useCallback(
    (surveyId: string) => {
      setActiveOverviewQueue(null);
      void loadPortalSurveyDetail(surveyId);
    },
    [loadPortalSurveyDetail]
  );

  const openHistoryWorkspace = useCallback(() => {
    setSelectedPortalHistoryCaseId(null);
    setSelectedPortalDeviceId(null);
    setPortalDeviceModalSection('details');
    setActivePortalTab('history');
    setHistorySearchQuery('');
    setHistoryStatusFilter('all');
    setHistoryDeviceFilter('all');
    setPendingHistoryCaseId(null);
  }, []);

  const openCaseInServiceHistory = useCallback((caseId: string) => {
    setSelectedPortalHistoryCaseId(null);
    setSelectedPortalDeviceId(null);
    setPortalDeviceModalSection('details');
    setActivePortalTab('history');
    setHistorySearchQuery('');
    setHistoryStatusFilter('all');
    setHistoryDeviceFilter('all');
    setPendingHistoryCaseId(caseId);
  }, []);

  const openSurveyFromCaseHistory = useCallback((surveyId: string) => {
    closePortalHistoryCaseModal();
    void loadPortalSurveyDetail(surveyId);
  }, [closePortalHistoryCaseModal, loadPortalSurveyDetail]);

  const openPortalScheduleRequestModal = useCallback(() => {
    if (!canPortalRequestSchedule) return;
    setPortalScheduleNotice(null);
    setPortalScheduleRequestDraft(
      buildPortalScheduleRequestDraft(
        selectedPortalRequestedStartAt || selectedPortalScheduledStartAt,
        selectedPortalRequestedEndAt || selectedPortalScheduledEndAt
      )
    );
    setPortalScheduleRequestModalOpen(true);
  }, [
    canPortalRequestSchedule,
    selectedPortalRequestedEndAt,
    selectedPortalRequestedStartAt,
    selectedPortalScheduledEndAt,
    selectedPortalScheduledStartAt
  ]);

  const closePortalScheduleRequestModal = useCallback(() => {
    if (portalScheduleActionBusy === 'request') return;
    setPortalScheduleRequestModalOpen(false);
    setPortalScheduleRequestDraft(null);
  }, [portalScheduleActionBusy]);

  const updatePortalScheduleRequestStartField = useCallback(
    (field: 'startDate' | 'startTime', value: string) => {
      setPortalScheduleRequestDraft(current => {
        if (!current) return current;
        const currentDurationMinutes = getPortalScheduleRequestDurationMinutes(current);
        return alignPortalScheduleRequestEndToStart({
          ...current,
          [field]: value,
          validationMessage: null
        }, currentDurationMinutes);
      });
    },
    []
  );

  const updatePortalScheduleRequestEndField = useCallback(
    (field: 'endDate' | 'endTime', value: string) => {
      setPortalScheduleRequestDraft(current =>
        current
          ? {
              ...current,
              [field]: value,
              validationMessage: null
            }
          : current
      );
    },
    []
  );

  const handlePortalConfirmSchedule = useCallback(async () => {
    if (!selectedPortalHistoryCase?.id || !selectedPortalHistoryTask?.id || !canPortalConfirmSchedule) return;
    setPortalScheduleActionBusy('confirm');
    setPortalScheduleNotice(null);
    try {
      const payload = await caseApi.confirmTaskSchedule(selectedPortalHistoryTask.id);
      const refreshedCase =
        (payload?.case as Case | null) ||
        mergeCaseTaskUpdate(selectedPortalHistoryCase, (payload?.task as CaseTask | null) || null) ||
        (await refreshPortalCaseRecord(selectedPortalHistoryCase.id));
      if (refreshedCase) applyPortalCaseUpdate(refreshedCase);
      setPortalScheduleNotice({
        type: 'success',
        message: 'Schedule confirmed.'
      });
    } catch (error: any) {
      setPortalScheduleNotice({
        type: 'error',
        message: error?.message || 'Failed to confirm the scheduled visit.'
      });
    } finally {
      setPortalScheduleActionBusy(null);
    }
  }, [
    applyPortalCaseUpdate,
    canPortalConfirmSchedule,
    refreshPortalCaseRecord,
    selectedPortalHistoryCase,
    selectedPortalHistoryTask?.id
  ]);

  const handlePortalRequestSchedule = useCallback(async () => {
    if (!selectedPortalHistoryCase?.id || !selectedPortalHistoryTask?.id || !portalScheduleRequestDraft) return;
    const requestedStart = combinePortalScheduleDateTimeInput(
      portalScheduleRequestDraft.startDate,
      portalScheduleRequestDraft.startTime
    );
    const requestedDue = combinePortalScheduleDateTimeInput(
      portalScheduleRequestDraft.endDate,
      portalScheduleRequestDraft.endTime
    );
    if (!requestedStart) {
      setPortalScheduleRequestDraft(current =>
        current ? { ...current, validationMessage: 'Requested start date and time are required.' } : current
      );
      return;
    }
    if (!requestedDue) {
      setPortalScheduleRequestDraft(current =>
        current ? { ...current, validationMessage: 'Requested finish date and time are required.' } : current
      );
      return;
    }
    if (requestedDue.getTime() <= requestedStart.getTime()) {
      setPortalScheduleRequestDraft(current =>
        current ? { ...current, validationMessage: 'Requested finish must be after requested start.' } : current
      );
      return;
    }

    setPortalScheduleActionBusy('request');
    setPortalScheduleNotice(null);
    try {
      const payload = await caseApi.requestTaskSchedule(selectedPortalHistoryTask.id, {
        requestedStart: requestedStart.toISOString(),
        requestedDue: requestedDue.toISOString()
      });
      const refreshedCase =
        (payload?.case as Case | null) ||
        mergeCaseTaskUpdate(selectedPortalHistoryCase, (payload?.task as CaseTask | null) || null) ||
        (await refreshPortalCaseRecord(selectedPortalHistoryCase.id));
      if (refreshedCase) applyPortalCaseUpdate(refreshedCase);
      setPortalScheduleRequestModalOpen(false);
      setPortalScheduleRequestDraft(null);
      setPortalScheduleNotice({
        type: 'success',
        message: selectedPortalHasCommittedSchedule ? 'Schedule change requested.' : 'Suggested schedule sent to planning.'
      });
    } catch (error: any) {
      const message = error?.message || 'Failed to request a schedule update.';
      setPortalScheduleNotice({
        type: 'error',
        message
      });
      setPortalScheduleRequestDraft(current =>
        current ? { ...current, validationMessage: message } : current
      );
    } finally {
      setPortalScheduleActionBusy(null);
    }
  }, [
    applyPortalCaseUpdate,
    portalScheduleRequestDraft,
    refreshPortalCaseRecord,
    selectedPortalHasCommittedSchedule,
    selectedPortalHistoryCase,
    selectedPortalHistoryTask?.id
  ]);

  const deviceById = useMemo(() => {
    return customerDevices.reduce<Record<string, Device>>((acc, device) => {
      acc[device.id] = device;
      return acc;
    }, {});
  }, [customerDevices]);

  const deviceBySerial = useMemo(() => {
    return customerDevices.reduce<Record<string, Device>>((acc, device) => {
      if (device.serialNumber) {
        acc[device.serialNumber] = device;
      }
      return acc;
    }, {});
  }, [customerDevices]);

  const deviceByAsset = useMemo(() => {
    return customerDevices.reduce<Record<string, Device>>((acc, device) => {
      if (device.assetNumber) {
        acc[device.assetNumber] = device;
      }
      return acc;
    }, {});
  }, [customerDevices]);

  const deviceByModel = useMemo(() => {
    return customerDevices.reduce<Record<string, Device>>((acc, device) => {
      const modelNumber = device.modelNumber || device.model?.modelNumber;
      if (modelNumber) {
        acc[modelNumber] = device;
      }
      return acc;
    }, {});
  }, [customerDevices]);

  const deviceByLabel = useMemo(() => {
    return customerDevices.reduce<Record<string, Device>>((acc, device) => {
      const label = getDeviceLabel(device).toLowerCase();
      if (label) {
        acc[label] = device;
      }
      return acc;
    }, {});
  }, [customerDevices]);

  const resolveCaseDevice = useCallback(
    (entry: Case) => {
      const deviceId = entry.deviceId || entry.device?.id;
      if (deviceId && deviceById[deviceId]) return deviceById[deviceId];
      const serial = entry.device?.serialNumber;
      if (serial && deviceBySerial[serial]) return deviceBySerial[serial];
      const asset = (entry.device as any)?.assetNumber;
      if (asset && deviceByAsset[asset]) return deviceByAsset[asset];
      const modelNumber = entry.device?.modelNumber || entry.device?.model?.modelNumber;
      if (modelNumber && deviceByModel[modelNumber]) return deviceByModel[modelNumber];
      const label = entry.device ? getDeviceLabel(entry.device).toLowerCase() : '';
      if (label && deviceByLabel[label]) return deviceByLabel[label];
      return entry.device;
    },
    [deviceById, deviceBySerial, deviceByAsset, deviceByModel, deviceByLabel]
  );

  const getPortalCaseDeviceLabel = useCallback(
    (entry: Case) => {
      const resolvedDevice = resolveCaseDevice(entry);
      return (
        resolvedDevice?.modelNumber ||
        resolvedDevice?.model?.modelNumber ||
        resolvedDevice?.serialNumber ||
        'Unknown device'
      );
    },
    [resolveCaseDevice]
  );

  const getPortalCaseDeviceFilterValue = useCallback(
    (entry: Case) => {
      const resolvedDevice = resolveCaseDevice(entry);
      if (resolvedDevice?.id) return `id:${resolvedDevice.id}`;
      if (resolvedDevice?.serialNumber) return `serial:${resolvedDevice.serialNumber.toLowerCase()}`;
      if ((resolvedDevice as any)?.assetNumber) return `asset:${String((resolvedDevice as any).assetNumber).toLowerCase()}`;
      const modelNumber = resolvedDevice?.modelNumber || resolvedDevice?.model?.modelNumber;
      if (modelNumber) return `model:${modelNumber.toLowerCase()}`;
      return 'unknown';
    },
    [resolveCaseDevice]
  );

  const historyDeviceOptions = useMemo(() => {
    const optionMap = new Map<string, string>();
    sortedPortalCases.forEach(entry => {
      const value = getPortalCaseDeviceFilterValue(entry);
      const label = getPortalCaseDeviceLabel(entry);
      if (!optionMap.has(value)) {
        optionMap.set(value, label);
      }
    });
    return Array.from(optionMap.entries())
      .map(([value, label]) => ({ value, label }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [sortedPortalCases, getPortalCaseDeviceFilterValue, getPortalCaseDeviceLabel]);

  useEffect(() => {
    if (selectedPortalDeviceId && !selectedPortalDevice) {
      closePortalDeviceModal();
    }
  }, [selectedPortalDeviceId, selectedPortalDevice, closePortalDeviceModal]);

  useEffect(() => {
    if (selectedPortalHistoryCaseId && !selectedPortalHistoryCase) {
      closePortalHistoryCaseModal();
    }
  }, [selectedPortalHistoryCaseId, selectedPortalHistoryCase, closePortalHistoryCaseModal]);

  useEffect(() => {
    setPortalScheduleActionBusy(null);
    setPortalScheduleNotice(null);
    setPortalScheduleRequestModalOpen(false);
    setPortalScheduleRequestDraft(null);
  }, [selectedPortalHistoryCaseId]);

  useEffect(() => {
    if (activePortalTab !== 'devices' && selectedPortalDeviceId) {
      closePortalDeviceModal();
    }
  }, [activePortalTab, selectedPortalDeviceId, closePortalDeviceModal]);

  useEffect(() => {
    if (!['overview', 'history', 'devices'].includes(activePortalTab) && selectedPortalHistoryCaseId) {
      closePortalHistoryCaseModal();
    }
  }, [activePortalTab, selectedPortalHistoryCaseId, closePortalHistoryCaseModal]);

  useEffect(() => {
    if (historyDeviceFilter === 'all') return;
    const stillValid = historyDeviceOptions.some(option => option.value === historyDeviceFilter);
    if (!stillValid) {
      setHistoryDeviceFilter('all');
    }
  }, [historyDeviceFilter, historyDeviceOptions]);

  const filteredPortalCases = useMemo(() => {
    const search = historySearchQuery.trim().toLowerCase();
    return sortedPortalCases.filter(entry => {
      if (historyStatusFilter === 'open' && isClosedStatus(entry.status)) return false;
      if (historyStatusFilter === 'closed' && !isClosedStatus(entry.status)) return false;

      if (historyDeviceFilter !== 'all') {
        const deviceValue = getPortalCaseDeviceFilterValue(entry);
        if (deviceValue !== historyDeviceFilter) return false;
      }

      if (!search) return true;
      const caseLabel = String(entry.caseNumber || entry.caseCode || entry.id || '').toLowerCase();
      const deviceLabel = getPortalCaseDeviceLabel(entry).toLowerCase();
      const problem = (entry.problemDescription || '').toLowerCase();
      const status = (entry.status || '').toLowerCase();
      const searchable = `${caseLabel} ${deviceLabel} ${problem} ${status}`;
      return searchable.includes(search);
    });
  }, [
    sortedPortalCases,
    historySearchQuery,
    historyStatusFilter,
    historyDeviceFilter,
    getPortalCaseDeviceFilterValue,
    getPortalCaseDeviceLabel
  ]);

  const getPortalCaseModalData = useCallback(
    (entry: Case) => {
      const caseLabel = getCaseDisplay(entry);
      const resolvedDevice = resolveCaseDevice(entry);
      const deviceLabel = getPortalCaseDeviceLabel(entry);
      const slaAssignment = getCurrentDeviceSlaAssignment(
        resolvedDevice?.slaAssignments || entry.device?.slaAssignments
      );
      const statusStyle = getStatusBadgeStyles(entry.status);
      const rawEventList = buildMeaningfulTimeline((entry.events || []).slice(), 'customer').sort((a, b) => {
        const aTime = new Date(a.occurredAt ?? 0).getTime();
        const bTime = new Date(b.occurredAt ?? 0).getTime();
        return bTime - aTime;
      });
      const quoteEvents = rawEventList.filter(event => isQuoteTimelineEvent(event.eventType));
      const quoteDecisionByKey = buildQuoteDecisionLookup(entry.events || []);

      const visibleQuoteEvents = quoteEvents.filter(event => {
        const state = getQuoteDecisionState(quoteDecisionByKey, event);
        return state?.decision !== 'INACTIVE';
      });
      const visibleQuoteKeys = new Set<string>();
      visibleQuoteEvents.forEach(event => {
        getQuoteTimelineKeys(event).forEach(key => visibleQuoteKeys.add(key));
      });
      const eventList = rawEventList.filter(event => {
        const eventType = String(event?.eventType || '').trim().toUpperCase();
        if (eventType !== 'QUOTE_INACTIVATED') return true;
        if (visibleQuoteKeys.size === 0) return true;
        return getQuoteTimelineKeys(event).some(key => visibleQuoteKeys.has(key));
      });

      const visibleServiceReports = (Array.isArray(entry.serviceReports) ? entry.serviceReports : []).filter(
        report => String(report?.status || '').trim().toUpperCase() === 'SENT'
      );
      const visibleCaseSurveys = (Array.isArray(entry.caseSurveys) ? entry.caseSurveys : [])
        .slice()
        .sort((a, b) => {
          const aTime = new Date(a?.completedAt || a?.releasedAt || 0).getTime();
          const bTime = new Date(b?.completedAt || b?.releasedAt || 0).getTime();
          return bTime - aTime;
        });

      return {
        caseLabel,
        resolvedDevice,
        deviceLabel,
        slaAssignment,
        statusStyle,
        eventList,
        quoteDecisionByKey,
        visibleQuoteEvents,
        visibleServiceReports,
        visibleCaseSurveys
      };
    },
    [
      buildQuoteDecisionLookup,
      getQuoteDecisionState,
      getQuoteTimelineKeys,
      resolveCaseDevice,
      getPortalCaseDeviceLabel,
      isQuoteTimelineEvent
    ]
  );

  const selectedPortalHistoryCaseData = useMemo(
    () => (selectedPortalHistoryCase ? getPortalCaseModalData(selectedPortalHistoryCase) : null),
    [selectedPortalHistoryCase, getPortalCaseModalData]
  );
  const selectedPortalHistoryCaseSlaAssignment = selectedPortalHistoryCaseData?.slaAssignment || null;
  const selectedPortalHistoryCaseSlaStartLabel = selectedPortalHistoryCaseSlaAssignment?.startDate
    ? formatPortalDate(selectedPortalHistoryCaseSlaAssignment.startDate)
    : 'Not set';
  const selectedPortalHistoryCaseSlaEndLabel = (() => {
    const resolvedEndDate = resolveDeviceSlaEndDate(selectedPortalHistoryCaseSlaAssignment);
    return resolvedEndDate ? formatPortalDate(resolvedEndDate.toISOString()) : 'Open ended';
  })();
  const portalScheduleRequestStartPreview = useMemo(
    () =>
      combinePortalScheduleDateTimeInput(
        portalScheduleRequestDraft?.startDate || '',
        portalScheduleRequestDraft?.startTime || ''
      ),
    [portalScheduleRequestDraft?.startDate, portalScheduleRequestDraft?.startTime]
  );
  const portalScheduleRequestEndPreview = useMemo(
    () =>
      combinePortalScheduleDateTimeInput(
        portalScheduleRequestDraft?.endDate || '',
        portalScheduleRequestDraft?.endTime || ''
      ),
    [portalScheduleRequestDraft?.endDate, portalScheduleRequestDraft?.endTime]
  );

  useEffect(() => {
    if (activePortalTab !== 'history' || !pendingHistoryCaseId) return;
    const target = historyCaseRefs.current[pendingHistoryCaseId];
    if (target) {
      requestAnimationFrame(() => {
        target.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    }
    setSelectedPortalHistoryCaseId(pendingHistoryCaseId);
    setPendingHistoryCaseId(null);
  }, [activePortalTab, pendingHistoryCaseId, filteredPortalCases]);

  const formatAttachmentSize = (size: number) => {
    if (!size) return '0 KB';
    const kb = size / 1024;
    if (kb < 1024) return `${kb.toFixed(1)} KB`;
    return `${(kb / 1024).toFixed(1)} MB`;
  };

  const getMessagePreview = (value: string) => {
    const trimmed = value.replace(/\s+/g, ' ').trim();
    if (!trimmed) return 'No message content.';
    if (trimmed.length <= 160) return trimmed;
    return `${trimmed.slice(0, 160)}...`;
  };

  const getMessageFocusLabel = useCallback(
    (message: PortalMessage) => {
      if (message.focusType === 'device' && message.deviceId) {
        const device = deviceById[message.deviceId];
        return device ? `Device - ${getDeviceLabel(device)}` : 'Device';
      }
      if (message.focusType === 'case' && message.caseId) {
        const caseItem = sortedPortalCases.find(entry => entry.id === message.caseId);
        return caseItem ? `Case - ${getCaseDisplay(caseItem)}` : 'Case';
      }
      return 'General';
    },
    [deviceById, sortedPortalCases]
  );

  const handleMessageAttachmentChange = (files: FileList | null) => {
    if (!files || files.length === 0) return;
    const attachments = Array.from(files).map((file, index) => ({
      id: `att-${Date.now()}-${index}`,
      name: file.name,
      type: file.type || 'file',
      size: file.size,
      file
    }));
    setMessageAttachments(prev => [...prev, ...attachments]);
  };

  const handleMessageAttachmentDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    handleMessageAttachmentChange(event.dataTransfer.files);
  };

  const handleRemoveMessageAttachment = (attachmentId: string) => {
    setMessageAttachments(prev => prev.filter(item => item.id !== attachmentId));
  };

  const handleMessageReplyAttachmentChange = (files: FileList | null) => {
    if (!files || files.length === 0) return;
    const attachments = Array.from(files).map((file, index) => ({
      id: `reply-att-${Date.now()}-${index}`,
      name: file.name,
      type: file.type || 'file',
      size: file.size,
      file
    }));
    setMessageReplyAttachments(prev => [...prev, ...attachments]);
  };

  const handleRemoveMessageReplyAttachment = (attachmentId: string) => {
    setMessageReplyAttachments(prev => prev.filter(item => item.id !== attachmentId));
  };

  const resolveMessageCustomerId = useCallback(
    ({
      focusType,
      caseId,
      deviceId
    }: {
      focusType: 'general' | 'device' | 'case';
      caseId?: string | null;
      deviceId?: string | null;
    }) => {
      if (focusType === 'case' && caseId) {
        const caseMatch = sortedPortalCases.find(entry => entry.id === caseId);
        if (caseMatch?.customerId) return caseMatch.customerId;
      }
      if (focusType === 'device' && deviceId) {
        const deviceMatch = deviceById[deviceId];
        if (deviceMatch?.customerId) return deviceMatch.customerId;
      }
      if (portalCustomer?.id) return portalCustomer.id;
      const fallbackCaseCustomerId = sortedPortalCases.find(entry => !!entry.customerId)?.customerId;
      if (fallbackCaseCustomerId) return fallbackCaseCustomerId;
      const fallbackDeviceCustomerId = Object.values(deviceById).find(device => !!device.customerId)?.customerId;
      return fallbackDeviceCustomerId || '';
    },
    [deviceById, portalCustomer?.id, sortedPortalCases]
  );

  const handleSendMessage = async () => {
    if (!messageBody.trim()) return;
    if (messageFocusType === 'device' && !messageDeviceId) return;
    if (messageFocusType === 'case' && !messageCaseId) return;
    const resolvedCustomerId = resolveMessageCustomerId({
      focusType: messageFocusType,
      caseId: messageFocusType === 'case' ? messageCaseId : null,
      deviceId: messageFocusType === 'device' ? messageDeviceId : null
    });
    if (!resolvedCustomerId) {
      alert('Unable to identify the customer for this message. Please refresh and try again.');
      return;
    }
    setMessageSending(true);
    try {
      const created = (await messageApi.create({
        body: messageBody.trim(),
        focusType: messageFocusType,
        customerId: resolvedCustomerId,
        deviceId: messageFocusType === 'device' ? messageDeviceId : undefined,
        caseId: messageFocusType === 'case' ? messageCaseId : undefined,
        attachments: messageAttachments.length > 0 ? messageAttachments : undefined
      })) as PortalMessage;
      setMessageBody('');
      setMessageAttachments([]);
      setSelectedPortalMessageId(created?.id || null);
      setShowComposePanel(false);
      await loadPortalMessages();
    } catch (error) {
      console.error('Failed to send portal message:', error);
      alert('Failed to send message. Please try again.');
    } finally {
      setMessageSending(false);
    }
  };

  const selectedPortalMessage = useMemo(() => {
    if (!selectedPortalMessageId) return null;
    return portalMessages.find(message => message.id === selectedPortalMessageId) || null;
  }, [portalMessages, selectedPortalMessageId]);
  const selectedPortalNotification = useMemo(() => {
    if (!selectedPortalNotificationId) return null;
    return portalNotifications.find(notification => notification.id === selectedPortalNotificationId) || null;
  }, [portalNotifications, selectedPortalNotificationId]);

  const filteredPortalMessages = useMemo(() => {
    const filtered = portalMessages.filter(message => {
      if (showUnreadOnly && (message.senderRole === 'customer' || readMessageIds.includes(message.id))) return false;
      if (messageShowSent ? message.senderRole !== 'customer' : message.senderRole === 'customer') return false;
      if (messageFocusFilter !== 'all' && message.focusType !== messageFocusFilter) return false;
      if (messageCaseFilter && message.caseId !== messageCaseFilter) return false;
      if (messageDeviceFilter && message.deviceId !== messageDeviceFilter) return false;
      return true;
    });
    return filtered.sort((a, b) => {
      const aTime = new Date(a.createdAt || 0).getTime();
      const bTime = new Date(b.createdAt || 0).getTime();
      return messageSortOrder === 'newest' ? bTime - aTime : aTime - bTime;
    });
  }, [
    portalMessages,
    showUnreadOnly,
    readMessageIds,
    messageShowSent,
    messageFocusFilter,
    messageCaseFilter,
    messageDeviceFilter,
    messageSortOrder
  ]);

  useEffect(() => {
    setMessageVisibleCount(10);
  }, [
    messageFocusFilter,
    messageCaseFilter,
    messageDeviceFilter,
    messageSortOrder,
    showUnreadOnly,
    messageView,
    messageShowSent
  ]);

  const unreadPortalCount = useMemo(
    () => {
      const unreadMessages = portalMessages.filter(
        message => message.senderRole !== 'customer' && !readMessageIds.includes(message.id)
      ).length;
      const unreadNotifications = portalNotifications.filter(
        notification => !notification.readAt && !notification.dismissedAt
      ).length;
      return unreadMessages + unreadNotifications;
    },
    [portalMessages, portalNotifications, readMessageIds]
  );

  const handleSendMessageReply = async () => {
    if (!selectedPortalMessage) return;
    if (!messageReplyBody.trim()) return;
    const resolvedCustomerId =
      selectedPortalMessage.customerId ||
      resolveMessageCustomerId({
        focusType: selectedPortalMessage.focusType,
        caseId: selectedPortalMessage.caseId,
        deviceId: selectedPortalMessage.deviceId
      });
    if (!resolvedCustomerId) {
      alert('Unable to identify the customer for this reply. Please refresh and try again.');
      return;
    }
    try {
      await messageApi.create({
        body: messageReplyBody.trim(),
        focusType: selectedPortalMessage.focusType,
        customerId: resolvedCustomerId,
        deviceId: selectedPortalMessage.deviceId || undefined,
        caseId: selectedPortalMessage.caseId || undefined,
        attachments: messageReplyAttachments.length > 0 ? messageReplyAttachments : undefined
      });
      setMessageReplyBody('');
      setMessageReplyAttachments([]);
      await loadPortalMessages();
    } catch (error) {
      console.error('Failed to send portal message reply:', error);
      alert('Failed to send reply. Please try again.');
    }
  };

  const handleArchivePortalMessage = async (archived: boolean) => {
    if (!selectedPortalMessage) return;
    try {
      await messageApi.archive(selectedPortalMessage.id, archived);
      if (archived && messageView === 'active') {
        setSelectedPortalMessageId(null);
      }
      await loadPortalMessages();
    } catch (error) {
      console.error('Failed to update portal message archive state:', error);
      alert('Failed to update archive state.');
    }
  };

  const handleDeletePortalMessage = async () => {
    if (!selectedPortalMessage) return;
    try {
      await messageApi.deleteForMe(selectedPortalMessage.id);
      setSelectedPortalMessageId(null);
      await loadPortalMessages();
    } catch (error) {
      console.error('Failed to delete portal message:', error);
      alert('Failed to delete message.');
    }
  };

  const handleRestorePortalMessage = async () => {
    if (!selectedPortalMessage) return;
    try {
      await messageApi.restoreForMe(selectedPortalMessage.id);
      if (messageView === 'archived') {
        setSelectedPortalMessageId(null);
      }
      await loadPortalMessages();
    } catch (error) {
      console.error('Failed to restore portal message:', error);
      alert('Failed to restore message.');
    }
  };

  useEffect(() => {
    setMessageReplyBody('');
    setMessageReplyAttachments([]);
  }, [selectedPortalMessageId]);

  const styles = {
    portalShell: {
      display: 'flex',
      flexDirection: (isTablet ? 'column' : 'row') as React.CSSProperties['flexDirection'],
      minHeight: '100vh',
      backgroundColor: BRAND_SURFACE,
      fontFamily: 'Inter, system-ui, sans-serif'
    },
    sidebar: {
      width: isTablet ? '100%' : isNarrowDesktop ? '248px' : '272px',
      backgroundColor: BRAND_TEXT,
      color: BRAND_SURFACE,
      display: 'flex',
      flexDirection: 'column' as const,
      padding: 0,
      gap: isTablet ? '0.9rem' : '1.5rem'
    },
    sidebarBrand: {
      padding: isTablet ? '1rem 1rem 0.5rem' : '1.35rem 1.5rem 1.15rem',
      backgroundColor: BRAND_SURFACE,
      borderBottom: isTablet ? 'none' : `1px solid ${BRAND_BORDER}`,
      display: 'grid',
      gap: '0.45rem',
      justifyItems: 'center' as const
    },
    sidebarLogo: {
      display: 'block',
      width: '100%',
      maxWidth: isTablet ? '148px' : '184px',
      height: 'auto'
    },
    sidebarSubtitle: {
      fontSize: isCompact ? '1rem' : '1.12rem',
      fontWeight: 600,
      lineHeight: 1.35,
      color: BRAND_MUTED,
      marginTop: 0,
      textAlign: 'center' as const
    },
    sidebarNav: {
      display: isTablet ? 'flex' : 'grid',
      gap: '0.6rem',
      padding: isTablet ? '0 1rem 1rem' : '0 1.1rem',
      overflowX: isTablet ? ('auto' as const) : ('visible' as const),
      overflowY: 'visible' as const
    },
    navButton: {
      border: '1px solid transparent',
      backgroundColor: 'transparent',
      color: BRAND_SURFACE,
      textAlign: 'left' as const,
      padding: isCompact ? '0.72rem 0.9rem' : '0.78rem 0.95rem',
      borderRadius: '0.72rem',
      fontWeight: 600,
      fontSize: isCompact ? '0.94rem' : '0.98rem',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: '0.65rem',
      minHeight: '48px',
      whiteSpace: 'nowrap' as const,
      flex: isTablet ? '0 0 auto' : 'initial'
    },
    navButtonActive: {
      backgroundColor: BRAND_SIDEBAR_ACTIVE,
      borderColor: 'rgba(242, 238, 231, 0.18)',
      color: '#ffffff'
    },
    navBadge: {
      backgroundColor: BRAND_ACCENT,
      color: '#ffffff',
      borderRadius: '999px',
      padding: '0.1rem 0.55rem',
      fontSize: '0.75rem',
      fontWeight: 700
    },
    sidebarFooter: {
      marginTop: 'auto',
      display: 'grid',
      gap: '0.5rem',
      padding: isTablet ? '0 1rem 1rem' : '0 1.1rem 1.1rem'
    },
    portalContent: {
      flex: 1,
      padding: pagePadding,
      minWidth: 0,
      width: '100%',
      maxWidth: isTablet ? '100%' : isNarrowDesktop ? '980px' : '1080px',
      margin: '0 auto'
    },
    portalHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'flex-start',
      gap: '1.5rem',
      flexWrap: 'wrap' as const,
      flexDirection: (isCompact ? 'column' : 'row') as React.CSSProperties['flexDirection'],
      marginBottom: isCompact ? '1.25rem' : '1.75rem'
    },
    portalTitle: {
      fontSize: isCompact ? '1.6rem' : isTablet ? '1.8rem' : '2rem',
      fontWeight: 700,
      marginBottom: '0.4rem',
      color: BRAND_TEXT
    },
    portalSubtitle: {
      color: BRAND_MUTED,
      fontSize: isCompact ? '0.95rem' : '1rem',
      lineHeight: 1.5
    },
    portalActions: {
      display: 'flex',
      gap: '0.75rem',
      flexWrap: 'wrap' as const,
      flexDirection: (isCompact ? 'column' : 'row') as React.CSSProperties['flexDirection'],
      width: isCompact ? '100%' : 'auto'
    },
    portalPrimaryButton: {
      backgroundColor: BRAND_TEXT,
      color: '#ffffff',
      border: '1px solid transparent',
      borderRadius: '0.55rem',
      padding: '0.72rem 1.1rem',
      fontWeight: 600,
      cursor: 'pointer',
      width: isCompact ? '100%' : 'auto'
    },
    portalSecondaryButton: {
      backgroundColor: BRAND_SURFACE_SOFT,
      color: BRAND_TEXT,
      border: `1px solid ${BRAND_BORDER}`,
      borderRadius: '0.55rem',
      padding: '0.72rem 1.1rem',
      fontWeight: 600,
      cursor: 'pointer',
      width: isCompact ? '100%' : 'auto'
    },
    overviewCardGrid: {
      display: 'grid',
      gridTemplateColumns: isCompact
        ? '1fr'
        : isTablet
        ? 'repeat(2, minmax(0, 1fr))'
        : 'repeat(auto-fit, minmax(220px, 1fr))',
      gap: '1rem',
      marginBottom: '2rem'
    },
    overviewCard: {
      backgroundColor: BRAND_SURFACE_SOFT,
      borderRadius: '0.75rem',
      padding: '1.25rem',
      boxShadow: '0 1px 3px rgba(15, 23, 42, 0.08)',
      border: `1px solid ${BRAND_BORDER}`,
      display: 'grid',
      gap: '0.35rem'
    },
    overviewCardLabel: {
      fontSize: '0.85rem',
      color: BRAND_MUTED,
      fontWeight: 600
    },
    overviewCardValue: {
      fontSize: '1.5rem',
      fontWeight: 700,
      color: BRAND_TEXT
    },
    overviewCardMeta: {
      fontSize: '0.9rem',
      color: BRAND_MUTED
    },
    overviewCardButton: {
      border: 'none',
      textAlign: 'left' as const,
      cursor: 'pointer',
      width: '100%',
      fontFamily: 'inherit'
    },
    overviewColumns: {
      display: 'grid',
      gridTemplateColumns: isNarrowDesktop ? '1fr' : 'minmax(0, 2fr) minmax(0, 1fr)',
      gap: '1.5rem',
      alignItems: 'start'
    },
    overviewColumn: {
      backgroundColor: BRAND_SURFACE_SOFT,
      borderRadius: '0.75rem',
      padding: '1.25rem',
      boxShadow: '0 1px 3px rgba(15, 23, 42, 0.08)',
      border: `1px solid ${BRAND_BORDER}`,
      display: 'grid',
      gap: '1rem'
    },
    overviewSectionTitle: {
      fontSize: '1.05rem',
      fontWeight: 700,
      color: BRAND_TEXT
    },
    overviewSectionText: {
      fontSize: '0.92rem',
      color: BRAND_MUTED,
      lineHeight: 1.5
    },
    overviewActivityList: {
      display: 'grid',
      gap: '0.75rem',
      alignContent: 'start'
    },
    overviewActivityItem: {
      border: `1px solid ${BRAND_BORDER}`,
      borderRadius: '0.65rem',
      padding: '0.75rem',
      display: 'grid',
      gap: '0.4rem',
      backgroundColor: '#ffffff'
    },
    overviewActivityButton: {
      border: 'none',
      padding: 0,
      background: 'transparent',
      textAlign: 'left' as const,
      cursor: 'pointer',
      width: '100%',
      fontFamily: 'inherit'
    },
    overviewActivityHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      gap: '0.5rem'
    },
    overviewActivityTitle: {
      fontWeight: 600,
      color: BRAND_TEXT
    },
    overviewActivityStatus: {
      fontSize: '0.76rem',
      fontWeight: 700,
      letterSpacing: '0.02em',
      textTransform: 'uppercase' as const,
      color: '#1d4ed8',
      backgroundColor: '#eff6ff',
      border: '1px solid #bfdbfe',
      borderRadius: '999px',
      padding: '0.2rem 0.55rem',
      whiteSpace: 'nowrap' as const
    },
    overviewActivityMeta: {
      fontSize: '0.85rem',
      color: BRAND_MUTED
    },
    overviewDeviceList: {
      display: 'grid',
      gap: '0.75rem'
    },
    overviewDeviceItem: {
      border: `1px solid ${BRAND_BORDER}`,
      borderRadius: '0.6rem',
      padding: '0.75rem',
      display: 'grid',
      gap: '0.25rem',
      backgroundColor: '#ffffff',
      textAlign: 'left' as const,
      cursor: 'pointer',
      width: '100%',
      fontFamily: 'inherit',
      appearance: 'none' as const
    },
    overviewDeviceMeta: {
      fontSize: '0.85rem',
      color: BRAND_MUTED
    },
    overviewQuickActions: {
      display: 'grid',
      gap: '0.5rem'
    },
    overviewQuickButton: {
      border: `1px solid ${BRAND_BORDER}`,
      backgroundColor: BRAND_SURFACE,
      color: BRAND_TEXT,
      borderRadius: '0.55rem',
      padding: '0.6rem 0.9rem',
      fontWeight: 600,
      cursor: 'pointer',
      textAlign: 'left' as const
    },
    overviewEmpty: {
      padding: '1rem',
      borderRadius: '0.6rem',
      border: `1px dashed ${BRAND_BORDER}`,
      backgroundColor: BRAND_SURFACE,
      color: BRAND_MUTED
    },
    portalPanel: {
      backgroundColor: BRAND_SURFACE_SOFT,
      borderRadius: '0.75rem',
      padding: '1.5rem',
      boxShadow: '0 1px 3px rgba(15, 23, 42, 0.08)',
      border: `1px solid ${BRAND_BORDER}`,
      display: 'grid',
      gap: '0.75rem'
    },
    portalPanelTitle: {
      fontSize: '1.1rem',
      fontWeight: 700,
      color: BRAND_TEXT
    },
    portalPanelText: {
      color: BRAND_MUTED,
      lineHeight: 1.5
    },
    surveySection: {
      backgroundColor: BRAND_SURFACE_SOFT,
      borderRadius: '0.75rem',
      padding: '1.25rem',
      boxShadow: '0 1px 3px rgba(15, 23, 42, 0.08)',
      border: `1px solid ${BRAND_BORDER}`,
      display: 'grid',
      gap: '0.9rem'
    },
    surveySectionHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      gap: '1rem'
    },
    surveyList: {
      display: 'grid',
      gap: '0.85rem'
    },
    surveyCardButton: {
      border: 'none',
      padding: 0,
      background: 'transparent',
      textAlign: 'left' as const,
      cursor: 'pointer',
      width: '100%',
      fontFamily: 'inherit'
    },
    surveyCard: {
      border: `1px solid ${BRAND_BORDER}`,
      borderRadius: '0.75rem',
      padding: '1rem',
      display: 'grid',
      gap: '0.8rem',
      backgroundColor: '#ffffff'
    },
    surveyCardHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'flex-start',
      gap: '1rem',
      flexWrap: 'wrap' as const
    },
    surveyCardTitle: {
      fontSize: '1rem',
      fontWeight: 700,
      color: BRAND_TEXT
    },
    surveyCardMeta: {
      fontSize: '0.88rem',
      color: BRAND_MUTED,
      marginTop: '0.25rem'
    },
    surveyStatusBadge: {
      fontSize: '0.76rem',
      fontWeight: 700,
      letterSpacing: '0.02em',
      textTransform: 'uppercase' as const,
      borderRadius: '999px',
      padding: '0.28rem 0.7rem',
      border: '1px solid transparent',
      whiteSpace: 'nowrap' as const
    },
    surveyStatusAvailable: {
      color: '#8A611E',
      backgroundColor: '#F6E7C9',
      borderColor: '#EDC98A'
    },
    surveyStatusCompleted: {
      color: '#25624D',
      backgroundColor: '#E7F2EC',
      borderColor: '#BDD4C7'
    },
    surveyCardFacts: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
      gap: '0.75rem'
    },
    surveyFact: {
      display: 'grid',
      gap: '0.2rem'
    },
    surveyFactLabel: {
      fontSize: '0.76rem',
      fontWeight: 700,
      textTransform: 'uppercase' as const,
      letterSpacing: '0.02em',
      color: BRAND_MUTED
    },
    surveyFactValue: {
      fontSize: '0.92rem',
      color: BRAND_TEXT
    },
    surveyCardScore: {
      fontSize: '0.9rem',
      fontWeight: 600,
      color: BRAND_TEXT
    },
    surveyScoreRow: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: '0.55rem',
      flexWrap: 'wrap' as const
    },
    surveyScoreText: {
      fontSize: '0.9rem',
      fontWeight: 700,
      color: BRAND_TEXT
    },
    surveyPercentBadge: {
      display: 'inline-flex',
      alignItems: 'center',
      borderRadius: '999px',
      border: '1px solid transparent',
      padding: '0.15rem 0.55rem',
      fontSize: '0.78rem',
      fontWeight: 800,
      lineHeight: 1.4
    },
    surveyCardAction: {
      fontSize: '0.9rem',
      fontWeight: 600,
      color: '#1d4ed8'
    },
    surveyDetailBody: {
      display: 'grid',
      gap: '1rem'
    },
    surveyNotice: {
      borderRadius: '0.7rem',
      padding: '0.85rem 1rem',
      fontWeight: 600
    },
    surveyNoticeSuccess: {
      backgroundColor: '#E7F2EC',
      border: '1px solid #BDD4C7',
      color: '#25624D'
    },
    surveyNoticeError: {
      backgroundColor: '#FEF2F2',
      border: '1px solid #FECACA',
      color: '#B91C1C'
    },
    surveyProgressRow: {
      display: 'grid',
      gap: '0.25rem'
    },
    surveyQuestionList: {
      display: 'grid',
      gap: '0.9rem'
    },
    surveyQuestionCard: {
      border: `1px solid ${BRAND_BORDER}`,
      borderRadius: '0.75rem',
      padding: '1rem',
      backgroundColor: '#ffffff',
      display: 'grid',
      gap: '0.5rem'
    },
    surveyQuestionTitle: {
      fontSize: '1rem',
      fontWeight: 700,
      color: BRAND_TEXT
    },
    surveyQuestionDescription: {
      fontSize: '0.92rem',
      color: BRAND_MUTED,
      lineHeight: 1.5
    },
    surveyQuestionScale: {
      fontSize: '0.82rem',
      fontWeight: 600,
      color: BRAND_MUTED
    },
    surveyStarsRow: {
      display: 'flex',
      gap: '0.55rem',
      flexWrap: 'wrap' as const
    },
    surveyStarButton: {
      minWidth: '42px',
      padding: '0.55rem 0.7rem',
      borderRadius: '0.6rem',
      border: `1px solid ${BRAND_BORDER}`,
      backgroundColor: '#ffffff',
      color: BRAND_TEXT,
      fontWeight: 700,
      cursor: 'pointer'
    },
    surveyStarButtonActive: {
      backgroundColor: BRAND_TEXT,
      color: '#ffffff',
      borderColor: BRAND_TEXT
    },
    surveyReadOnlyValue: {
      fontSize: '0.95rem',
      fontWeight: 700,
      color: BRAND_TEXT
    },
    surveyCommentsBlock: {
      display: 'grid',
      gap: '0.5rem'
    },
    surveyCommentsReadOnly: {
      borderRadius: '0.7rem',
      border: `1px solid ${BRAND_BORDER}`,
      backgroundColor: '#ffffff',
      padding: '0.9rem',
      minHeight: '110px',
      color: BRAND_TEXT,
      whiteSpace: 'pre-wrap' as const
    },
    surveyFooterActions: {
      display: 'grid',
      gap: '0.85rem',
      alignItems: 'start'
    },
    surveyFooterMeta: {
      fontSize: '0.88rem',
      color: BRAND_MUTED,
      lineHeight: 1.5
    },
    surveyPrimaryButtonDisabled: {
      opacity: 0.55,
      cursor: 'not-allowed'
    },
    messagesLayout: {
      display: 'grid',
      gap: '1.5rem'
    },
    messagesComposer: {
      backgroundColor: BRAND_SURFACE_SOFT,
      borderRadius: '0.75rem',
      padding: '1.5rem',
      boxShadow: '0 1px 3px rgba(15, 23, 42, 0.08)',
      border: `1px solid ${BRAND_BORDER}`,
      display: 'grid',
      gap: '1rem'
    },
    messagesPanel: {
      backgroundColor: BRAND_SURFACE_SOFT,
      borderRadius: '0.75rem',
      padding: '1.5rem',
      boxShadow: '0 1px 3px rgba(15, 23, 42, 0.08)',
      border: `1px solid ${BRAND_BORDER}`,
      display: 'grid',
      gap: '0.75rem',
      alignContent: 'start'
    },
    messagesTitle: {
      fontSize: '1.1rem',
      fontWeight: 700,
      color: BRAND_TEXT
    },
    messagesSection: {
      backgroundColor: BRAND_SURFACE_SOFT,
      borderRadius: '0.75rem',
      padding: '1.25rem',
      boxShadow: '0 1px 3px rgba(15, 23, 42, 0.08)',
      border: `1px solid ${BRAND_BORDER}`,
      display: 'grid',
      gap: '0.75rem'
    },
    messagesSectionHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      gap: '1rem'
    },
    messagesNewButton: {
      border: `1px solid ${BRAND_BORDER}`,
      backgroundColor: BRAND_SURFACE,
      color: BRAND_TEXT,
      borderRadius: '0.6rem',
      padding: '0.45rem 0.9rem',
      fontWeight: 600,
      cursor: 'pointer',
      whiteSpace: 'nowrap' as const
    },
    messagesSubtitle: {
      color: BRAND_MUTED,
      fontSize: '0.9rem'
    },
    messagesField: {
      display: 'grid',
      gap: '0.5rem'
    },
    messagesLabel: {
      fontSize: '0.85rem',
      fontWeight: 600,
      color: BRAND_TEXT
    },
    messagesFocusMeta: {
      fontSize: '0.8rem',
      color: BRAND_MUTED
    },
    messagesTextarea: {
      borderRadius: '0.6rem',
      border: `1px solid ${BRAND_BORDER}`,
      padding: '0.75rem',
      fontSize: '0.95rem',
      fontFamily: 'inherit',
      backgroundColor: '#ffffff',
      resize: 'vertical' as const
    },
    messagesAttachmentRow: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.75rem'
    },
    messagesAttachmentDropZone: {
      border: `1px dashed ${BRAND_BORDER}`,
      backgroundColor: BRAND_SURFACE,
      borderRadius: '0.75rem',
      padding: '1rem',
      cursor: 'pointer',
      display: 'grid',
      gap: '0.35rem'
    },
    messagesAttachmentDropTitle: {
      color: BRAND_TEXT,
      fontWeight: 700,
      fontSize: '0.95rem'
    },
    messagesAttachmentDropHint: {
      color: BRAND_MUTED,
      fontSize: '0.85rem',
      lineHeight: 1.4
    },
    messagesAttachmentList: {
      display: 'grid',
      gap: '0.5rem'
    },
    messagesAttachmentItem: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '0.5rem 0.75rem',
      borderRadius: '0.6rem',
      backgroundColor: BRAND_SURFACE,
      border: `1px solid ${BRAND_BORDER}`,
      fontSize: '0.85rem',
      color: BRAND_TEXT
    },
    messagesAttachmentRemove: {
      border: 'none',
      backgroundColor: 'transparent',
      color: BRAND_TEXT,
      fontWeight: 600,
      cursor: 'pointer'
    },
    messagesSendRow: {
      display: 'flex',
      justifyContent: 'flex-end'
    },
    messagesSendButton: {
      border: 'none',
      backgroundColor: BRAND_TEXT,
      color: '#ffffff',
      borderRadius: '0.6rem',
      padding: '0.65rem 1.5rem',
      fontWeight: 600,
      cursor: 'pointer'
    },
    messagesList: {
      display: 'grid',
      gap: '0.75rem'
    },
    messagesListScroll: {
      maxHeight: '60vh',
      overflowY: 'auto' as const,
      paddingRight: '0.25rem'
    },
    messagesLoadMore: {
      marginTop: '0.5rem',
      border: `1px solid ${BRAND_BORDER}`,
      backgroundColor: '#ffffff',
      color: BRAND_TEXT,
      borderRadius: '0.6rem',
      padding: '0.5rem 1rem',
      fontWeight: 600,
      cursor: 'pointer',
      justifySelf: 'start'
    },
    messagesFilterRow: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
      gap: '0.75rem',
      alignItems: 'center',
      padding: '0.85rem',
      borderRadius: '0.7rem',
      border: `1px solid ${BRAND_BORDER}`,
      backgroundColor: '#ffffff'
    },
    messagesFilterToggle: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
      fontSize: '0.9rem',
      color: BRAND_TEXT
    },
    messagesFilterToggleInput: {
      width: '16px',
      height: '16px'
    },
    messagesItem: {
      border: `1px solid ${BRAND_BORDER}`,
      borderRadius: '0.65rem',
      padding: '0.75rem',
      display: 'grid',
      gap: '0.4rem',
      backgroundColor: '#ffffff'
    },
    messagesItemHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'flex-start',
      gap: '0.75rem',
      flexWrap: 'wrap' as const
    },
    messagesItemHeaderLeft: {
      display: 'grid',
      gap: '0.45rem'
    },
    messagesItemBadgeRow: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.45rem',
      flexWrap: 'wrap' as const
    },
    messagesSenderBadge: {
      fontSize: '0.72rem',
      fontWeight: 700,
      letterSpacing: '0.02em',
      textTransform: 'uppercase' as const,
      padding: '0.2rem 0.6rem',
      borderRadius: '999px',
      border: `1px solid ${BRAND_BORDER}`,
      backgroundColor: BRAND_SURFACE,
      color: BRAND_TEXT
    },
    messagesSenderBadgeSupport: {
      color: '#1d4ed8',
      backgroundColor: '#eff6ff',
      border: '1px solid #bfdbfe'
    },
    messagesFocusBadge: {
      fontSize: '0.78rem',
      fontWeight: 600,
      padding: '0.2rem 0.6rem',
      borderRadius: '999px',
      border: `1px solid ${BRAND_BORDER}`,
      backgroundColor: BRAND_SURFACE_SOFT,
      color: BRAND_MUTED
    },
    messagesStateBadge: {
      fontSize: '0.72rem',
      fontWeight: 700,
      letterSpacing: '0.02em',
      textTransform: 'uppercase' as const,
      padding: '0.2rem 0.6rem',
      borderRadius: '999px',
      border: '1px solid #d6d3d1',
      backgroundColor: '#f5f5f4',
      color: '#57534e'
    },
    messagesItemTitle: {
      fontSize: '1rem',
      fontWeight: 700,
      lineHeight: 1.35,
      color: BRAND_TEXT
    },
    messagesUnreadBadge: {
      backgroundColor: '#dbeafe',
      color: '#1d4ed8',
      fontSize: '0.7rem',
      padding: '0.15rem 0.5rem',
      borderRadius: '999px',
      fontWeight: 700,
      border: '1px solid #bfdbfe'
    },
    messagesItemBody: {
      color: BRAND_TEXT,
      lineHeight: 1.5,
      fontSize: '0.95rem',
      display: '-webkit-box',
      WebkitLineClamp: 2,
      WebkitBoxOrient: 'vertical' as const,
      overflow: 'hidden',
      wordBreak: 'break-word' as const,
      overflowWrap: 'anywhere' as const
    },
    messagesItemMeta: {
      fontSize: '0.8rem',
      color: BRAND_MUTED
    },
    messagesItemMetaRow: {
      display: 'flex',
      justifyContent: 'space-between',
      gap: '0.75rem',
      flexWrap: 'wrap' as const,
      alignItems: 'center'
    },
    messagesItemMetaGroup: {
      display: 'flex',
      gap: '0.5rem',
      flexWrap: 'wrap' as const,
      alignItems: 'center'
    },
    messagesItemMetaTag: {
      fontSize: '0.78rem',
      color: BRAND_MUTED,
      borderRadius: '999px',
      border: `1px solid ${BRAND_BORDER}`,
      backgroundColor: '#ffffff',
      padding: '0.18rem 0.55rem'
    },
    messagesListButton: {
      textAlign: 'left' as const,
      cursor: 'pointer',
      width: '100%',
      fontFamily: 'inherit',
      appearance: 'none' as const
    },
    messagesItemActive: {
      borderColor: 'rgba(18, 63, 67, 0.28)',
      backgroundColor: BRAND_SURFACE_SOFT,
      boxShadow: '0 0 0 1px rgba(18, 63, 67, 0.08)'
    },
    messagesDetailCard: {
      border: `1px solid ${BRAND_BORDER}`,
      borderRadius: '0.65rem',
      padding: '0.85rem',
      display: 'grid',
      gap: '0.75rem',
      backgroundColor: '#ffffff'
    },
    messagesDetailHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      gap: '1rem',
      alignItems: 'flex-start',
      flexWrap: 'wrap' as const
    },
    messagesDetailBadgeRow: {
      display: 'flex',
      gap: '0.5rem',
      flexWrap: 'wrap' as const,
      alignItems: 'center'
    },
    messagesDetailTitle: {
      fontSize: '1rem',
      fontWeight: 600,
      color: BRAND_TEXT
    },
    messagesDetailBody: {
      color: BRAND_TEXT,
      lineHeight: 1.65,
      whiteSpace: 'pre-wrap' as const,
      wordBreak: 'break-word' as const,
      overflowWrap: 'anywhere' as const
    },
    messagesReplyPanel: {
      display: 'grid',
      gap: '0.75rem',
      border: `1px solid ${BRAND_BORDER}`,
      borderRadius: '0.75rem',
      backgroundColor: '#ffffff',
      padding: '1rem'
    },
    messagesReplyTitle: {
      fontSize: '0.85rem',
      fontWeight: 600,
      color: BRAND_TEXT
    },
    messagesReplyInput: {
      borderRadius: '0.6rem',
      border: `1px solid ${BRAND_BORDER}`,
      padding: '0.75rem',
      fontSize: '0.95rem',
      fontFamily: 'inherit',
      backgroundColor: '#ffffff'
    },
    messagesReplyActions: {
      display: 'flex',
      justifyContent: 'flex-end',
      gap: '0.75rem',
      flexWrap: 'wrap' as const
    },
    messagesReplyButton: {
      border: 'none',
      backgroundColor: BRAND_TEXT,
      color: '#ffffff',
      borderRadius: '0.6rem',
      padding: '0.5rem 1.25rem',
      fontWeight: 600,
      cursor: 'pointer'
    },
    messagesReplySecondaryButton: {
      border: `1px solid ${BRAND_BORDER}`,
      backgroundColor: BRAND_SURFACE,
      color: BRAND_TEXT,
      borderRadius: '0.6rem',
      padding: '0.5rem 1.25rem',
      fontWeight: 600,
      cursor: 'pointer'
    },
    messagesReplyDangerButton: {
      border: '1px solid #f0c9c2',
      backgroundColor: '#fff7f4',
      color: '#9f3f2d',
      borderRadius: '0.6rem',
      padding: '0.5rem 1.25rem',
      fontWeight: 600,
      cursor: 'pointer'
    },
    messagesEmpty: {
      padding: '1rem',
      borderRadius: '0.6rem',
      border: `1px dashed ${BRAND_BORDER}`,
      backgroundColor: BRAND_SURFACE,
      color: BRAND_MUTED
    },
    deviceGrid: {
      display: 'grid',
      gap: '0.45rem'
    },
    deviceCard: {
      backgroundColor: '#ffffff',
      borderRadius: '0.85rem',
      padding: '0.72rem 1rem',
      boxShadow: '0 1px 3px rgba(15, 23, 42, 0.08)',
      border: `1px solid ${BRAND_BORDER}`,
      display: 'grid',
      gap: '0.55rem'
    },
    deviceCardButton: {
      border: 'none',
      padding: 0,
      background: 'transparent',
      textAlign: 'left' as const,
      cursor: 'pointer',
      width: '100%',
      fontFamily: 'inherit'
    },
    deviceHeaderRow: {
      display: 'grid',
      gridTemplateColumns: 'minmax(0, 1.85fr) minmax(120px, 0.7fr) minmax(190px, 0.75fr)',
      gap: '1.1rem',
      alignItems: 'center'
    },
    deviceSummaryIdentity: {
      display: 'grid',
      gridTemplateColumns: 'auto minmax(0, 1fr)',
      gap: '0.9rem',
      alignItems: 'center',
      minWidth: 0
    },
    deviceSummaryIdentityCopy: {
      display: 'grid',
      gap: '0.35rem',
      minWidth: 0
    },
    deviceHeaderContent: {
      display: 'grid',
      gap: '0.55rem',
      minWidth: 0
    },
    deviceBadgeRow: {
      display: 'flex',
      gap: '0.45rem',
      flexWrap: 'wrap' as const,
      alignItems: 'center'
    },
    deviceLifecycleBadge: {
      fontSize: '0.72rem',
      fontWeight: 700,
      letterSpacing: '0.02em',
      textTransform: 'uppercase' as const,
      padding: '0.2rem 0.6rem',
      borderRadius: '999px',
      border: `1px solid ${BRAND_BORDER}`,
      backgroundColor: BRAND_SURFACE,
      color: BRAND_TEXT
    },
    deviceCountBadge: {
      fontSize: '0.78rem',
      fontWeight: 600,
      padding: '0.2rem 0.6rem',
      borderRadius: '999px',
      border: `1px solid ${BRAND_BORDER}`,
      backgroundColor: '#ffffff',
      color: BRAND_MUTED
    },
    deviceThumb: {
      width: '56px',
      height: '56px',
      borderRadius: '0.85rem',
      backgroundColor: BRAND_SURFACE,
      color: BRAND_TEXT,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontWeight: 700,
      fontSize: '1rem',
      border: `1px solid ${BRAND_BORDER}`
    },
    deviceThumbImage: {
      width: '56px',
      height: '56px',
      borderRadius: '0.85rem',
      objectFit: 'cover' as const,
      border: `1px solid ${BRAND_BORDER}`,
      backgroundColor: '#ffffff'
    },
    deviceTitle: {
      fontSize: '1.05rem',
      fontWeight: 700,
      color: BRAND_TEXT
    },
    deviceSummarySerial: {
      fontSize: '0.88rem',
      color: BRAND_MUTED
    },
    deviceSubtitle: {
      fontSize: '0.85rem',
      color: BRAND_MUTED
    },
    deviceSummaryStatus: {
      display: 'grid',
      gap: '0.3rem',
      alignContent: 'center',
      justifyItems: 'start'
    },
    deviceSummaryStatusMeta: {
      fontSize: '0.85rem',
      color: BRAND_MUTED
    },
    deviceSummarySignals: {
      display: 'grid',
      gridTemplateColumns: 'repeat(2, minmax(88px, max-content))',
      gap: '0.25rem 1rem',
      alignItems: 'center',
      justifyContent: 'end'
    },
    deviceSummarySignal: {
      display: 'grid',
      gap: '0.18rem',
      padding: 0,
      border: 'none',
      backgroundColor: 'transparent',
      alignContent: 'center'
    },
    deviceSummarySignalLabel: {
      fontSize: '0.72rem',
      color: BRAND_MUTED,
      fontWeight: 700,
      letterSpacing: '0.03em',
      textTransform: 'uppercase' as const
    },
    deviceSummarySignalValue: {
      fontSize: '1.02rem',
      color: BRAND_TEXT,
      fontWeight: 700
    },
    deviceSummarySignalMeta: {
      fontSize: '0.78rem',
      color: BRAND_MUTED
    },
    deviceMetaRow: {
      display: 'flex',
      gap: '0.5rem',
      flexWrap: 'wrap' as const,
      alignItems: 'center'
    },
    deviceMetaPill: {
      fontSize: '0.82rem',
      color: BRAND_MUTED,
      borderRadius: '999px',
      border: `1px solid ${BRAND_BORDER}`,
      backgroundColor: '#ffffff',
      padding: '0.2rem 0.55rem'
    },
    deviceActionRow: {
      display: 'flex',
      gap: '0.5rem',
      flexWrap: 'wrap' as const,
      justifyContent: 'flex-end'
    },
    deviceActionButton: {
      border: `1px solid ${BRAND_BORDER}`,
      backgroundColor: '#ffffff',
      color: BRAND_TEXT,
      borderRadius: '0.6rem',
      padding: '0.45rem 0.9rem',
      fontWeight: 600,
      cursor: 'pointer'
    },
    deviceActionButtonActive: {
      backgroundColor: BRAND_TEXT,
      color: '#ffffff',
      borderColor: 'transparent'
    },
    deviceModalPanel: {
      width: isTablet ? 'min(100%, calc(100vw - 1.5rem))' : 'min(1080px, calc(100vw - 2rem))',
      maxHeight: '90vh',
      overflowY: 'auto' as const,
      backgroundColor: '#ffffff',
      borderRadius: '1rem',
      border: `1px solid ${BRAND_BORDER}`,
      boxShadow: '0 28px 64px rgba(15, 23, 42, 0.2)',
      padding: isTablet ? '1.15rem' : '1.5rem',
      display: 'grid',
      gap: '1.15rem'
    },
    deviceModalHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'flex-start',
      gap: '1rem',
      flexWrap: 'wrap' as const
    },
    deviceModalTitle: {
      fontSize: '2rem',
      fontWeight: 700,
      color: BRAND_TEXT
    },
    deviceModalSubtitle: {
      fontSize: '0.95rem',
      color: BRAND_MUTED,
      marginTop: '0.3rem'
    },
    deviceModalHero: {
      display: 'grid',
      gridTemplateColumns: 'minmax(0, 1.6fr) minmax(180px, 0.8fr) minmax(220px, 1fr)',
      gap: '1rem',
      alignItems: 'stretch'
    },
    caseModalHeroWithSla: {
      display: 'grid',
      gridTemplateColumns: isTablet
        ? '1fr'
        : 'minmax(0, 1.45fr) minmax(220px, 0.85fr) minmax(220px, 1fr)',
      gap: '1rem',
      alignItems: 'stretch'
    },
    deviceModalHeroCard: {
      display: 'grid',
      gridTemplateColumns: 'auto minmax(0, 1fr)',
      gap: '1rem',
      alignItems: 'center',
      padding: '1.15rem',
      borderRadius: '0.85rem',
      border: `1px solid ${BRAND_BORDER}`,
      backgroundColor: '#ffffff'
    },
    deviceModalThumb: {
      width: '92px',
      height: '92px',
      borderRadius: '1rem',
      backgroundColor: BRAND_SURFACE,
      color: BRAND_TEXT,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontWeight: 700,
      fontSize: '1.35rem',
      border: `1px solid ${BRAND_BORDER}`
    },
    deviceModalThumbImage: {
      width: '92px',
      height: '92px',
      borderRadius: '1rem',
      objectFit: 'cover' as const,
      border: `1px solid ${BRAND_BORDER}`,
      backgroundColor: '#ffffff'
    },
    deviceModalHeroCopy: {
      display: 'grid',
      gap: '0.45rem',
      minWidth: 0
    },
    deviceModalHeroMeta: {
      display: 'flex',
      gap: '0.5rem',
      flexWrap: 'wrap' as const,
      alignItems: 'center'
    },
    deviceModalStatusCard: {
      display: 'grid',
      gap: '0.6rem',
      alignContent: 'start',
      padding: '1rem 1.05rem',
      borderRadius: '0.85rem',
      border: `1px solid ${BRAND_BORDER}`,
      backgroundColor: '#ffffff'
    },
    deviceModalSideStack: {
      display: 'grid',
      gap: '1rem',
      alignContent: 'start'
    },
    deviceModalSlaCard: {
      display: 'grid',
      gap: '0.65rem',
      alignContent: 'start',
      padding: '1rem 1.05rem',
      borderRadius: '0.85rem',
      border: `1px solid ${BRAND_BORDER}`,
      backgroundColor: '#ffffff'
    },
    deviceModalSlaRange: {
      display: 'grid',
      gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
      gap: '0.65rem'
    },
    deviceModalSlaFact: {
      display: 'grid',
      gap: '0.18rem'
    },
    deviceModalSlaFactLabel: {
      fontSize: '0.72rem',
      color: BRAND_MUTED,
      fontWeight: 700,
      letterSpacing: '0.03em',
      textTransform: 'uppercase' as const
    },
    deviceModalSlaFactValue: {
      fontSize: '0.95rem',
      color: BRAND_TEXT,
      fontWeight: 700
    },
    deviceModalStatusLabel: {
      fontSize: '0.75rem',
      color: BRAND_MUTED,
      fontWeight: 700,
      letterSpacing: '0.03em',
      textTransform: 'uppercase' as const
    },
    deviceModalStatusMeta: {
      fontSize: '0.9rem',
      color: BRAND_MUTED,
      lineHeight: 1.45
    },
    deviceModalMetricStack: {
      display: 'grid',
      gap: '1rem'
    },
    deviceModalMetricCard: {
      display: 'grid',
      gap: '0.3rem',
      alignContent: 'start',
      padding: '1rem 1.1rem',
      borderRadius: '0.85rem',
      border: `1px solid ${BRAND_BORDER}`,
      backgroundColor: '#ffffff'
    },
    deviceModalMetricLabel: {
      fontSize: '0.78rem',
      color: BRAND_MUTED,
      fontWeight: 700,
      letterSpacing: '0.03em',
      textTransform: 'uppercase' as const
    },
    deviceModalMetricValue: {
      fontSize: '1.25rem',
      color: BRAND_TEXT,
      fontWeight: 700
    },
    deviceModalMetricMeta: {
      fontSize: '0.85rem',
      color: BRAND_MUTED
    },
    deviceModalTabRow: {
      display: 'flex',
      gap: '0.6rem',
      flexWrap: 'wrap' as const
    },
    deviceModalTab: {
      border: `1px solid ${BRAND_BORDER}`,
      backgroundColor: '#ffffff',
      color: BRAND_TEXT,
      borderRadius: '999px',
      padding: '0.45rem 0.95rem',
      fontWeight: 600,
      cursor: 'pointer'
    },
    deviceModalTabActive: {
      backgroundColor: BRAND_TEXT,
      color: '#ffffff',
      borderColor: 'transparent'
    },
    deviceSection: {
      display: 'grid',
      gap: '0.85rem',
      padding: '1rem',
      borderRadius: '0.75rem',
      backgroundColor: '#ffffff',
      border: `1px solid ${BRAND_BORDER}`
    },
    deviceSectionHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      gap: '0.75rem',
      flexWrap: 'wrap' as const
    },
    deviceSectionTitle: {
      fontSize: '0.98rem',
      fontWeight: 700,
      color: BRAND_TEXT
    },
    deviceSectionText: {
      fontSize: '0.85rem',
      color: BRAND_MUTED
    },
    deviceInfoGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
      gap: '0.75rem'
    },
    deviceInfoCard: {
      display: 'grid',
      gap: '0.35rem',
      padding: '0.85rem',
      backgroundColor: BRAND_SURFACE,
      borderRadius: '0.65rem',
      border: `1px solid ${BRAND_BORDER}`
    },
    deviceQrPanel: {
      display: 'grid',
      gap: '1rem',
      padding: '1rem',
      borderRadius: '0.75rem',
      border: `1px solid ${BRAND_BORDER}`,
      backgroundColor: '#ffffff'
    },
    deviceQrLayout: {
      display: 'grid',
      gridTemplateColumns: 'auto minmax(0, 1fr)',
      gap: '1rem',
      alignItems: 'start'
    },
    deviceQrCanvas: {
      width: 'fit-content',
      padding: '0.85rem',
      borderRadius: '0.75rem',
      backgroundColor: BRAND_SURFACE,
      border: `1px solid ${BRAND_BORDER}`
    },
    deviceQrMeta: {
      fontSize: '0.9rem',
      color: BRAND_TEXT,
      fontWeight: 600
    },
    deviceQrText: {
      fontSize: '0.88rem',
      color: BRAND_MUTED,
      lineHeight: 1.5
    },
    deviceQrLink: {
      fontSize: '0.85rem',
      color: BRAND_TEXT,
      wordBreak: 'break-all' as const,
      padding: '0.75rem',
      backgroundColor: BRAND_SURFACE,
      borderRadius: '0.65rem',
      border: `1px dashed ${BRAND_BORDER}`
    },
    deviceQrActions: {
      display: 'flex',
      gap: '0.5rem',
      flexWrap: 'wrap' as const
    },
    deviceInfoLabel: {
      fontSize: '0.75rem',
      color: BRAND_MUTED,
      fontWeight: 700,
      letterSpacing: '0.03em',
      textTransform: 'uppercase' as const
    },
    deviceInfoValue: {
      fontSize: '0.95rem',
      color: BRAND_TEXT,
      fontWeight: 600
    },
    deviceHistoryList: {
      display: 'grid',
      gap: '0.6rem'
    },
    deviceHistoryRowButton: {
      border: 'none',
      padding: 0,
      background: 'transparent',
      textAlign: 'left' as const,
      cursor: 'pointer',
      width: '100%',
      fontFamily: 'inherit'
    },
    deviceHistoryItem: {
      padding: '0.85rem 0.95rem',
      borderRadius: '0.65rem',
      border: `1px solid ${BRAND_BORDER}`,
      backgroundColor: BRAND_SURFACE,
      display: 'grid',
      gap: '0.35rem'
    },
    deviceHistoryHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      gap: '0.75rem',
      alignItems: 'center',
      flexWrap: 'wrap' as const
    },
    deviceHistoryTitle: {
      fontWeight: 700,
      color: BRAND_TEXT
    },
    deviceHistoryStatusBadge: {
      fontSize: '0.76rem',
      fontWeight: 700,
      letterSpacing: '0.02em',
      textTransform: 'uppercase' as const,
      borderRadius: '999px',
      padding: '0.18rem 0.55rem',
      border: '1px solid transparent'
    },
    deviceHistoryMeta: {
      fontSize: '0.82rem',
      color: BRAND_MUTED
    },
    deviceEmpty: {
      padding: '1rem',
      borderRadius: '0.6rem',
      border: `1px dashed ${BRAND_BORDER}`,
      backgroundColor: BRAND_SURFACE,
      color: BRAND_MUTED
    },
    historyGrid: {
      display: 'grid',
      gap: '0.35rem'
    },
    historyFilters: {
      display: 'grid',
      gap: '0.75rem',
      gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
      alignItems: 'center',
      backgroundColor: BRAND_SURFACE_SOFT,
      border: `1px solid ${BRAND_BORDER}`,
      borderRadius: '0.85rem',
      padding: '0.9rem',
      boxShadow: '0 1px 3px rgba(15, 23, 42, 0.08)'
    },
    historyFilterInput: {
      width: '100%',
      padding: '0.65rem 0.75rem',
      border: `1px solid ${BRAND_BORDER}`,
      borderRadius: '0.6rem',
      fontSize: '0.95rem',
      color: BRAND_TEXT,
      backgroundColor: '#ffffff'
    },
    historyFilterSelect: {
      width: '100%',
      padding: '0.65rem 0.75rem',
      border: `1px solid ${BRAND_BORDER}`,
      borderRadius: '0.6rem',
      fontSize: '0.95rem',
      backgroundColor: '#ffffff',
      color: BRAND_TEXT
    },
    historyFilterButton: {
      border: `1px solid ${BRAND_BORDER}`,
      backgroundColor: '#ffffff',
      color: BRAND_TEXT,
      borderRadius: '0.6rem',
      padding: '0.65rem 0.9rem',
      fontWeight: 600,
      cursor: 'pointer'
    },
    historyCard: {
      backgroundColor: '#ffffff',
      borderRadius: '0.85rem',
      padding: '0.75rem 0.95rem',
      boxShadow: '0 1px 3px rgba(15, 23, 42, 0.08)',
      border: `1px solid ${BRAND_BORDER}`,
      display: 'grid',
      gap: '0.25rem'
    },
    historyCardButton: {
      border: 'none',
      padding: 0,
      background: 'transparent',
      textAlign: 'left' as const,
      cursor: 'pointer',
      width: '100%',
      fontFamily: 'inherit'
    },
    historyHeaderRow: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      gap: '0.85rem',
      flexWrap: 'wrap' as const
    },
    historyHeaderClickable: {
      cursor: 'pointer'
    },
    historyModalActions: {
      display: 'flex',
      gap: '0.6rem',
      flexWrap: 'wrap' as const,
      alignItems: 'center'
    },
    historyHeaderLeft: {
      display: 'flex',
      gap: '0.85rem',
      alignItems: 'center',
      minWidth: 0
    },
    historySummaryLayout: {
      display: 'flex',
      flexWrap: 'wrap' as const,
      gap: '0.9rem',
      alignItems: 'center'
    },
    historySummaryMain: {
      display: 'grid',
      gap: '0.18rem',
      minWidth: 0,
      flex: '1 1 280px'
    },
    historySummaryDeviceColumn: {
      display: 'grid',
      gap: '0.12rem',
      minWidth: 0,
      flex: '0 1 220px',
      alignContent: 'center'
    },
    historySummaryDeviceName: {
      fontSize: '0.92rem',
      fontWeight: 700,
      color: BRAND_TEXT
    },
    historySummaryDeviceMeta: {
      fontSize: '0.8rem',
      color: BRAND_MUTED,
      lineHeight: 1.2
    },
    historySummaryMetaGrid: {
      display: 'grid',
      gridTemplateColumns: 'minmax(92px, 1fr)',
      gap: '0.2rem',
      alignItems: 'center',
      justifyItems: 'center',
      flex: '0 0 110px'
    },
    historySummaryMetaCard: {
      display: 'grid',
      gap: '0.2rem',
      minWidth: 0,
      alignContent: 'start',
      justifyItems: 'center',
      textAlign: 'center' as const
    },
    historyDeviceThumb: {
      width: '44px',
      height: '44px',
      borderRadius: '0.65rem',
      objectFit: 'cover' as const,
      border: `1px solid ${BRAND_BORDER}`,
      backgroundColor: '#ffffff'
    },
    historyDeviceThumbFallback: {
      width: '44px',
      height: '44px',
      borderRadius: '0.65rem',
      backgroundColor: BRAND_SURFACE,
      color: BRAND_TEXT,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontWeight: 700,
      fontSize: '0.9rem',
      border: `1px solid ${BRAND_BORDER}`
    },
    historyTitle: {
      fontSize: '1rem',
      fontWeight: 700,
      color: BRAND_TEXT
    },
    historyMeta: {
      fontSize: '0.8rem',
      color: BRAND_MUTED
    },
    historySummarySnippet: {
      fontSize: '0.88rem',
      color: BRAND_TEXT,
      lineHeight: 1.25,
      whiteSpace: 'nowrap' as const,
      overflow: 'hidden',
      textOverflow: 'ellipsis'
    },
    historyStatusPill: {
      borderRadius: '999px',
      padding: '0.3rem 0.8rem',
      fontWeight: 600,
      fontSize: '0.8rem',
      border: '1px solid transparent'
    },
    historyActionRow: {
      display: 'flex',
      gap: '0.5rem',
      flexWrap: 'wrap' as const,
      alignItems: 'center',
      justifyContent: 'center'
    },
    historyActionButton: {
      border: `1px solid ${BRAND_BORDER}`,
      backgroundColor: '#ffffff',
      color: BRAND_TEXT,
      borderRadius: '0.6rem',
      padding: '0.4rem 0.9rem',
      fontWeight: 600,
      cursor: 'pointer'
    },
    historyDetailGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
      gap: '0.75rem'
    },
    historyDetailCard: {
      display: 'grid',
      gap: '0.35rem',
      padding: '0.85rem',
      backgroundColor: '#ffffff',
      borderRadius: '0.7rem',
      border: `1px solid ${BRAND_BORDER}`
    },
    historyDetailLabel: {
      fontSize: '0.75rem',
      color: BRAND_MUTED,
      fontWeight: 700,
      letterSpacing: '0.03em',
      textTransform: 'uppercase' as const
    },
    historyDetailValue: {
      fontSize: '0.95rem',
      color: BRAND_TEXT,
      fontWeight: 600
    },
    historySectionTitle: {
      fontSize: '1rem',
      fontWeight: 700,
      color: BRAND_TEXT
    },
    historyTimeline: {
      display: 'grid',
      gap: '0.6rem'
    },
    historyTimelineItem: {
      padding: '0.6rem 0.75rem',
      borderRadius: '0.6rem',
      border: `1px solid ${BRAND_BORDER}`,
      backgroundColor: '#ffffff',
      display: 'grid',
      gap: '0.25rem'
    },
    historySubSection: {
      display: 'grid',
      gap: '0.5rem',
      padding: '0.85rem',
      borderRadius: '0.7rem',
      border: `1px solid ${BRAND_BORDER}`,
      backgroundColor: '#ffffff'
    },
    historySubMeta: {
      fontSize: '0.85rem',
      color: BRAND_MUTED
    },
    historyQuoteButton: {
      width: '100%',
      textAlign: 'left' as const,
      border: `1px solid ${BRAND_BORDER}`,
      backgroundColor: BRAND_SURFACE,
      borderRadius: '0.65rem',
      padding: '0.85rem',
      cursor: 'pointer',
      display: 'grid',
      gap: '0.35rem'
    },
    quoteDecisionPill: {
      display: 'inline-flex',
      alignItems: 'center',
      borderRadius: '999px',
      border: '1px solid transparent',
      padding: '0.2rem 0.6rem',
      fontSize: '0.75rem',
      fontWeight: 700,
      letterSpacing: '0.01em'
    },
    quoteDecisionPending: {
      backgroundColor: '#e2e8f0',
      borderColor: '#cbd5e1',
      color: '#334155'
    },
    quoteDecisionApproved: {
      backgroundColor: '#dcfce7',
      borderColor: '#86efac',
      color: '#166534'
    },
    quoteDecisionDenied: {
      backgroundColor: '#fee2e2',
      borderColor: '#fca5a5',
      color: '#991b1b'
    },
    quoteDecisionAdjust: {
      backgroundColor: '#ffedd5',
      borderColor: '#fdba74',
      color: '#9a3412'
    },
    quoteModalTitle: {
      fontSize: '1.2rem',
      fontWeight: 700,
      color: '#0f172a'
    },
    quoteModalMeta: {
      fontSize: '0.9rem',
      color: '#64748b'
    },
    quoteModalSection: {
      marginTop: '0.85rem',
      padding: '0.75rem',
      borderRadius: '0.6rem',
      border: '1px solid #e2e8f0',
      backgroundColor: '#ffffff'
    },
    quoteLineItem: {
      display: 'flex',
      justifyContent: 'space-between',
      gap: '0.8rem',
      padding: '0.45rem 0',
      borderBottom: '1px solid #f1f5f9'
    },
    historyEmpty: {
      padding: '1rem',
      borderRadius: '0.6rem',
      border: `1px dashed ${BRAND_BORDER}`,
      backgroundColor: BRAND_SURFACE,
      color: BRAND_MUTED
    },
    supportWrapper: {
      backgroundColor: BRAND_SURFACE,
      padding: 0
    },
    container: {
      maxWidth: isCompact ? '100%' : isTablet ? '760px' : '880px',
      margin: '0 auto',
      padding: '0',
      backgroundColor: 'transparent',
      minHeight: 'auto',
      display: 'grid',
      gap: '1.25rem'
    },
    header: {
      display: 'grid',
      gap: '0.45rem',
      padding: isCompact ? '1.1rem 1rem' : isTablet ? '1.35rem 1.4rem' : '1.75rem 2rem',
      backgroundColor: BRAND_SURFACE_SOFT,
      borderRadius: '0.85rem',
      border: `1px solid ${BRAND_BORDER}`,
      boxShadow: '0 1px 3px rgba(15, 23, 42, 0.08)'
    },
    supportHeaderEyebrow: {
      fontSize: '0.78rem',
      fontWeight: 700,
      letterSpacing: '0.08em',
      textTransform: 'uppercase' as const,
      color: BRAND_ACCENT
    },
    supportNotice: {
      padding: '0.9rem 1rem',
      borderRadius: '0.75rem',
      border: `1px solid ${BRAND_BORDER}`,
      backgroundColor: '#ffffff',
      color: BRAND_TEXT,
      boxShadow: '0 1px 2px rgba(15, 23, 42, 0.05)'
    },
    supportNoticeError: {
      border: '1px solid #e7c3bc',
      backgroundColor: '#fff7f4',
      color: '#9f3f2d'
    },
    supportStepperCard: {
      backgroundColor: BRAND_SURFACE_SOFT,
      borderRadius: '0.85rem',
      border: `1px solid ${BRAND_BORDER}`,
      padding: '1.5rem 1.25rem',
      boxShadow: '0 1px 3px rgba(15, 23, 42, 0.08)'
    },
    title: {
      fontSize: isCompact ? '1.55rem' : '2rem',
      fontWeight: 700,
      margin: 0,
      color: BRAND_TEXT
    },
    subtitle: {
      color: BRAND_MUTED,
      fontSize: isCompact ? '0.95rem' : '1rem',
      lineHeight: 1.6,
      margin: 0
    },
    progressBar: {
      display: 'flex',
      alignItems: 'flex-start',
      justifyContent: 'space-between',
      maxWidth: '720px',
      margin: '0 auto',
      position: 'relative' as const,
      gap: '0.5rem'
    },
    progressStep: {
      display: 'flex',
      flexDirection: 'column' as const,
      alignItems: 'center',
      position: 'relative' as const,
      flex: 1,
      gap: '0.65rem',
      zIndex: 2
    },
    progressConnector: {
      position: 'absolute' as const,
      top: '20px',
      left: '20px',
      right: '20px',
      height: '2px',
      backgroundColor: 'rgba(18, 63, 67, 0.12)',
      zIndex: 0
    },
    progressConnectorActive: {
      backgroundColor: BRAND_ACCENT,
      transition: 'width 0.3s ease'
    },
    progressCircle: {
      width: '40px',
      height: '40px',
      borderRadius: '999px',
      backgroundColor: '#ffffff',
      border: `1px solid ${BRAND_BORDER}`,
      color: BRAND_MUTED,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontWeight: 700,
      zIndex: 1,
      position: 'relative' as const,
      cursor: 'default',
      userSelect: 'none' as const,
      boxShadow: '0 1px 2px rgba(15, 23, 42, 0.06)'
    },
    progressCircleActive: {
      backgroundColor: BRAND_TEXT,
      borderColor: 'transparent',
      color: '#ffffff'
    },
    progressCircleComplete: {
      backgroundColor: BRAND_ACCENT,
      borderColor: 'transparent',
      color: '#ffffff'
    },
    progressLabel: {
      marginTop: 0,
      fontSize: '0.82rem',
      color: BRAND_MUTED,
      fontWeight: 600,
      textAlign: 'center' as const
    },
    card: {
      backgroundColor: BRAND_SURFACE_SOFT,
      borderRadius: '0.85rem',
      padding: isCompact ? '1rem' : isTablet ? '1.25rem' : '1.75rem',
      boxShadow: '0 1px 3px rgba(15, 23, 42, 0.08)',
      border: `1px solid ${BRAND_BORDER}`,
      display: 'grid',
      gap: '1.25rem'
    },
    supportSectionHeader: {
      display: 'grid',
      gap: '0.35rem'
    },
    supportSectionBadge: {
      width: 'fit-content',
      padding: '0.2rem 0.65rem',
      borderRadius: '999px',
      border: `1px solid ${BRAND_BORDER}`,
      backgroundColor: BRAND_SURFACE,
      color: BRAND_TEXT,
      fontSize: '0.75rem',
      fontWeight: 700,
      letterSpacing: '0.04em',
      textTransform: 'uppercase' as const
    },
    supportSectionTitle: {
      fontSize: isCompact ? '1.18rem' : '1.35rem',
      fontWeight: 700,
      color: BRAND_TEXT,
      margin: 0
    },
    supportSectionSubtitle: {
      fontSize: isCompact ? '0.92rem' : '0.95rem',
      color: BRAND_MUTED,
      lineHeight: 1.55,
      margin: 0
    },
    supportInsetCard: {
      marginTop: '0.25rem',
      padding: '0.95rem 1rem',
      borderRadius: '0.7rem',
      backgroundColor: '#ffffff',
      border: `1px solid ${BRAND_BORDER}`,
      color: BRAND_TEXT
    },
    supportInsetLabel: {
      fontSize: '0.78rem',
      fontWeight: 700,
      color: BRAND_MUTED,
      marginBottom: '0.35rem',
      letterSpacing: '0.03em',
      textTransform: 'uppercase' as const
    },
    accountWorkspaceNav: {
      display: 'grid',
      gridTemplateColumns: isCompact
        ? '1fr'
        : isTablet
        ? 'repeat(2, minmax(0, 1fr))'
        : 'repeat(auto-fit, minmax(180px, 1fr))',
      gap: '0.75rem'
    },
    accountWorkspaceNavButton: {
      display: 'grid',
      gap: '0.25rem',
      padding: isCompact ? '0.85rem 0.9rem' : '0.95rem 1rem',
      borderRadius: '0.8rem',
      border: `1px solid ${BRAND_BORDER}`,
      backgroundColor: '#ffffff',
      textAlign: 'left' as const,
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      fontFamily: 'inherit',
      minHeight: isCompact ? '86px' : '106px'
    },
    accountWorkspaceNavButtonActive: {
      backgroundColor: BRAND_TEXT,
      borderColor: 'transparent',
      boxShadow: '0 10px 24px rgba(18, 63, 67, 0.16)'
    },
    accountWorkspaceNavLabel: {
      fontSize: '0.75rem',
      fontWeight: 700,
      letterSpacing: '0.05em',
      textTransform: 'uppercase' as const,
      color: BRAND_MUTED
    },
    accountWorkspaceNavValue: {
      fontSize: '1.1rem',
      fontWeight: 700,
      color: BRAND_TEXT,
      lineHeight: 1.3
    },
    accountWorkspaceNavMeta: {
      fontSize: '0.88rem',
      color: BRAND_MUTED,
      lineHeight: 1.5
    },
    accountWorkspacePanel: {
      padding: '1.25rem',
      borderRadius: '0.85rem',
      border: `1px solid ${BRAND_BORDER}`,
      backgroundColor: BRAND_SURFACE,
      display: 'grid',
      gap: '1rem'
    },
    accountPreviewGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
      gap: '0.75rem'
    },
    accountLinkedList: {
      display: 'grid',
      gap: '0.6rem'
    },
    accountLinkedListItem: {
      padding: '0.75rem 0.85rem',
      borderRadius: '0.7rem',
      border: `1px solid ${BRAND_BORDER}`,
      backgroundColor: '#ffffff'
    },
    supportLoadingCard: {
      marginTop: '0.75rem',
      padding: '1.5rem',
      borderRadius: '0.75rem',
      border: `1px solid ${BRAND_BORDER}`,
      backgroundColor: '#ffffff',
      textAlign: 'center' as const
    },
    supportLoadingTitle: {
      fontSize: '1rem',
      fontWeight: 700,
      color: BRAND_TEXT
    },
    supportLoadingText: {
      marginTop: '0.35rem',
      color: BRAND_MUTED,
      fontSize: '0.9rem'
    },
    supportLoadingOverlay: {
      position: 'absolute' as const,
      inset: 0,
      backgroundColor: 'rgba(251, 248, 242, 0.94)',
      borderRadius: '0.75rem',
      border: `1px solid ${BRAND_BORDER}`,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexDirection: 'column' as const,
      gap: '0.35rem',
      zIndex: 2
    },
    supportCenteredActions: {
      display: 'flex',
      gap: '0.75rem',
      flexWrap: 'wrap' as const,
      justifyContent: 'center'
    },
    formGroup: {
      marginBottom: 0,
      display: 'grid',
      gap: '0.5rem'
    },
    label: {
      display: 'block',
      marginBottom: 0,
      fontWeight: 600,
      fontSize: '0.95rem',
      color: BRAND_TEXT
    },
    sublabel: {
      fontSize: '0.88rem',
      color: BRAND_MUTED,
      marginBottom: 0,
      lineHeight: 1.5
    },
    input: {
      width: '100%',
      padding: '0.75rem',
      border: `1px solid ${BRAND_BORDER}`,
      borderRadius: '0.6rem',
      fontSize: '1rem',
      backgroundColor: '#ffffff',
      color: BRAND_TEXT
    },
    select: {
      width: '100%',
      padding: '0.75rem',
      border: `1px solid ${BRAND_BORDER}`,
      borderRadius: '0.6rem',
      fontSize: '1rem',
      backgroundColor: '#ffffff',
      color: BRAND_TEXT
    },
    textarea: {
      width: '100%',
      minHeight: '120px',
      padding: '0.75rem',
      border: `1px solid ${BRAND_BORDER}`,
      borderRadius: '0.6rem',
      fontSize: '1rem',
      backgroundColor: '#ffffff',
      resize: 'vertical' as const,
      color: BRAND_TEXT
    },
    button: {
      padding: isCompact ? '0.78rem 1rem' : '0.7rem 1.2rem',
      borderRadius: '0.6rem',
      border: `1px solid ${BRAND_BORDER}`,
      fontWeight: 600,
      fontSize: '0.95rem',
      cursor: 'pointer',
      transition: 'all 0.2s',
      fontFamily: 'inherit'
    },
    primaryButton: {
      backgroundColor: BRAND_TEXT,
      borderColor: 'transparent',
      color: '#ffffff'
    },
    secondaryButton: {
      backgroundColor: BRAND_SURFACE,
      color: BRAND_TEXT,
      borderColor: BRAND_BORDER
    },
    tertiaryButton: {
      backgroundColor: '#ffffff',
      color: BRAND_TEXT,
      borderColor: BRAND_BORDER
    },
    deviceHistoryButton: {
      backgroundColor: '#ffffff',
      color: BRAND_TEXT,
      borderColor: BRAND_BORDER
    },
    contactButton: {
      backgroundColor: '#fbf3e2',
      color: '#8a4b13',
      borderColor: '#ecd8b4'
    },
    buttonGroup: {
      display: 'flex',
      gap: '0.75rem',
      justifyContent: isCompact ? 'stretch' : 'flex-end',
      flexWrap: 'wrap' as const,
      alignItems: isCompact ? 'stretch' : 'center',
      flexDirection: (isCompact ? 'column' : 'row') as React.CSSProperties['flexDirection'],
      paddingTop: '0.5rem'
    },
    uploadArea: {
      border: `1px dashed ${BRAND_BORDER}`,
      borderRadius: '0.75rem',
      padding: '1.5rem',
      textAlign: 'center' as const,
      cursor: 'pointer',
      transition: 'all 0.2s',
      backgroundColor: '#ffffff',
      display: 'grid',
      gap: '0.35rem',
      color: BRAND_TEXT
    },
    uploadAreaHover: {
      borderColor: BRAND_ACCENT,
      backgroundColor: '#fffaf1'
    },
    fileList: {
      marginTop: '1rem',
      display: 'grid',
      gap: '0.5rem'
    },
    fileItem: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      gap: '0.75rem',
      padding: '0.7rem 0.85rem',
      backgroundColor: '#ffffff',
      borderRadius: '0.65rem',
      border: `1px solid ${BRAND_BORDER}`
    },
    removeButton: {
      color: '#9f3f2d',
      cursor: 'pointer',
      fontWeight: 600
    },
    optionGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
      gap: '0.75rem',
      marginTop: '1rem'
    },
    quickQuestionCard: {
      padding: '0.95rem 1rem',
      border: `1px solid ${BRAND_BORDER}`,
      borderRadius: '0.65rem',
      backgroundColor: '#ffffff',
      display: 'grid',
      gap: '0.75rem'
    },
    quickQuestionLabel: {
      color: BRAND_TEXT,
      fontWeight: 600,
      lineHeight: 1.4
    },
    quickQuestionActions: {
      display: 'grid',
      gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
      gap: '0.5rem'
    },
    quickQuestionAction: {
      padding: '0.5rem 0.65rem',
      border: `1px solid ${BRAND_BORDER}`,
      borderRadius: '0.55rem',
      backgroundColor: '#ffffff',
      color: BRAND_TEXT,
      fontSize: '0.84rem',
      fontWeight: 600,
      textAlign: 'center' as const,
      cursor: 'pointer',
      transition: 'all 0.2s',
      width: '100%',
      minHeight: '2.1rem',
      boxSizing: 'border-box' as const,
      outline: 'none',
      boxShadow: 'none'
    },
    quickQuestionActionYesActive: {
      borderColor: '#1f7a5a',
      backgroundColor: '#e8f3ef',
      color: '#14532d'
    },
    quickQuestionActionNoActive: {
      borderColor: '#b45309',
      backgroundColor: '#fff7ed',
      color: '#92400e'
    },
    quickQuestionActionSkipActive: {
      borderColor: '#64748b',
      backgroundColor: '#f1f5f9',
      color: '#334155'
    },
    optionButton: {
      padding: '0.95rem 1rem',
      border: `1px solid ${BRAND_BORDER}`,
      borderRadius: '0.65rem',
      backgroundColor: '#ffffff',
      cursor: 'pointer',
      transition: 'all 0.2s',
      textAlign: 'left' as const,
      color: BRAND_TEXT,
      fontWeight: 600
    },
    optionButtonSelected: {
      borderColor: 'rgba(18, 63, 67, 0.28)',
      backgroundColor: BRAND_SURFACE,
      boxShadow: '0 0 0 1px rgba(18, 63, 67, 0.08)'
    },
    reviewSection: {
      marginBottom: '0.75rem',
      padding: '1rem',
      backgroundColor: '#ffffff',
      borderRadius: '0.7rem',
      border: `1px solid ${BRAND_BORDER}`,
      display: 'grid',
      gap: '0.35rem'
    },
    reviewTitle: {
      fontWeight: 700,
      marginBottom: 0,
      color: BRAND_TEXT,
      fontSize: '0.78rem',
      letterSpacing: '0.03em',
      textTransform: 'uppercase' as const
    },
    reviewContent: {
      color: BRAND_MUTED,
      lineHeight: 1.55
    },
    successIcon: {
      width: '72px',
      height: '72px',
      borderRadius: '999px',
      backgroundColor: '#e8f3ef',
      color: BRAND_TEXT,
      display: 'grid',
      placeItems: 'center' as const,
      fontSize: '2rem',
      fontWeight: 700,
      textAlign: 'center' as const,
      margin: '0 auto'
    },
    caseNumber: {
      fontSize: '1.5rem',
      fontWeight: 700,
      color: BRAND_TEXT,
      textAlign: 'center' as const,
      marginBottom: 0
    },
    customModelSection: {
      backgroundColor: '#ffffff',
      padding: '1rem',
      borderRadius: '0.7rem',
      marginTop: '0.25rem',
      border: `1px solid ${BRAND_BORDER}`,
      display: 'grid',
      gap: '1rem'
    },
    warningText: {
      color: '#8a4b13',
      fontSize: '0.875rem',
      marginTop: 0,
      padding: '0.75rem 0.85rem',
      backgroundColor: '#fbf3e2',
      border: '1px solid #ecd8b4',
      borderRadius: '0.6rem'
    },
    supportSuccessText: {
      textAlign: 'center' as const,
      color: BRAND_MUTED,
      marginBottom: 0,
      lineHeight: 1.6
    },
    supportSuccessActions: {
      display: 'flex',
      gap: '0.75rem',
      justifyContent: 'center',
      flexWrap: 'wrap' as const
    },
    historyOverlay: {
      position: 'fixed' as const,
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(15, 23, 42, 0.55)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'flex-start',
      padding: '4rem 1rem',
      zIndex: 1000
    },
    historyPanel: {
      width: '100%',
      maxWidth: '720px',
      backgroundColor: '#ffffff',
      borderRadius: '0.75rem',
      boxShadow: '0 20px 40px rgba(15, 23, 42, 0.25)',
      maxHeight: '80vh',
      display: 'flex',
      flexDirection: 'column' as const,
      overflow: 'hidden'
    },
    quotePanel: {
      width: '100%',
      maxWidth: '980px',
      backgroundColor: '#ffffff',
      borderRadius: '0.75rem',
      boxShadow: '0 20px 40px rgba(15, 23, 42, 0.25)',
      maxHeight: '80vh',
      display: 'flex',
      flexDirection: 'column' as const,
      overflow: 'hidden'
    },
    historyHeader: {
      padding: '1.5rem 2rem',
      borderBottom: '1px solid #e5e7eb',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center'
    },
    historyModalTitle: {
      fontSize: '1.25rem',
      fontWeight: 600,
      marginBottom: '0.25rem'
    },
    historySubtitle: {
      color: '#6b7280',
      fontSize: '0.95rem'
    },
    historyClose: {
      backgroundColor: '#ffffff',
      border: `1px solid ${BRAND_BORDER}`,
      borderRadius: '0.75rem',
      fontSize: '0.95rem',
      fontWeight: 600,
      color: BRAND_TEXT,
      cursor: 'pointer',
      padding: '0.55rem 1rem',
      lineHeight: 1.2
    },
    historyBody: {
      padding: '1.5rem 2rem',
      overflowY: 'auto' as const
    },
    historyState: {
      textAlign: 'center' as const,
      color: '#6b7280',
      padding: '2rem 0'
    },
    historyList: {
      display: 'flex',
      flexDirection: 'column' as const,
      gap: '1rem'
    },
    historyCase: {
      border: '1px solid #e5e7eb',
      borderRadius: '0.5rem',
      overflow: 'hidden',
      backgroundColor: '#fff'
    },
    historyCaseHeader: {
      width: '100%',
      background: 'none',
      border: 'none',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: '1rem 1.25rem',
      cursor: 'pointer'
    },
    historyCaseTitle: {
      fontWeight: 600,
      fontSize: '1rem',
      marginBottom: '0.15rem'
    },
    historyCaseMeta: {
      fontSize: '0.85rem',
      color: '#6b7280'
    },
    historyCaret: {
      fontSize: '1.5rem',
      color: '#6b7280',
      marginLeft: '1rem'
    },
    historyCaseBody: {
      borderTop: '1px solid #e5e7eb',
      padding: '1rem 1.25rem',
      backgroundColor: '#f9fafb'
    },
    historyDescription: {
      marginBottom: '0.75rem',
      color: '#374151'
    },
    historyStatusRow: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      flexWrap: 'wrap' as const,
      gap: '0.5rem',
      marginBottom: '0.75rem'
    },
    historyStatusLabel: {
      fontWeight: 500,
      color: '#6b7280'
    },
    historyStatusBadge: {
      padding: '0.2rem 0.75rem',
      borderRadius: '999px',
      fontSize: '0.85rem',
      fontWeight: 600,
      backgroundColor: '#e5e7eb',
      color: '#374151'
    },
    historyMetaRow: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
      gap: '0.5rem',
      fontSize: '0.85rem',
      color: '#6b7280',
      marginBottom: '0.75rem'
    },
    historyTasks: {
      marginTop: '1rem'
    },
    historyTasksTitle: {
      fontWeight: 600,
      marginBottom: '0.5rem',
      fontSize: '0.95rem'
    },
    historyTaskList: {
      listStyle: 'none',
      padding: 0,
      margin: 0,
      display: 'flex',
      flexDirection: 'column' as const,
      gap: '0.75rem'
    },
    historyTaskItem: {
      padding: '0.75rem',
      borderRadius: '0.375rem',
      backgroundColor: '#ffffff',
      border: '1px solid #e5e7eb'
    },
    historyTaskLabel: {
      fontWeight: 500,
      marginBottom: '0.25rem'
    },
    historyTaskMeta: {
      fontSize: '0.85rem',
      color: '#6b7280'
    }
  };

  const isInitialQuestionLoading = dynamicQuestionsLoading && !dynamicQuestionsLoaded;
  const isRefreshingQuestions = dynamicQuestionsLoading && dynamicQuestionsLoaded;

  const supportRequestContent = (
    <div style={styles.container}>
      <div style={styles.header}>
        <div style={styles.supportHeaderEyebrow}>Support Request</div>
        <h1 style={styles.title}>Submit a Service Request</h1>
        <p style={styles.subtitle}>
          We&apos;ll guide you through the details, collect the right evidence, and route the issue to your support team.
        </p>
      </div>

      {supportRequestNotice && (
        <div style={{ ...styles.supportNotice, ...styles.supportNoticeError }}>
          {supportRequestNotice.message}
        </div>
      )}

      <div style={styles.supportStepperCard}>
        <div style={styles.progressBar}>
          <div style={styles.progressConnector} />
          <div
            style={{
              ...styles.progressConnector,
              ...styles.progressConnectorActive,
              width: `${((Math.min(currentStep, 4) - 1) / 3) * 100}%`,
              maxWidth: 'calc(100% - 40px)'
            }}
          />
          {[1, 2, 3, 4].map((step) => (
            <div key={step} style={styles.progressStep}>
            <div
              style={{
                ...styles.progressCircle,
                ...(currentStep === step ? styles.progressCircleActive : {}),
                ...(currentStep > step ? styles.progressCircleComplete : {})
              }}
            >
              {currentStep > step ? '\u2713' : step}
            </div>
            <span style={styles.progressLabel}>
              {step === 1 && 'Device Info'}
              {step === 2 && 'Problem'}
              {step === 3 && 'Details'}
              {step === 4 && 'Contact'}
            </span>
          </div>
        ))}
      </div>
      </div>

      {currentStep === 1 && (
  <div style={styles.card}>
    <div style={styles.supportSectionHeader}>
      <div style={styles.supportSectionBadge}>Step 1 of 4</div>
      <h2 style={styles.supportSectionTitle}>Device Information</h2>
      <p style={styles.supportSectionSubtitle}>
        Help us identify your device so we can route the request with the right context.
      </p>
    </div>

    {customerDevices.length > 0 && (
      <div style={styles.formGroup}>
        <label style={styles.label}>Your Registered Devices</label>
        <select
          style={styles.select}
          value={selectedAssignedModelKey}
          onChange={(e) => handleAssignedModelSelect(e.target.value)}
        >
          <option value=''>Select your device...</option>
          {assignedModelOptions.map(option => (
            <option key={option.key} value={option.key}>
              {option.label}
            </option>
          ))}
          <option value='MANUAL'>My device isn't listed</option>
        </select>
      </div>
    )}

    {usingAssignedDevice && !manualIdentifierEntry && assignedSerialOptions.length > 0 && (
      portalCustomer?.assetNumbersEnabled ? (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: isCompact ? '1fr' : 'repeat(2, minmax(0, 1fr))',
            gap: '0.75rem'
          }}
        >
          <div style={styles.formGroup}>
            <label style={styles.label}>Asset Number</label>
            <select
              style={styles.select}
              value={selectedAssignedAssetSelectValue}
              onChange={(e) => handleAssignedAssetSelect(e.target.value)}
            >
              <option value=''>Select an asset number...</option>
              {assignedSerialOptions
                .filter(device => String(device.assetNumber || '').trim())
                .map(device => (
                  <option key={device.id} value={device.id}>
                    {device.assetNumber}
                  </option>
                ))}
              <option value='MANUAL_ASSET'>Asset number not listed</option>
            </select>
          </div>
          <div style={styles.formGroup}>
            <label style={styles.label}>Serial Number</label>
            <select
              style={styles.select}
              value={selectedAssignedSerialSelectValue}
              onChange={(e) => handleAssignedSerialSelect(e.target.value)}
            >
              <option value=''>Select a serial number...</option>
              {assignedSerialOptions.map(device => (
                <option key={device.id} value={device.id}>
                  {device.serialNumber || 'Serial not recorded'}
                </option>
              ))}
              <option value='MANUAL_SERIAL'>Serial number not listed</option>
            </select>
          </div>
        </div>
      ) : (
        <div style={styles.formGroup}>
          <label style={styles.label}>Serial Number</label>
          <select
            style={styles.select}
            value={selectedAssignedDeviceId}
            onChange={(e) => handleAssignedSerialSelect(e.target.value)}
          >
            <option value=''>Select a serial number...</option>
            {assignedSerialOptions.map(device => (
              <option key={device.id} value={device.id}>
                {formatDeviceIdentifier(device)}
              </option>
            ))}
            <option value='MANUAL_SERIAL'>Serial not listed</option>
          </select>
        </div>
      )
    )}

    {!usingAssignedDevice && (manualIdentifierEntry || customerDevices.length === 0) && (
      <div style={styles.formGroup}>
        <label style={styles.label}>Device Model</label>
        <select
          style={styles.select}
          value={selectedModel}
          onChange={(e) => handleModelChange(e.target.value)}
          required
          disabled={usingAssignedDevice}
        >
          <option value=''>Select your device model...</option>
          {deviceModels.map(model => (
            <option key={model.id} value={model.id}>
              {model.modelNumber} - {model.manufacturer}
            </option>
          ))}
          <option value='' disabled>--------------------------</option>
          <option value='OTHER'>Other (Not Listed)</option>
        </select>
      </div>
    )}

    {!usingAssignedDevice && isCustomModel && (
      <div style={styles.customModelSection}>
        <p style={{ margin: 0, fontWeight: 600, color: BRAND_TEXT }}>Enter your device information:</p>
        <div style={styles.formGroup}>
          <label style={styles.label}>Model Number</label>
          <input
            type='text'
            style={styles.input}
            placeholder='e.g., ABC-1234'
            value={customModelNumber}
            onChange={(e) => setCustomModelNumber(e.target.value)}
            required
          />
        </div>
        <div style={styles.formGroup}>
          <label style={styles.label}>Manufacturer</label>
          <input
            type='text'
            style={styles.input}
            placeholder='e.g., Acme Corp'
            value={customManufacturer}
            onChange={(e) => setCustomManufacturer(e.target.value)}
            required
          />
        </div>
        <p style={styles.warningText}>
          Note: A technician will verify this model information before proceeding with service.
        </p>
      </div>
    )}

    {(manualIdentifierEntry || customerDevices.length === 0) && (
      portalCustomer?.assetNumbersEnabled ? (
      <div style={styles.formGroup}>
        {usingAssignedDevice && (
          <button
            type='button'
            style={{ ...styles.button, ...styles.secondaryButton, justifySelf: 'start', padding: '0.55rem 0.8rem' }}
            onClick={() => clearAssignedIdentifierSelection(false)}
          >
            Return to listed devices
          </button>
        )}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: isCompact ? '1fr' : 'repeat(2, minmax(0, 1fr))',
            gap: '0.75rem'
          }}
        >
          <div style={styles.formGroup}>
            <label style={styles.label}>Serial Number</label>
            <input
              type='text'
              style={styles.input}
              placeholder='Enter serial number if known'
              value={serialNumber}
              onChange={(e) => {
                setSelectedAssignedDeviceId('');
                setSerialNumber(e.target.value);
              }}
            />
          </div>
          <div style={styles.formGroup}>
            <label style={styles.label}>Asset Number</label>
            <input
              type='text'
              style={styles.input}
              placeholder='Enter asset number if known'
              value={assetNumber}
              onChange={(e) => {
                setSelectedAssignedDeviceId('');
                setAssetNumber(e.target.value);
              }}
            />
          </div>
        </div>
      </div>
    ) : (
      <div style={styles.formGroup}>
        {usingAssignedDevice && (
          <button
            type='button'
            style={{ ...styles.button, ...styles.secondaryButton, justifySelf: 'start', padding: '0.55rem 0.8rem' }}
            onClick={() => clearAssignedIdentifierSelection(false)}
          >
            Return to listed devices
          </button>
        )}
        <label style={styles.label}>Serial Number</label>
        <input
          type='text'
          style={styles.input}
          placeholder='Enter your device serial number'
          value={serialNumber}
          onChange={(e) => {
            setSelectedAssignedDeviceId('');
            setSerialNumber(e.target.value);
          }}
          required
        />
      </div>
      )
    )}

    <div style={{ ...styles.buttonGroup, justifyContent: 'flex-end' }}>
      <button
        style={{ ...styles.button, ...styles.primaryButton }}
        onClick={handleDeviceLookup}
        disabled={!canContinue}
      >
        Continue
      </button>
    </div>
  </div>
)}

      {currentStep === 2 && (
        <div style={styles.card}>
          <div style={styles.supportSectionHeader}>
            <div style={styles.supportSectionBadge}>Step 2 of 4</div>
            <h2 style={styles.supportSectionTitle}>Describe the Problem</h2>
            <p style={styles.supportSectionSubtitle}>
              Tell us what&apos;s happening with your {deviceInfo?.modelNumber || 'device'}.
            </p>
          </div>
          
          <div style={styles.formGroup}>
            <label style={styles.label}>Problem Description</label>
            <textarea
              style={styles.textarea}
              placeholder="Please describe the issue you're experiencing in detail..."
              value={problemDescription}
              onChange={(e) => setProblemDescription(e.target.value)}
              required
            />
          </div>

          <div style={styles.formGroup}>
            <label style={styles.label}>Upload Photos or Videos (Optional)</label>
            <p style={styles.sublabel}>Help us better understand the issue</p>
            <div
              style={styles.uploadArea}
              onClick={() => fileInputRef.current?.click()}
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => {
                e.preventDefault();
                const files = e.dataTransfer.files;
                if (files) setUploadedFiles(prev => [...prev, ...Array.from(files)]);
              }}
            >
              <p>Drag and drop files here or click to browse</p>
              <p style={styles.sublabel}>
                Supported: Images, Videos (Max 50MB)
              </p>
            </div>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept="image/*,video/*"
              onChange={handleFileUpload}
              style={{ display: 'none' }}
            />
            
            {uploadedFiles.length > 0 && (
              <div style={styles.fileList}>
                {uploadedFiles.map((file, index) => (
                  <div key={index} style={styles.fileItem}>
                    <span>{file.name} ({(file.size / 1024 / 1024).toFixed(1)}MB)</span>
                    <span
                      style={styles.removeButton}
                      onClick={() => handleRemoveFile(index)}
                    >
                      Remove
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div style={styles.buttonGroup}>
            <button
              style={{ ...styles.button, ...styles.secondaryButton }}
              onClick={() => setCurrentStep(1)}
            >
              Back
            </button>
            <button
              style={{ ...styles.button, ...styles.primaryButton }}
              onClick={() => setCurrentStep(3)}
              disabled={!problemDescription}
            >
              Continue
            </button>
          </div>
        </div>
      )}

      {currentStep === 3 && (
        <div style={styles.card}>
          <div style={styles.supportSectionHeader}>
            <div style={styles.supportSectionBadge}>Step 3 of 4</div>
            <h2 style={styles.supportSectionTitle}>Additional Information</h2>
            <p style={styles.supportSectionSubtitle}>
              Select any details that apply so your support team can diagnose faster.
            </p>
          </div>
          <div style={styles.supportInsetCard}>
            <div style={styles.supportInsetLabel}>
              Problem summary
            </div>
            <div style={{ whiteSpace: 'pre-wrap' as const }}>
              {problemDescription.trim() || 'No problem description provided.'}
            </div>
          </div>
          
          <div style={styles.formGroup}>
            <label style={styles.label}>Quick Answers (Optional)</label>
            {!dynamicQuestionsLoading && dynamicQuestionsError && (
              <p style={styles.sublabel}>{dynamicQuestionsError}</p>
            )}
            {isInitialQuestionLoading ? (
              <div style={styles.supportLoadingCard}>
                <div style={styles.supportLoadingTitle}>
                  Loading additional questions...
                </div>
                <div style={styles.supportLoadingText}>
                  We are tailoring these to your device and description.
                </div>
              </div>
            ) : (
              <div style={{ position: 'relative' as const }}>
                {isRefreshingQuestions && (
                  <div style={styles.supportLoadingOverlay}>
                    <div style={styles.supportLoadingTitle}>
                      Loading additional questions...
                    </div>
                    <div style={styles.supportLoadingText}>
                      Hold on while we refresh the list.
                    </div>
                  </div>
                )}
                <div
                  style={{
                    ...styles.optionGrid,
                    opacity: isRefreshingQuestions ? 0.4 : 1,
                    pointerEvents: isRefreshingQuestions ? 'none' : 'auto'
                  }}
                >
                  {dynamicQuestions.quickButtons.map((question) => (
                    <div
                      key={question.id}
                      style={styles.quickQuestionCard}
                    >
                      <div style={styles.quickQuestionLabel}>{question.label}</div>
                      <div style={styles.quickQuestionActions}>
                        <button
                          type="button"
                          style={{
                            ...styles.quickQuestionAction,
                            ...(quickAnswerMap[question.id] === 'YES'
                              ? styles.quickQuestionActionYesActive
                              : {})
                          }}
                          onClick={() => handleQuickAnswerChoice(question.id, 'YES')}
                          disabled={dynamicQuestionsLoading}
                        >
                          Yes
                        </button>
                        <button
                          type="button"
                          style={{
                            ...styles.quickQuestionAction,
                            ...(quickAnswerMap[question.id] === 'NO'
                              ? styles.quickQuestionActionNoActive
                              : {})
                          }}
                          onClick={() => handleQuickAnswerChoice(question.id, 'NO')}
                          disabled={dynamicQuestionsLoading}
                        >
                          No
                        </button>
                        <button
                          type="button"
                          style={{
                            ...styles.quickQuestionAction,
                            ...(quickAnswerMap[question.id] === 'SKIP'
                              ? styles.quickQuestionActionSkipActive
                              : {})
                          }}
                          onClick={() => handleQuickAnswerChoice(question.id, 'SKIP')}
                          disabled={dynamicQuestionsLoading}
                        >
                          Ignore
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div style={styles.formGroup}>
            {!isInitialQuestionLoading && (
              <div style={styles.supportCenteredActions}>
                <button
                  style={{ ...styles.button, ...styles.primaryButton }}
                  onClick={() => fetchDynamicQuestions(true)}
                  disabled={dynamicQuestionsLoading}
                >
                  {dynamicQuestionsLoading ? 'Refreshing...' : 'Refresh questions'}
                </button>
                <button
                  style={{ ...styles.button, ...styles.tertiaryButton }}
                  onClick={() => {
                    setQuickAnswerMap({});
                    setAdditionalNotes('');
                  }}
                  disabled={dynamicQuestionsLoading}
                >
                  Clear selections
                </button>
              </div>
            )}
          </div>

          <div style={styles.formGroup}>
            <label style={styles.label}>Additional Notes (Optional)</label>
            <p style={styles.sublabel}>
              Enter any additional information that might help the support team.
            </p>
            <textarea
              style={styles.textarea}
              placeholder="Anything else you noticed, already checked, or changed..."
              value={additionalNotes}
              onChange={(e) => setAdditionalNotes(e.target.value)}
              disabled={dynamicQuestionsLoading}
            />
          </div>

          <div style={styles.buttonGroup}>
            <button
              style={{ ...styles.button, ...styles.secondaryButton }}
              onClick={() => setCurrentStep(2)}
            >
              Back
            </button>
            <button
              style={{ ...styles.button, ...styles.primaryButton }}
              onClick={() => setCurrentStep(4)}
            >
              Continue
            </button>
          </div>
        </div>
      )}

      {currentStep === 4 && (
        <div style={styles.card}>
          <div style={styles.supportSectionHeader}>
            <div style={styles.supportSectionBadge}>Step 4 of 4</div>
            <h2 style={styles.supportSectionTitle}>Contact Information</h2>
            <p style={styles.supportSectionSubtitle}>How should we reach you about this case?</p>
          </div>
          
          <div style={styles.formGroup}>
            <label style={styles.label}>Email Address *</label>
            <input
              type="email"
              style={styles.input}
              placeholder="your.email@company.com"
              value={contactEmail}
              onChange={(e) => setContactEmail(e.target.value)}
              required
            />
          </div>

          <div style={styles.formGroup}>
            <label style={styles.label}>Phone Number (Optional)</label>
            <input
              type="tel"
              style={styles.input}
              placeholder="+1 (555) 123-4567"
              value={contactPhone}
              onChange={(e) => setContactPhone(e.target.value)}
            />
          </div>

          <div style={{ marginTop: '2rem' }}>
            <h3 style={{ marginBottom: '1rem', color: BRAND_TEXT, fontSize: '1rem' }}>Review Your Request</h3>
            
            <div style={styles.reviewSection}>
              <div style={styles.reviewTitle}>Device</div>
              <div style={styles.reviewContent}>
                {deviceInfo?.modelNumber} - {formatDeviceIdentifier(deviceInfo)}
                {isCustomModel && ' (To be verified)'}
              </div>
            </div>

            <div style={styles.reviewSection}>
              <div style={styles.reviewTitle}>Problem</div>
              <div style={styles.reviewContent}>{problemDescription}</div>
            </div>

            {(Object.keys(quickAnswerMap).length > 0 || additionalNotes.trim()) && (
              <div style={styles.reviewSection}>
                <div style={styles.reviewTitle}>Additional Info</div>
                <div style={styles.reviewContent}>
                  {dynamicQuestions.quickButtons
                    .map(question => {
                      const answer = quickAnswerMap[question.id];
                      if (!answer) return null;
                      const answerLabel =
                        answer === 'YES' ? 'Yes' : answer === 'NO' ? 'No' : 'Ignored';
                      return `${question.label} ${answerLabel}`;
                    })
                    .filter(Boolean)
                    .join(' | ') || 'No quick answers selected.'}
                  {additionalNotes.trim() ? (
                    <div style={{ marginTop: '0.5rem', whiteSpace: 'pre-wrap' as const }}>
                      {additionalNotes.trim()}
                    </div>
                  ) : null}
                </div>
              </div>
            )}

            {uploadedFiles.length > 0 && (
              <div style={styles.reviewSection}>
                <div style={styles.reviewTitle}>Attachments</div>
                <div style={styles.reviewContent}>
                  {uploadedFiles.length} file(s) uploaded
                </div>
              </div>
            )}
          </div>

          <div style={styles.buttonGroup}>
            <button
              style={{ ...styles.button, ...styles.secondaryButton }}
              onClick={() => setCurrentStep(3)}
              disabled={isSubmitting}
            >
              Back
            </button>
            <button
              style={{ ...styles.button, ...styles.primaryButton }}
              onClick={handleSubmit}
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Submitting...' : 'Submit Request'}
            </button>
          </div>
        </div>
      )}

      {currentStep === 5 && (
        <div style={styles.card}>
          <div style={styles.supportSectionHeader}>
            <div style={styles.supportSectionBadge}>Submitted</div>
          </div>
          <div style={styles.successIcon} aria-hidden="true">{'\u2713'}</div>
          <h2 style={{ textAlign: 'center', margin: 0, color: BRAND_TEXT }}>Request Submitted Successfully</h2>
          <div style={styles.caseNumber}>{caseLabel} #{caseNumber}</div>
          <p style={styles.supportSuccessText}>
            We&apos;ve received your service request. A technician will review the case and contact you soon.
          </p>

          <div style={styles.supportSuccessActions}>
            <button
              style={{ ...styles.button, ...styles.secondaryButton }}
              onClick={() => resetSupportRequestForm({ clearCaseNumber: true })}
            >
              Submit Another Request
            </button>
            <button
              style={{ ...styles.button, ...styles.primaryButton }}
              onClick={() => window.print()}
            >
              Print Confirmation
            </button>
          </div>
        </div>
      )}

      {isHistoryOpen && (
        <div style={styles.historyOverlay} role="dialog" aria-modal="true" aria-label="Device history">
          <div style={styles.historyPanel}>
            <div style={styles.historyHeader}>
              <div>
                <div style={styles.historyModalTitle}>Device History</div>
                <div style={styles.historySubtitle}>
                  {[historyDeviceMeta.model, historyDeviceMeta.serial].filter(Boolean).join(' | ') || 'No device selected'}
                </div>
              </div>
              <button
                style={styles.historyClose}
                onClick={closeHistoryPanel}
                aria-label="Close device history"
              >
                Close
              </button>
            </div>
            <div style={styles.historyBody}>
              {isHistoryLoading && <div style={styles.historyState}>Loading history...</div>}
              {!isHistoryLoading && historyError && (
                <div style={{ ...styles.historyState, color: '#b91c1c' }}>{historyError}</div>
              )}
              {!isHistoryLoading && !historyError && deviceHistory.length === 0 && (
                <div style={styles.historyState}>No previous cases were found for this device.</div>
              )}
              {!isHistoryLoading && !historyError && deviceHistory.length > 0 && (
                <div style={styles.historyList}>
                  {deviceHistory.map(historyCase => {
                    const expanded = !!expandedHistoryCases[historyCase.id];
                    return (
                      <div key={historyCase.id} style={styles.historyCase}>
                        <button
                          style={styles.historyCaseHeader}
                          onClick={() => toggleHistoryCase(historyCase.id)}
                          aria-expanded={expanded}
                        >
                          <div>
                            <div style={styles.historyCaseTitle}>
                              {historyCase.caseNumber ? `Case #${historyCase.caseNumber}` : 'Service Case'}
                            </div>
                            <div style={styles.historyCaseMeta}>
                              {formatDateTime(historyCase.createdAt)} | {formatPortalCaseStatusLabel(historyCase.status, 'Unknown status')}
                            </div>
                          </div>
                          <span style={styles.historyCaret}>{expanded ? '-' : '+'}</span>
                        </button>
                        {expanded && (
                          <div style={styles.historyCaseBody}>
                            <p style={styles.historyDescription}>
                              {historyCase.problemDescription || 'No description was provided for this case.'}
                            </p>
                            <div style={styles.historyStatusRow}>
                              <span style={styles.historyStatusLabel}>Current Status</span>
                              <span
                                style={{
                                  ...styles.historyStatusBadge,
                                  ...getStatusBadgeStyles(historyCase.status)
                                }}
                              >
                                {formatPortalCaseStatusLabel(historyCase.status)}
                              </span>
                            </div>
                            <div style={styles.historyMetaRow}>
                              <div>Opened: {formatDateTime(historyCase.createdAt)}</div>
                              <div>Updated: {formatDateTime(historyCase.updatedAt || historyCase.closedAt)}</div>
                            </div>
                            {historyCase.tasks && historyCase.tasks.length > 0 && (
                              <div style={styles.historyTasks}>
                                <div style={styles.historyTasksTitle}>Work Progress</div>
                                <ul style={styles.historyTaskList}>
                                  {historyCase.tasks.map(task => (
                                    <li key={task.id} style={styles.historyTaskItem}>
                                      <div style={styles.historyTaskLabel}>
                                        {task.type?.label || 'Task'} | {task.stage?.label || task.status || 'Scheduled'}
                                      </div>
                                      <div style={styles.historyTaskMeta}>
                                        Status: {task.status || 'PENDING'} | Updated{' '}
                                        {formatDateTime(task.updatedAt || task.startedAt || task.createdAt)}
                                      </div>
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );

  const portalDisplayName =
    portalCustomer?.companyName || portalCustomer?.company || 'Customer';
  const portalUserDisplayName =
    user?.fullName ||
    user?.username ||
    portalCustomer?.contacts?.find(contact => contact.isPrimary && contact.fullName)?.fullName ||
    portalCustomer?.contacts?.find(contact => contact.fullName)?.fullName ||
    portalDisplayName;

  const baseNavItems: Array<{
    id: 'overview' | 'messages' | 'support' | 'devices' | 'history' | 'surveys' | 'account';
    label: string;
  }> = [
    { id: 'overview', label: 'Overview' },
    { id: 'messages', label: isLitePortal ? 'Notifications' : 'Messages' },
    { id: 'support', label: 'Support Request' },
    { id: 'devices', label: 'Devices' },
    { id: 'history', label: 'Service History' },
    { id: 'surveys', label: 'Surveys' },
    { id: 'account', label: 'Account' }
  ];
  const navItems = isLitePortal
    ? baseNavItems.filter(item => ['support', 'devices', 'messages', 'account'].includes(item.id))
    : baseNavItems;

  const tabMeta: Record<
    typeof activePortalTab,
    { title: string; subtitle: string }
  > = {
    overview: {
      title: 'Customer Portal',
      subtitle: `Welcome back, ${portalUserDisplayName}.`
    },
    messages: {
      title: isLitePortal ? 'Notifications' : 'Messages',
      subtitle: isLitePortal
        ? 'Review service notices for your account and devices.'
        : 'Keep in touch with your support team.'
    },
    support: {
      title: 'Support Request',
      subtitle: 'Submit a new service request.'
    },
    devices: {
      title: 'Devices',
      subtitle: 'View and manage your registered devices.'
    },
    history: {
      title: 'Service History',
      subtitle: 'Review completed service events and visit summaries.'
    },
    surveys: {
      title: 'Surveys',
      subtitle: 'Complete customer satisfaction surveys tied to service visits.'
    },
    account: {
      title: 'Account',
      subtitle: 'Update contact details, set display preferences, manage your password, and control portal access.'
    }
  };

  const handleDownloadPortalAttachment = async (messageId: string, attachment: PortalMessageAttachment) => {
    if (!messageId || !attachment?.id) return;
    try {
      await messageApi.downloadAttachment(messageId, attachment.id, attachment.name);
    } catch (error) {
      console.error('Failed to download portal message attachment:', error);
    }
  };

  const handleOpenPortalNotificationAttachment = async (
    notification: AppNotification,
    attachment: AppNotificationAttachment
  ) => {
    if (!notification?.id || !attachment?.id) return;
    try {
      await notificationApi.openAttachment(notification.id, attachment.id, attachment.name);
    } catch (error) {
      console.error('Failed to open portal notification attachment:', error);
    }
  };

  const renderAccount = () => {
    const previewTimestamp = new Date().toISOString();
    const notice = accountPreferencesNotice
      ? accountPreferencesNotice
      : portalSettingsError
      ? { type: 'error' as const, message: portalSettingsError }
      : null;
    const profileNotice = accountProfileNotice
      ? accountProfileNotice
      : accountProfileError
      ? { type: 'error' as const, message: accountProfileError }
      : null;
    const emailChangePending =
      normalizePortalProfileEmail(accountProfileForm.email) !== normalizePortalProfileEmail(accountProfile?.email);
    const inconsistentFields = accountProfile
      ? [
          !accountProfile.fieldConsistency.fullName ? 'full name' : '',
          !accountProfile.fieldConsistency.email ? 'email' : '',
          !accountProfile.fieldConsistency.phone ? 'phone' : '',
          !accountProfile.fieldConsistency.role ? 'title' : ''
        ].filter(Boolean)
      : [];
    const accountWorkspaceTabs: Array<{
      id: 'profile' | 'preferences' | 'support' | 'security' | 'access';
      label: string;
      value: string;
      meta: string;
    }> = [
      {
        id: 'profile',
        label: 'Profile',
        value: accountProfile?.fullName || portalUserDisplayName,
        meta: accountProfile?.email || 'Update contact details'
      },
      {
        id: 'preferences',
        label: 'Display',
        value: `${effectivePortalSettings.currency} - ${effectivePortalSettings.dateFormat}`,
        meta: getPortalUnitLabel(effectivePortalSettings.units)
      },
      {
        id: 'security',
        label: 'Security',
        value: 'Password',
        meta: 'Change your sign-in password'
      },
      {
        id: 'support',
        label: 'Support',
        value: 'My tickets',
        meta: 'Review requests and replies'
      },
      {
        id: 'access',
        label: 'Access',
        value: 'Portal active',
        meta: 'Control portal access only'
      }
    ];

    return (
      <div style={{ display: 'grid', gap: '1rem' }}>
        <div style={styles.card}>
          <div style={styles.sublabel}>Choose a section below to update your account without leaving the portal.</div>
          <div style={styles.accountWorkspaceNav}>
            {accountWorkspaceTabs.map(tab => {
              const isActive = tab.id === accountWorkspaceTab;
              return (
                <button
                  key={tab.id}
                  type='button'
                  style={{
                    ...styles.accountWorkspaceNavButton,
                    ...(isActive ? styles.accountWorkspaceNavButtonActive : {})
                  }}
                  onClick={() => setAccountWorkspaceTab(tab.id)}
                >
                  <div
                    style={{
                      ...styles.accountWorkspaceNavLabel,
                      color: isActive ? '#f6efe4' : BRAND_MUTED
                    }}
                  >
                    {tab.label}
                  </div>
                  <div
                    style={{
                      ...styles.accountWorkspaceNavValue,
                      color: isActive ? '#ffffff' : BRAND_TEXT
                    }}
                  >
                    {tab.value}
                  </div>
                  <div
                    style={{
                      ...styles.accountWorkspaceNavMeta,
                      color: isActive ? '#dce7e5' : BRAND_MUTED
                    }}
                  >
                    {tab.meta}
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {accountWorkspaceTab === 'support' && (
          <div style={styles.card}><MySupportTicketsPanel /></div>
        )}

        {accountWorkspaceTab === 'preferences' && notice && (
          <div
            style={{
              ...styles.supportInsetCard,
              borderColor: notice.type === 'error' ? '#f0c9c2' : '#bfd8cb',
              backgroundColor: notice.type === 'error' ? '#fff7f4' : '#f3fbf5',
              color: notice.type === 'error' ? '#9f3f2d' : '#1b5e48'
            }}
          >
            {notice.message}
          </div>
        )}

        {accountWorkspaceTab === 'profile' && (
          <div style={styles.card}>
          <div style={styles.supportSectionHeader}>
            <div style={styles.supportSectionBadge}>Profile</div>
            <h2 style={styles.supportSectionTitle}>Contact details</h2>
            <p style={styles.supportSectionSubtitle}>
              Update the customer contact record linked to this portal login. Your username stays the same.
            </p>
          </div>

          {profileNotice && (
            <div
              style={{
                ...styles.supportInsetCard,
                borderColor: profileNotice.type === 'error' ? '#f0c9c2' : '#bfd8cb',
                backgroundColor: profileNotice.type === 'error' ? '#fff7f4' : '#f3fbf5',
                color: profileNotice.type === 'error' ? '#9f3f2d' : '#1b5e48'
              }}
            >
              {profileNotice.message}
            </div>
          )}

          {accountProfileLoading && !accountProfile ? (
            <div style={styles.supportInsetCard}>Loading your linked contact details...</div>
          ) : !accountProfile ? (
            <>
              <div style={styles.supportInsetCard}>
                We could not load a linked customer contact for this portal account.
              </div>
              <div style={styles.buttonGroup}>
                <button
                  type='button'
                  style={{ ...styles.button, ...styles.secondaryButton }}
                  onClick={loadAccountProfile}
                  disabled={accountProfileLoading}
                >
                  {accountProfileLoading ? 'Reloading...' : 'Reload profile'}
                </button>
              </div>
            </>
          ) : (
            <>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
                  gap: '1rem'
                }}
              >
                <div style={{ display: 'grid', gap: '0.45rem' }}>
                  <div style={styles.supportInsetCard}>
                    <div style={styles.supportInsetLabel}>Username</div>
                    <div>{accountProfile.username}</div>
                  </div>
                  <div style={styles.sublabel}>
                    This is your sign-in name. Contact detail updates below do not change your username.
                  </div>
                </div>

                <div style={{ display: 'grid', gap: '0.45rem' }}>
                  <div style={styles.supportInsetCard}>
                    <div style={styles.supportInsetLabel}>Linked contact records</div>
                    <div>{accountProfile.linkedContacts.length}</div>
                    {!accountProfile.updatesApplyToAllLinkedContacts && accountProfile.linkedContacts[0] && (
                      <div style={styles.sublabel}>
                        {accountProfile.linkedContacts[0].locationName || 'Customer-wide contact'}
                      </div>
                    )}
                  </div>
                  <div style={styles.sublabel}>
                    {accountProfile.updatesApplyToAllLinkedContacts
                      ? 'Saving applies the same identity details to every contact record linked to this login.'
                      : 'Changes update your linked customer contact record directly.'}
                  </div>
                  {!accountProfile.updatesApplyToAllLinkedContacts && accountProfile.linkedContacts[0] && (
                    <div style={styles.sublabel}>
                      Linked contact scope: {accountProfile.linkedContacts[0].locationName || 'Customer-wide contact'}.
                    </div>
                  )}
                </div>
              </div>

              {inconsistentFields.length > 0 && (
                <div
                  style={{
                    ...styles.supportInsetCard,
                    borderColor: '#ead9b1',
                    backgroundColor: '#fffaf0',
                    color: '#7a5a14'
                  }}
                >
                  Linked records currently differ for {inconsistentFields.join(', ')}. Saving will normalize those values.
                </div>
              )}

              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
                  gap: '1rem'
                }}
              >
                <div style={styles.formGroup}>
                  <label style={styles.label}>Full name</label>
                  <input
                    type='text'
                    style={styles.input}
                    value={accountProfileForm.fullName}
                    onChange={event => handleAccountProfileFieldChange('fullName', event.target.value)}
                    disabled={accountProfileSaving}
                    autoComplete='name'
                  />
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label}>Email</label>
                  <input
                    type='email'
                    style={styles.input}
                    value={accountProfileForm.email}
                    onChange={event => handleAccountProfileFieldChange('email', event.target.value)}
                    disabled={accountProfileSaving}
                    autoComplete='email'
                  />
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label}>Role / title</label>
                  <input
                    type='text'
                    style={styles.input}
                    value={accountProfileForm.role}
                    onChange={event => handleAccountProfileFieldChange('role', event.target.value)}
                    disabled={accountProfileSaving}
                    autoComplete='organization-title'
                  />
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label}>Phone</label>
                  <input
                    type='tel'
                    style={styles.input}
                    value={accountProfileForm.phone}
                    onChange={event => handleAccountProfileFieldChange('phone', event.target.value)}
                    disabled={accountProfileSaving}
                    autoComplete='tel'
                  />
                </div>
              </div>

              {emailChangePending && (
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
                    gap: '1rem'
                  }}
                >
                  <div style={styles.formGroup}>
                    <label style={styles.label}>Confirm new email</label>
                    <input
                      type='email'
                      style={styles.input}
                      value={accountProfileForm.confirmEmail}
                      onChange={event => handleAccountProfileFieldChange('confirmEmail', event.target.value)}
                      disabled={accountProfileSaving}
                      autoComplete='email'
                    />
                  </div>

                  <div style={styles.formGroup}>
                    <label style={styles.label}>Current password</label>
                    <input
                      type='password'
                      style={styles.input}
                      value={accountProfileForm.currentPassword}
                      onChange={event => handleAccountProfileFieldChange('currentPassword', event.target.value)}
                      disabled={accountProfileSaving}
                      autoComplete='current-password'
                    />
                  </div>
                </div>
              )}

              <div style={styles.sublabel}>
                {emailChangePending
                  ? 'Changing your email also updates the email on the linked portal login and requires your current password.'
                  : 'Name, title, and phone update your linked customer contact directly.'}
              </div>

              {accountProfile.linkedContacts.length > 1 && (
              <div style={{ display: 'grid', gap: '0.75rem' }}>
                <div style={styles.supportInsetLabel}>Linked customer contacts</div>
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
                    gap: '0.75rem'
                  }}
                >
                  {accountProfile.linkedContacts.map(contact => (
                    <div key={contact.accessId} style={styles.supportInsetCard}>
                      <div style={styles.supportInsetLabel}>{contact.customerName}</div>
                      <div>{contact.locationName || 'Customer-wide contact'}</div>
                      <div style={styles.sublabel}>
                        {contact.isPrimary ? 'Primary contact' : 'Additional contact'} -{' '}
                        {contact.isActive ? 'Contact active' : 'Contact inactive'}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              )}

              <div style={styles.buttonGroup}>
                <button
                  type='button'
                  style={{ ...styles.button, ...styles.secondaryButton }}
                  onClick={loadAccountProfile}
                  disabled={accountProfileLoading || accountProfileSaving}
                >
                  {accountProfileLoading ? 'Reloading...' : 'Reload profile'}
                </button>
                <button
                  type='button'
                  style={{ ...styles.button, ...styles.primaryButton }}
                  onClick={handleSaveAccountProfile}
                  disabled={accountProfileLoading || accountProfileSaving}
                >
                  {accountProfileSaving ? 'Saving...' : 'Save contact details'}
                </button>
              </div>
            </>
          )}
        </div>
        )}

        {accountWorkspaceTab === 'preferences' && (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
            gap: '1rem',
            alignItems: 'start'
          }}
        >
          <div style={styles.card}>
            <div style={styles.supportSectionHeader}>
              <div style={styles.supportSectionBadge}>Display</div>
              <h2 style={styles.supportSectionTitle}>Portal preferences</h2>
              <p style={styles.supportSectionSubtitle}>
                These settings change how dates, prices, and measurement labels are shown inside your portal only.
              </p>
            </div>

            {portalSettingsLoading && !portalSettings ? (
              <div style={styles.supportInsetCard}>Loading your current preferences...</div>
            ) : (
              <>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Currency</label>
                  <select
                    style={styles.select}
                    value={accountPreferences.currency}
                    onChange={event => handleAccountPreferenceChange('currency', event.target.value)}
                    disabled={accountPreferencesSaving}
                  >
                    <option value=''>Use organization default ({globalPortalSettings.currency})</option>
                    {PORTAL_CURRENCY_OPTIONS.map(option => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </select>
                  <div style={styles.sublabel}>Used for quote totals and other money display in the portal.</div>
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label}>Units</label>
                  <select
                    style={styles.select}
                    value={accountPreferences.units}
                    onChange={event => handleAccountPreferenceChange('units', event.target.value)}
                    disabled={accountPreferencesSaving}
                  >
                    <option value=''>Use organization default ({getPortalUnitLabel(globalPortalSettings.units)})</option>
                    {PORTAL_UNIT_OPTIONS.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                  <div style={styles.sublabel}>Used wherever the portal shows measurement-system labels.</div>
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label}>Date format</label>
                  <select
                    style={styles.select}
                    value={accountPreferences.dateFormat}
                    onChange={event => handleAccountPreferenceChange('dateFormat', event.target.value)}
                    disabled={accountPreferencesSaving}
                  >
                    <option value=''>Use organization default ({globalPortalSettings.dateFormat})</option>
                    {PORTAL_DATE_OPTIONS.map(option => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </select>
                  <div style={styles.sublabel}>Applied to service history, messages, quote timestamps, and device dates.</div>
                </div>

                <div style={styles.buttonGroup}>
                  <button
                    type='button'
                    style={{ ...styles.button, ...styles.secondaryButton }}
                    onClick={loadPortalSettings}
                    disabled={portalSettingsLoading || accountPreferencesSaving}
                  >
                    {portalSettingsLoading ? 'Reloading...' : 'Reload saved values'}
                  </button>
                  <button
                    type='button'
                    style={{ ...styles.button, ...styles.primaryButton }}
                    onClick={handleSaveAccountPreferences}
                    disabled={portalSettingsLoading || accountPreferencesSaving}
                  >
                    {accountPreferencesSaving ? 'Saving...' : 'Save preferences'}
                  </button>
                </div>
              </>
            )}
          </div>

          <div style={styles.card}>
            <div style={styles.supportSectionHeader}>
              <div style={styles.supportSectionBadge}>Preview</div>
              <h2 style={styles.supportSectionTitle}>Effective display</h2>
              <p style={styles.supportSectionSubtitle}>
                Changes apply immediately after saving and update the values shown throughout the portal.
              </p>
            </div>

            <div style={styles.supportInsetCard}>
              <div style={styles.supportInsetLabel}>Date</div>
              <div>{formatPortalDate(previewTimestamp)}</div>
            </div>

            <div style={styles.supportInsetCard}>
              <div style={styles.supportInsetLabel}>Date and time</div>
              <div>{formatPortalDateTime(previewTimestamp)}</div>
            </div>

            <div style={styles.supportInsetCard}>
              <div style={styles.supportInsetLabel}>Currency</div>
              <div>{formatPortalCurrency(1234.56)}</div>
            </div>

            <div style={styles.supportInsetCard}>
              <div style={styles.supportInsetLabel}>Units</div>
              <div>{getPortalUnitLabel(effectivePortalSettings.units)}</div>
            </div>

            <div style={styles.supportInsetCard}>
              <div style={styles.supportInsetLabel}>Language</div>
              <div>English only for now</div>
            </div>
          </div>
        </div>
        )}

        {accountWorkspaceTab === 'security' && (
        <div style={styles.card}>
          <div style={styles.supportSectionHeader}>
            <div style={styles.supportSectionBadge}>Security</div>
            <h2 style={styles.supportSectionTitle}>Change password</h2>
            <p style={styles.supportSectionSubtitle}>
              Update your portal password while staying signed in. Your current password is required before a new one is saved.
            </p>
          </div>

          {passwordNotice && (
            <div
              style={{
                ...styles.supportInsetCard,
                borderColor: passwordNotice.type === 'error' ? '#f0c9c2' : '#bfd8cb',
                backgroundColor: passwordNotice.type === 'error' ? '#fff7f4' : '#f3fbf5',
                color: passwordNotice.type === 'error' ? '#9f3f2d' : '#1b5e48'
              }}
            >
              {passwordNotice.message}
            </div>
          )}

          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
              gap: '1rem'
            }}
          >
            <div style={styles.formGroup}>
              <label style={styles.label}>Current password</label>
              <input
                type='password'
                autoComplete='current-password'
                style={styles.input}
                value={passwordForm.currentPassword}
                onChange={event => handlePasswordFieldChange('currentPassword', event.target.value)}
                disabled={passwordSaving}
              />
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>New password</label>
              <input
                type='password'
                autoComplete='new-password'
                style={styles.input}
                value={passwordForm.newPassword}
                onChange={event => handlePasswordFieldChange('newPassword', event.target.value)}
                disabled={passwordSaving}
              />
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>Confirm new password</label>
              <input
                type='password'
                autoComplete='new-password'
                style={styles.input}
                value={passwordForm.confirmPassword}
                onChange={event => handlePasswordFieldChange('confirmPassword', event.target.value)}
                disabled={passwordSaving}
              />
            </div>
          </div>

          <div style={styles.sublabel}>Minimum 10 characters. The new password must be different from the current password.</div>

          <div style={styles.buttonGroup}>
            <button
              type='button'
              style={{ ...styles.button, ...styles.primaryButton }}
              onClick={handleChangePassword}
              disabled={passwordSaving}
            >
              {passwordSaving ? 'Updating...' : 'Update password'}
            </button>
          </div>
        </div>
        )}

        {accountWorkspaceTab === 'access' && (
        <div style={styles.card}>
          <div style={styles.supportSectionHeader}>
            <div style={styles.supportSectionBadge}>Access</div>
            <h2 style={styles.supportSectionTitle}>Deactivate portal access</h2>
            <p style={styles.supportSectionSubtitle}>
              This disables only your portal sign-in. Your service history, cases, messages, and customer records stay in the system.
            </p>
          </div>

          {deactivateNotice && (
            <div
              style={{
                ...styles.supportInsetCard,
                borderColor: deactivateNotice.type === 'error' ? '#f0c9c2' : '#bfd8cb',
                backgroundColor: deactivateNotice.type === 'error' ? '#fff7f4' : '#f3fbf5',
                color: deactivateNotice.type === 'error' ? '#9f3f2d' : '#1b5e48'
              }}
            >
              {deactivateNotice.message}
            </div>
          )}

          <div style={{ display: 'grid', gap: '0.35rem' }}>
            <div style={styles.supportInsetLabel}>What happens next</div>
            <div style={styles.sublabel}>
              You will be signed out immediately. A technician or admin can re-enable your portal access later from the customer management side.
            </div>
          </div>

          <div style={styles.formGroup}>
            <label style={styles.label}>Type DEACTIVATE to confirm</label>
            <input
              type='text'
              style={styles.input}
              value={deactivateConfirmText}
              onChange={event => {
                setDeactivateNotice(null);
                setDeactivateConfirmText(event.target.value);
              }}
              disabled={deactivateSubmitting}
              autoComplete='off'
              spellCheck={false}
            />
          </div>

          <div style={styles.buttonGroup}>
            <button
              type='button'
              style={{
                ...styles.button,
                backgroundColor: '#fff7f4',
                color: '#9f3f2d',
                borderColor: '#f0c9c2'
              }}
              onClick={handleDeactivateOwnAccount}
              disabled={deactivateSubmitting || deactivateConfirmText.trim().toUpperCase() !== 'DEACTIVATE'}
            >
              {deactivateSubmitting ? 'Deactivating...' : 'Deactivate portal access'}
            </button>
          </div>
        </div>
        )}
      </div>
    );
  };

  const renderOverview = () => {
    return (
      <>
        <div style={styles.overviewCardGrid}>
          <button
            type='button'
            style={{ ...styles.overviewCard, ...styles.overviewCardButton }}
            onClick={() => setActiveOverviewQueue('surveys')}
          >
            <div style={styles.overviewCardLabel}>Surveys waiting response</div>
            <div style={styles.overviewCardValue}>{portalSurveysLoading ? '...' : pendingPortalSurveyCount}</div>
            <div style={styles.overviewCardMeta}>
              {portalSurveysLoading
                ? 'Loading feedback requests...'
                : portalSurveysError
                ? portalSurveysError
                : pendingPortalSurveyCount > 0
                ? `${pendingPortalSurveyCount} survey${pendingPortalSurveyCount === 1 ? '' : 's'} awaiting response`
                : 'No surveys waiting for feedback'}
            </div>
          </button>
          <button
            type='button'
            style={{ ...styles.overviewCard, ...styles.overviewCardButton }}
            onClick={() => setActiveOverviewQueue('schedules')}
          >
            <div style={styles.overviewCardLabel}>Schedules to review</div>
            <div style={styles.overviewCardValue}>{portalCasesLoading ? '...' : overviewScheduleReviewCases.length}</div>
            <div style={styles.overviewCardMeta}>
              {portalCasesLoading
                ? 'Loading visit windows...'
                : portalCasesError
                ? portalCasesError
                : overviewScheduleReviewCases.length > 0
                ? `${overviewScheduleReviewCases.length} case${overviewScheduleReviewCases.length === 1 ? '' : 's'} with proposed or changed visit times`
                : 'No visit schedules need review'}
            </div>
          </button>
          <button
            type='button'
            style={{ ...styles.overviewCard, ...styles.overviewCardButton }}
            onClick={() => setActiveOverviewQueue('quotes')}
          >
            <div style={styles.overviewCardLabel}>Quotes needing review</div>
            <div style={styles.overviewCardValue}>{portalCasesLoading ? '...' : overviewQuoteReviewCases.length}</div>
            <div style={styles.overviewCardMeta}>
              {portalCasesLoading
                ? 'Loading quote activity...'
                : portalCasesError
                ? portalCasesError
                : overviewQuoteReviewCases.length > 0
                ? `${overviewQuoteReviewCases.length} case${overviewQuoteReviewCases.length === 1 ? '' : 's'} have quotes waiting on a decision`
                : 'No quotes are waiting for a response'}
            </div>
          </button>
          <button
            type='button'
            style={{ ...styles.overviewCard, ...styles.overviewCardButton }}
            onClick={() => setActiveOverviewQueue('open')}
          >
            <div style={styles.overviewCardLabel}>Open jobs</div>
            <div style={styles.overviewCardValue}>{portalCasesLoading ? '...' : openRequestsCount}</div>
            <div style={styles.overviewCardMeta}>
              {portalCasesLoading
                ? 'Loading service work...'
                : portalCasesError
                ? portalCasesError
                : openRequestsCount > 0
                ? `${openRequestsCount} active request${openRequestsCount === 1 ? '' : 's'} still in progress`
                : 'No open service work right now'}
            </div>
          </button>
        </div>

        <div style={styles.overviewColumn}>
          <div style={styles.overviewSectionTitle}>Recent activity</div>
          <div style={styles.overviewSectionText}>Latest 10 customer-visible updates across your service cases.</div>
          <div style={styles.overviewActivityList}>
            {portalCasesLoading && (
              <div style={styles.overviewEmpty}>Loading service activity...</div>
            )}
            {!portalCasesLoading && portalCasesError && (
              <div style={styles.overviewEmpty}>{portalCasesError}</div>
            )}
            {!portalCasesLoading && !portalCasesError && sortedPortalCases.length === 0 && (
              <div style={styles.overviewEmpty}>No recent service activity yet.</div>
            )}
            {!portalCasesLoading &&
              !portalCasesError &&
              recentActivityItems.slice(0, 10).map(activity => {
                const { entry, event, quoteDecision } = activity;
                const caseLabel = entry.caseNumber || entry.caseCode || entry.id;
                const resolvedDevice = resolveCaseDevice(entry);
                const statusStyle = getStatusBadgeStyles(entry.status);
                const deviceLabel =
                  resolvedDevice?.modelNumber ||
                  resolvedDevice?.model?.modelNumber ||
                  resolvedDevice?.serialNumber ||
                  'Unknown device';
                const dateLabel = formatPortalDateTime(
                  event?.occurredAt || entry.updatedAt || entry.closedAt || entry.createdAt
                );
                const eventLabel = event ? formatTimelineEventLabel(event) : 'Case updated';
                const quoteBadge = quoteDecision ? quoteDecisionBadge(quoteDecision) : null;
                return (
                  <button
                    key={entry.id}
                    type='button'
                    style={styles.overviewActivityButton}
                    onClick={() => openOverviewCaseModal(entry.id)}
                  >
                    <div style={styles.overviewActivityItem}>
                      <div style={styles.overviewActivityHeader}>
                        <div style={styles.overviewActivityTitle}>Case {caseLabel}</div>
                        <span
                          style={{
                            ...styles.overviewActivityStatus,
                            backgroundColor: statusStyle.backgroundColor,
                            color: statusStyle.color,
                            borderColor: statusStyle.backgroundColor
                          }}
                        >
                          {formatPortalCaseStatusLabel(entry.status, 'Pending')}
                        </span>
                      </div>
                      <div style={styles.overviewActivityMeta}>{deviceLabel}</div>
                      <div style={styles.overviewActivityMeta}>{eventLabel}</div>
                      <div style={styles.overviewActivityMeta}>Updated {dateLabel}</div>
                      {quoteBadge && <span style={quoteBadge.style}>{quoteBadge.label}</span>}
                    </div>
                  </button>
                );
              })}
          </div>
        </div>
      </>
    );
  };

  const renderOverviewQueueModal = () => {
    if (!activeOverviewQueue) return null;

    const queueTitle =
      activeOverviewQueue === 'surveys'
        ? 'Surveys waiting response'
        : activeOverviewQueue === 'schedules'
        ? 'Schedules to review'
        : activeOverviewQueue === 'quotes'
        ? 'Quotes needing review'
        : 'Open jobs';
    const queueSubtitle =
      activeOverviewQueue === 'surveys'
        ? 'Open any case-linked feedback request from here without leaving overview.'
        : activeOverviewQueue === 'schedules'
        ? 'Review planned visits and schedule changes that still need attention.'
        : activeOverviewQueue === 'quotes'
        ? 'Open a case to review the latest customer-visible quote activity.'
        : 'These service cases are still active and can be opened directly from overview.';
    const pendingQuoteBadge = quoteDecisionBadge('PENDING');

    return (
      <div style={styles.historyOverlay} role='dialog' aria-modal='true' aria-label={queueTitle}>
        <div
          style={{
            ...styles.deviceModalPanel,
            maxWidth: '920px',
            maxHeight: '86vh'
          }}
          onClick={event => event.stopPropagation()}
        >
          <div style={styles.deviceModalHeader}>
            <div>
              <div style={styles.deviceModalTitle}>{queueTitle}</div>
              <div style={styles.deviceModalSubtitle}>{queueSubtitle}</div>
            </div>
            <button
              type='button'
              style={styles.historyClose}
              onClick={() => setActiveOverviewQueue(null)}
              aria-label={`Close ${queueTitle}`}
            >
              Close
            </button>
          </div>

          {activeOverviewQueue === 'surveys' && (
            <>
              {portalSurveysLoading && <div style={styles.overviewEmpty}>Loading surveys...</div>}
              {!portalSurveysLoading && portalSurveysError && <div style={styles.overviewEmpty}>{portalSurveysError}</div>}
              {!portalSurveysLoading && !portalSurveysError && availablePortalSurveys.length === 0 && (
                <div style={styles.overviewEmpty}>No surveys are waiting for feedback right now.</div>
              )}
              {!portalSurveysLoading && !portalSurveysError && availablePortalSurveys.length > 0 && (
                <div style={styles.deviceHistoryList}>
                  {availablePortalSurveys.map(survey => (
                    <button
                      key={survey.id}
                      type='button'
                      style={styles.historyCardButton}
                      onClick={() => openOverviewSurveyModal(survey.id)}
                    >
                      <div style={styles.historyCard}>
                        <div style={styles.historySummaryLayout}>
                          <div style={styles.historySummaryMain}>
                            <div style={styles.historyTitle}>Case {getPortalSurveyCaseLabel(survey.case)}</div>
                            <div style={styles.historySummarySnippet}>
                              {survey.surveyName} | {getPortalSurveyWorkTypeLabel(survey.case)}
                            </div>
                          </div>

                          <div style={styles.historySummaryDeviceColumn}>
                            <div style={styles.historySummaryDeviceName}>{getPortalSurveyDeviceLabel(survey.case)}</div>
                            <div style={styles.historySummaryDeviceMeta}>
                              Released {formatPortalDateTime(survey.releasedAt || undefined)}
                            </div>
                            <div style={styles.historySummaryDeviceMeta}>
                              Technician {survey.technicianName || 'Support team'}
                            </div>
                          </div>

                          <div style={styles.historySummaryMetaGrid}>
                            <div style={styles.historySummaryMetaCard}>
                              <div style={styles.historyDetailLabel}>Status</div>
                              <div style={styles.historyActionRow}>
                                <span style={{ ...styles.surveyStatusBadge, ...styles.surveyStatusAvailable }}>
                                  Pending
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </>
          )}

          {activeOverviewQueue === 'schedules' && (
            <>
              {portalCasesLoading && <div style={styles.overviewEmpty}>Loading schedule activity...</div>}
              {!portalCasesLoading && portalCasesError && <div style={styles.overviewEmpty}>{portalCasesError}</div>}
              {!portalCasesLoading && !portalCasesError && overviewScheduleReviewCases.length === 0 && (
                <div style={styles.overviewEmpty}>No schedules need review right now.</div>
              )}
              {!portalCasesLoading && !portalCasesError && overviewScheduleReviewCases.length > 0 && (
                <div style={styles.deviceHistoryList}>
                  {overviewScheduleReviewCases.map(({ entry, schedule }) => {
                    const windowLabel = schedule.displayStartAt
                      ? schedule.displayEndAt
                        ? `${formatPortalDateTime(schedule.displayStartAt.toISOString())} to ${formatPortalDateTime(schedule.displayEndAt.toISOString())}`
                        : formatPortalDateTime(schedule.displayStartAt.toISOString())
                      : 'No visit window on file';
                    const plannerBadgeStyle =
                      schedule.hasScheduleRequest
                        ? {
                            ...styles.quoteDecisionPill,
                            borderColor: '#f0c36d',
                            backgroundColor: '#fff5de',
                            color: '#8a5a00'
                          }
                        : {
                            ...styles.quoteDecisionPill,
                            borderColor: '#b9d8ff',
                            backgroundColor: '#eef6ff',
                            color: '#1d4ed8'
                          };

                    return (
                      <button
                        key={entry.id}
                        type='button'
                        style={styles.historyCardButton}
                        onClick={() => openOverviewCaseModal(entry.id)}
                      >
                        <div style={styles.historyCard}>
                          <div style={styles.historySummaryLayout}>
                            <div style={styles.historySummaryMain}>
                              <div style={styles.historyTitle}>Case {getCaseDisplay(entry)}</div>
                              <div style={styles.historySummarySnippet}>{getPortalCaseIssuePreview(entry.problemDescription)}</div>
                            </div>

                            <div style={styles.historySummaryDeviceColumn}>
                              <div style={styles.historySummaryDeviceName}>{getPortalCaseDeviceLabel(entry)}</div>
                              <div style={styles.historySummaryDeviceMeta}>{windowLabel}</div>
                              <div style={styles.historySummaryDeviceMeta}>
                                Duration {schedule.durationLabel}
                              </div>
                            </div>

                            <div style={styles.historySummaryMetaGrid}>
                              <div style={styles.historySummaryMetaCard}>
                                <div style={styles.historyDetailLabel}>Schedule</div>
                                <div style={styles.historyActionRow}>
                                  <span style={plannerBadgeStyle}>{schedule.plannerStatusLabel}</span>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </>
          )}

          {activeOverviewQueue === 'quotes' && (
            <>
              {portalCasesLoading && <div style={styles.overviewEmpty}>Loading quote activity...</div>}
              {!portalCasesLoading && portalCasesError && <div style={styles.overviewEmpty}>{portalCasesError}</div>}
              {!portalCasesLoading && !portalCasesError && overviewQuoteReviewCases.length === 0 && (
                <div style={styles.overviewEmpty}>No quotes are waiting for review right now.</div>
              )}
              {!portalCasesLoading && !portalCasesError && overviewQuoteReviewCases.length > 0 && (
                <div style={styles.deviceHistoryList}>
                  {overviewQuoteReviewCases.map(({ entry, event }) => {
                    const quoteMeta = resolveQuoteEventMeta(event);
                    return (
                      <button
                        key={entry.id}
                        type='button'
                        style={styles.historyCardButton}
                        onClick={() => openOverviewCaseModal(entry.id)}
                      >
                        <div style={styles.historyCard}>
                          <div style={styles.historySummaryLayout}>
                            <div style={styles.historySummaryMain}>
                              <div style={styles.historyTitle}>Case {getCaseDisplay(entry)}</div>
                              <div style={styles.historySummarySnippet}>
                                {quoteMeta.title || 'Service quote'} | {getPortalCaseIssuePreview(entry.problemDescription)}
                              </div>
                            </div>

                            <div style={styles.historySummaryDeviceColumn}>
                              <div style={styles.historySummaryDeviceName}>{getPortalCaseDeviceLabel(entry)}</div>
                              <div style={styles.historySummaryDeviceMeta}>
                                Shared {formatPortalDateTime(event?.occurredAt || entry.updatedAt)}
                              </div>
                              <div style={styles.historySummaryDeviceMeta}>Open the case to approve, deny, or request changes.</div>
                            </div>

                            <div style={styles.historySummaryMetaGrid}>
                              <div style={styles.historySummaryMetaCard}>
                                <div style={styles.historyDetailLabel}>Quote</div>
                                <div style={styles.historyActionRow}>
                                  <span style={pendingQuoteBadge.style}>{pendingQuoteBadge.label}</span>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </>
          )}

          {activeOverviewQueue === 'open' && (
            <>
              {portalCasesLoading && <div style={styles.overviewEmpty}>Loading active cases...</div>}
              {!portalCasesLoading && portalCasesError && <div style={styles.overviewEmpty}>{portalCasesError}</div>}
              {!portalCasesLoading && !portalCasesError && overviewOpenCases.length === 0 && (
                <div style={styles.overviewEmpty}>No active service cases right now.</div>
              )}
              {!portalCasesLoading && !portalCasesError && overviewOpenCases.length > 0 && (
                <div style={styles.deviceHistoryList}>
                  {overviewOpenCases.map(entry => {
                    const statusStyle = getStatusBadgeStyles(entry.status);
                    return (
                      <button
                        key={entry.id}
                        type='button'
                        style={styles.historyCardButton}
                        onClick={() => openOverviewCaseModal(entry.id)}
                      >
                        <div style={styles.historyCard}>
                          <div style={styles.historySummaryLayout}>
                            <div style={styles.historySummaryMain}>
                              <div style={styles.historyTitle}>Case {getCaseDisplay(entry)}</div>
                              <div style={styles.historySummarySnippet}>{getPortalCaseIssuePreview(entry.problemDescription)}</div>
                            </div>

                            <div style={styles.historySummaryDeviceColumn}>
                              <div style={styles.historySummaryDeviceName}>{getPortalCaseDeviceLabel(entry)}</div>
                              <div style={styles.historySummaryDeviceMeta}>
                                Updated {formatPortalDateTime(entry.updatedAt || entry.createdAt)}
                              </div>
                              <div style={styles.historySummaryDeviceMeta}>
                                Opened {formatPortalDate(entry.createdAt)}
                              </div>
                            </div>

                            <div style={styles.historySummaryMetaGrid}>
                              <div style={styles.historySummaryMetaCard}>
                                <div style={styles.historyDetailLabel}>Status</div>
                                <div style={styles.historyActionRow}>
                                  <span
                                    style={{
                                      ...styles.historyStatusPill,
                                      backgroundColor: statusStyle.backgroundColor,
                                      color: statusStyle.color,
                                      borderColor: statusStyle.backgroundColor
                                    }}
                                  >
                                    {formatPortalCaseStatusLabel(entry.status, 'Pending')}
                                  </span>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    );
  };


  const renderMessages = () => {
    const selectedDevice = messageDeviceId ? deviceById[messageDeviceId] : undefined;
    const selectedCase = messageCaseId
      ? sortedPortalCases.find(entry => entry.id === messageCaseId)
      : undefined;
    const composeCustomerId = resolveMessageCustomerId({
      focusType: messageFocusType,
      caseId: messageFocusType === 'case' ? messageCaseId : null,
      deviceId: messageFocusType === 'device' ? messageDeviceId : null
    });
    const focusSummary =
      messageFocusType === 'device'
        ? selectedDevice
          ? `Device - ${getDeviceLabel(selectedDevice)}`
          : 'Select a device'
        : messageFocusType === 'case'
        ? selectedCase
          ? `Case - ${getCaseDisplay(selectedCase)}`
          : 'Select a case'
        : 'General inquiry';
    const canSend =
      messageBody.trim().length > 0 &&
      (messageFocusType !== 'device' || !!selectedDevice) &&
      (messageFocusType !== 'case' || !!selectedCase) &&
      !!composeCustomerId &&
      !messageSending;
    const notificationUnreadCount = portalNotifications.filter(
      notification => !notification.readAt && !notification.dismissedAt
    ).length;
    const modeTabBase = {
      ...styles.messagesNewButton,
      borderColor: BRAND_BORDER,
      backgroundColor: BRAND_SURFACE,
      color: BRAND_TEXT
    };
    const modeTabActive = {
      ...modeTabBase,
      backgroundColor: BRAND_TEXT,
      borderColor: BRAND_TEXT,
      color: '#ffffff'
    };
    const modeTabs = isLitePortal ? null : (
      <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
        <button
          type="button"
          style={messageWorkspaceMode === 'messages' ? modeTabActive : modeTabBase}
          onClick={() => {
            setMessageWorkspaceMode('messages');
            setSelectedPortalNotificationId(null);
          }}
        >
          Messages {unreadPortalCount > notificationUnreadCount ? unreadPortalCount - notificationUnreadCount : 0}
        </button>
        <button
          type="button"
          style={messageWorkspaceMode === 'notifications' ? modeTabActive : modeTabBase}
          onClick={() => {
            setMessageWorkspaceMode('notifications');
            setShowComposePanel(false);
            setSelectedPortalMessageId(null);
          }}
        >
          Notifications {notificationUnreadCount}
        </button>
      </div>
    );

    const renderNotificationList = () => (
      <div style={styles.messagesSection}>
        <div style={styles.messagesSectionHeader}>
          <div>
            <div style={styles.messagesTitle}>Notifications</div>
            <div style={styles.messagesSubtitle}>Service notices for your account and registered devices.</div>
          </div>
        </div>
        {modeTabs}
        <div style={styles.messagesFilterRow}>
          <select
            style={styles.select}
            value={portalNotificationView}
            onChange={(event) => {
              setPortalNotificationView(event.target.value as 'active' | 'all' | 'dismissed');
              setSelectedPortalNotificationId(null);
            }}
          >
            <option value="active">Active</option>
            <option value="all">All</option>
            <option value="dismissed">Dismissed</option>
          </select>
        </div>
        {portalNotificationsLoading && <div style={styles.messagesEmpty}>Loading notifications...</div>}
        {!portalNotificationsLoading && portalNotifications.length === 0 && (
          <div style={styles.messagesEmpty}>
            {portalNotificationView === 'active' ? 'No active notifications.' : 'No notifications match this view.'}
          </div>
        )}
        {!portalNotificationsLoading && portalNotifications.length > 0 && (
          <div style={styles.messagesListScroll}>
            <div style={styles.messagesList}>
              {portalNotifications.map(notification => {
                const isUnread = !notification.readAt && !notification.dismissedAt;
                const isDismissed = Boolean(notification.dismissedAt);
                const attachmentsCount = Array.isArray(notification.attachments) ? notification.attachments.length : 0;
                return (
                  <button
                    key={notification.id}
                    type="button"
                    style={{
                      ...styles.messagesItem,
                      ...styles.messagesListButton,
                      ...(selectedPortalNotificationId === notification.id ? styles.messagesItemActive : {})
                    }}
                    onClick={() => {
                      setSelectedPortalNotificationId(notification.id);
                      if (isUnread) {
                        handleMarkPortalNotificationRead(notification.id);
                      }
                    }}
                  >
                    <div style={styles.messagesItemHeader}>
                      <div style={styles.messagesItemHeaderLeft}>
                        <div style={styles.messagesItemBadgeRow}>
                          <span style={{ ...styles.messagesSenderBadge, ...styles.messagesSenderBadgeSupport }}>
                            {notification.severity || 'info'}
                          </span>
                          {isUnread && <span style={styles.messagesUnreadBadge}>New</span>}
                          {isDismissed && <span style={styles.messagesStateBadge}>Dismissed</span>}
                        </div>
                        <div style={styles.messagesItemTitle}>{notification.title}</div>
                      </div>
                    </div>
                    <div style={styles.messagesItemBody}>{getMessagePreview(notification.body)}</div>
                    <div style={styles.messagesItemMetaRow}>
                      <div style={styles.messagesItemMeta}>
                        {formatPortalDateTime(notification.publishedAt || notification.createdAt || '')}
                      </div>
                      {attachmentsCount > 0 && (
                        <div style={styles.messagesItemMeta}>
                          {attachmentsCount} attachment{attachmentsCount === 1 ? '' : 's'}
                        </div>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>
    );

    if (isLitePortal || messageWorkspaceMode === 'notifications') {
      return (
        <div style={styles.messagesLayout}>
          {renderNotificationList()}
          {selectedPortalNotification && (
            <div
              style={styles.historyOverlay}
              role="dialog"
              aria-modal="true"
              aria-label="Notification details"
              onClick={() => setSelectedPortalNotificationId(null)}
            >
              <div style={styles.historyPanel} onClick={(event) => event.stopPropagation()}>
                <div style={styles.historyHeader}>
                  <div>
                    <div style={styles.historyModalTitle}>{selectedPortalNotification.title}</div>
                    <div style={styles.historySubtitle}>
                      {formatPortalDateTime(selectedPortalNotification.publishedAt || selectedPortalNotification.createdAt || '')}
                    </div>
                  </div>
                  <button type="button" style={styles.historyClose} onClick={() => setSelectedPortalNotificationId(null)}>
                    Close
                  </button>
                </div>
                <div style={styles.historyBody}>
                  <div style={styles.messagesDetailCard}>
                    <div style={styles.messagesDetailHeader}>
                      <div style={styles.messagesDetailTitle}>{selectedPortalNotification.title}</div>
                      <div style={styles.messagesDetailBadgeRow}>
                        <span style={{ ...styles.messagesSenderBadge, ...styles.messagesSenderBadgeSupport }}>
                          {selectedPortalNotification.severity || 'info'}
                        </span>
                        {selectedPortalNotification.dismissedAt && <span style={styles.messagesStateBadge}>Dismissed</span>}
                      </div>
                    </div>
                    <div style={styles.messagesDetailBody}>{selectedPortalNotification.body}</div>
                    {selectedPortalNotification.attachments && selectedPortalNotification.attachments.length > 0 && (
                      <div style={styles.messagesAttachmentList}>
                        {selectedPortalNotification.attachments.map(item => (
                          <div key={item.id} style={styles.messagesAttachmentItem}>
                            <button
                              type="button"
                              style={styles.messagesAttachmentRemove}
                              onClick={() => void handleOpenPortalNotificationAttachment(selectedPortalNotification, item)}
                            >
                              {item.name} ({formatAttachmentSize(Number(item.size || 0))})
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                    {!selectedPortalNotification.dismissedAt && (
                      <div style={styles.messagesReplyActions}>
                        <button
                          type="button"
                          style={styles.messagesReplySecondaryButton}
                          onClick={() => handleDismissPortalNotification(selectedPortalNotification)}
                        >
                          Dismiss
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      );
    }

    const renderCompose = () => (
      <div style={styles.messagesComposer}>
        <div style={styles.messagesTitle}>Start a message</div>
        <div style={styles.messagesSubtitle}>
          Ask a question or share an update with your support team.
        </div>

        <div style={styles.messagesField}>
          <label style={styles.messagesLabel}>Message focus</label>
          <select
            style={styles.select}
            value={messageFocusType}
            onChange={(event) => {
              const value = event.target.value as 'general' | 'device' | 'case';
              setMessageFocusType(value);
              setMessageDeviceId('');
              setMessageCaseId('');
            }}
          >
            <option value='general'>General</option>
            <option value='device'>Device</option>
            <option value='case'>Case</option>
          </select>
          <div style={styles.messagesFocusMeta}>Focus: {focusSummary}</div>
        </div>

        {messageFocusType === 'device' && (
          <div style={styles.messagesField}>
            <label style={styles.messagesLabel}>Select device</label>
            <select
              style={styles.select}
              value={messageDeviceId}
              onChange={(event) => setMessageDeviceId(event.target.value)}
            >
              <option value=''>Choose a device...</option>
              {customerDevices.map(device => (
                <option key={device.id} value={device.id}>
                  {getDeviceLabel(device)}
                </option>
              ))}
            </select>
          </div>
        )}

        {messageFocusType === 'case' && (
          <div style={styles.messagesField}>
            <label style={styles.messagesLabel}>Select case</label>
            <select
              style={styles.select}
              value={messageCaseId}
              onChange={(event) => setMessageCaseId(event.target.value)}
            >
              <option value=''>Choose a case...</option>
              {sortedPortalCases.map(entry => (
                <option key={entry.id} value={entry.id}>
                  {getCaseDisplay(entry)} - {formatPortalCaseStatusLabel(entry.status, 'Status pending')}
                </option>
              ))}
            </select>
          </div>
        )}

        <div style={styles.messagesField}>
          <label style={styles.messagesLabel}>Message</label>
          <textarea
            style={styles.messagesTextarea}
            placeholder='Type your message...'
            rows={6}
            value={messageBody}
            onChange={(event) => setMessageBody(event.target.value)}
          />
        </div>

        <div style={styles.messagesField}>
          <label style={styles.messagesLabel}>Attachments</label>
          <div
            style={styles.messagesAttachmentDropZone}
            role="button"
            tabIndex={0}
            onClick={() => messageAttachmentInputRef.current?.click()}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                messageAttachmentInputRef.current?.click();
              }
            }}
            onDragOver={(event) => event.preventDefault()}
            onDrop={handleMessageAttachmentDrop}
          >
            <div style={styles.messagesAttachmentDropTitle}>
              Drag and drop files here, or click to choose files
            </div>
            <div style={styles.messagesAttachmentDropHint}>
              Attach images, videos, or audio files for your support team.
            </div>
          </div>
          <input
            ref={messageAttachmentInputRef}
            type="file"
            multiple
            accept="image/*,video/*,audio/*"
            onChange={(event) => {
              handleMessageAttachmentChange(event.target.files);
              event.currentTarget.value = '';
            }}
            style={{ display: 'none' }}
          />
          {messageAttachments.length > 0 && (
            <div style={styles.messagesAttachmentList}>
              {messageAttachments.map(item => (
                <div key={item.id} style={styles.messagesAttachmentItem}>
                  <div>
                    {item.name} ({formatAttachmentSize(item.size)})
                  </div>
                  <button
                    type='button'
                    style={styles.messagesAttachmentRemove}
                    onClick={() => handleRemoveMessageAttachment(item.id)}
                  >
                    Remove
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        <div style={styles.messagesSendRow}>
          <button
            type='button'
            style={styles.messagesSendButton}
            onClick={handleSendMessage}
            disabled={!canSend}
          >
            {messageSending ? 'Sending...' : 'Send message'}
          </button>
        </div>
      </div>
    );

    const renderList = () => (
      <div style={styles.messagesSection}>
        <div style={styles.messagesSectionHeader}>
          <div>
            <div style={styles.messagesTitle}>{messageShowSent ? 'Sent' : 'Inbox'}</div>
            <div style={styles.messagesSubtitle}>
              {messageShowSent
                ? 'Messages you sent to your support team.'
                : 'Messages received from your support team.'}
            </div>
          </div>
          <button
            type="button"
            style={styles.messagesNewButton}
            onClick={() => {
              setShowComposePanel(prev => !prev);
              setSelectedPortalMessageId(null);
            }}
          >
            {showComposePanel ? 'Hide new message' : 'New message'}
          </button>
        </div>
        {modeTabs}
        <div style={styles.messagesFilterRow}>
          <select
            style={styles.select}
            value={messageView}
            onChange={(event) => setMessageView(event.target.value as 'active' | 'archived' | 'all')}
          >
            <option value="active">Active</option>
            <option value="archived">Archived</option>
            <option value="all">All</option>
          </select>
          <select
            style={styles.select}
            value={messageFocusFilter}
            onChange={(event) => {
              const value = event.target.value as 'all' | 'general' | 'device' | 'case';
              setMessageFocusFilter(value);
              setMessageCaseFilter('');
              setMessageDeviceFilter('');
            }}
          >
            <option value="all">All focus</option>
            <option value="general">General</option>
            <option value="case">Case</option>
            <option value="device">Device</option>
          </select>
          <select
            style={styles.select}
            value={messageCaseFilter}
            onChange={(event) => setMessageCaseFilter(event.target.value)}
            disabled={messageFocusFilter === 'device'}
          >
            <option value="">All cases</option>
            {sortedPortalCases.map(entry => (
              <option key={entry.id} value={entry.id}>
                {getCaseDisplay(entry)}
              </option>
            ))}
          </select>
          <select
            style={styles.select}
            value={messageDeviceFilter}
            onChange={(event) => setMessageDeviceFilter(event.target.value)}
            disabled={messageFocusFilter === 'case'}
          >
            <option value="">All devices</option>
            {customerDevices.map(device => (
              <option key={device.id} value={device.id}>
                {getDeviceLabel(device)}
              </option>
            ))}
          </select>
          <select
            style={styles.select}
            value={messageSortOrder}
            onChange={(event) => setMessageSortOrder(event.target.value as 'newest' | 'oldest')}
          >
            <option value="newest">Newest first</option>
            <option value="oldest">Oldest first</option>
          </select>
          <label style={styles.messagesFilterToggle}>
            <input
              type="checkbox"
              style={styles.messagesFilterToggleInput}
              checked={showUnreadOnly}
              onChange={(event) => setShowUnreadOnly(event.target.checked)}
            />
            New only
          </label>
          <label style={styles.messagesFilterToggle}>
            <input
              type="checkbox"
              style={styles.messagesFilterToggleInput}
              checked={messageShowSent}
              onChange={(event) => setMessageShowSent(event.target.checked)}
            />
            Show sent
          </label>
        </div>
        {portalMessages.length === 0 && (
          <div style={styles.messagesEmpty}>No messages yet. Start a new message to reach support.</div>
        )}
        {portalMessages.length > 0 && filteredPortalMessages.length === 0 && (
          <div style={styles.messagesEmpty}>
            {messageShowSent && showUnreadOnly
              ? 'Sent messages are not shown while New only is enabled.'
              : 'No messages match the current filters.'}
          </div>
        )}
        {filteredPortalMessages.length > 0 && (
          <div style={styles.messagesListScroll}>
            <div style={styles.messagesList}>
              {filteredPortalMessages.slice(0, messageVisibleCount).map(message => {
                const isInbound = message.senderRole !== 'customer';
                const isUnread = isInbound && !readMessageIds.includes(message.id);
                const attachmentsCount = message.attachments ? message.attachments.length : 0;
                const senderLabel =
                  message.senderRole === 'customer'
                    ? message.senderDisplayName || portalUserDisplayName
                    : message.senderDisplayName || 'Support team';
                const senderBadgeLabel = message.senderRole === 'customer' ? 'You' : 'Support';
                const isArchived = Boolean(message.archivedByCustomerAt);
                return (
                  <button
                    key={message.id}
                    type='button'
                    style={{
                      ...styles.messagesItem,
                      ...styles.messagesListButton,
                      ...(selectedPortalMessageId === message.id ? styles.messagesItemActive : {})
                    }}
                    onClick={() => {
                      setSelectedPortalMessageId(message.id);
                      setShowComposePanel(false);
                      if (isInbound && !readMessageIds.includes(message.id)) {
                        setReadMessageIds(prev => [...prev, message.id]);
                        void messageApi.markRead(message.id).catch(error =>
                          console.warn('Failed to mark portal message as read:', error)
                        );
                      }
                    }}
                  >
                    <div style={styles.messagesItemHeader}>
                      <div style={styles.messagesItemHeaderLeft}>
                        <div style={styles.messagesItemBadgeRow}>
                          <span
                            style={{
                              ...styles.messagesSenderBadge,
                              ...(message.senderRole === 'customer' ? {} : styles.messagesSenderBadgeSupport)
                            }}
                          >
                            {senderBadgeLabel}
                          </span>
                          {isUnread && <span style={styles.messagesUnreadBadge}>New</span>}
                          {isArchived && <span style={styles.messagesStateBadge}>Archived</span>}
                        </div>
                        <div style={styles.messagesItemTitle}>{senderLabel}</div>
                      </div>
                      <div style={styles.messagesItemBadgeRow}>
                        <span style={styles.messagesFocusBadge}>{getMessageFocusLabel(message)}</span>
                      </div>
                    </div>
                    <div style={styles.messagesItemBody}>{getMessagePreview(message.body)}</div>
                    <div style={styles.messagesItemMetaRow}>
                      <div style={styles.messagesItemMeta}>{formatPortalDateTime(message.createdAt)}</div>
                      <div style={styles.messagesItemMetaGroup}>
                        {attachmentsCount > 0 && (
                          <div style={styles.messagesItemMetaTag}>
                            {attachmentsCount} attachment{attachmentsCount === 1 ? '' : 's'}
                          </div>
                        )}
                      </div>
                    </div>
                  </button>
                );
              })}
              {filteredPortalMessages.length > messageVisibleCount && (
                <button
                  type='button'
                  style={styles.messagesLoadMore}
                  onClick={() => setMessageVisibleCount(prev => prev + 10)}
                >
                  Load older messages
                </button>
              )}
            </div>
          </div>
        )}
        </div>
      );

    return (
      <div style={styles.messagesLayout}>
        {renderList()}
        {showComposePanel && (
          <div
            style={styles.historyOverlay}
            role='dialog'
            aria-modal='true'
            aria-label='New message'
            onClick={() => setShowComposePanel(false)}
          >
            <div style={styles.historyPanel} onClick={(event) => event.stopPropagation()}>
              <div style={styles.historyHeader}>
                <div>
                  <div style={styles.historyModalTitle}>New message</div>
                  <div style={styles.historySubtitle}>Start a message with your support team.</div>
                </div>
                <button type='button' style={styles.historyClose} onClick={() => setShowComposePanel(false)}>
                  Close
                </button>
              </div>
              <div style={styles.historyBody}>{renderCompose()}</div>
            </div>
          </div>
        )}
        {selectedPortalMessage && !showComposePanel && (
          <div
            style={styles.historyOverlay}
            role='dialog'
            aria-modal='true'
            aria-label='Message details'
            onClick={() => setSelectedPortalMessageId(null)}
          >
            <div style={styles.historyPanel} onClick={(event) => event.stopPropagation()}>
              <div style={styles.historyHeader}>
                <div>
                  <div style={styles.historyModalTitle}>
                    {selectedPortalMessage.senderRole === 'customer' ? portalDisplayName : 'Support'}
                  </div>
                  <div style={styles.historySubtitle}>Focus: {getMessageFocusLabel(selectedPortalMessage)}</div>
                </div>
                <button type='button' style={styles.historyClose} onClick={() => setSelectedPortalMessageId(null)}>
                  Close
                </button>
              </div>
              <div style={styles.historyBody}>
                <div style={styles.messagesDetailCard}>
                  <div style={styles.messagesDetailHeader}>
                    <div>
                      <div style={styles.messagesDetailTitle}>
                        {selectedPortalMessage.senderRole === 'customer'
                          ? selectedPortalMessage.senderDisplayName || portalUserDisplayName
                          : selectedPortalMessage.senderDisplayName || 'Support team'}
                      </div>
                      <div style={styles.messagesItemMeta}>
                        {formatPortalDateTime(selectedPortalMessage.createdAt)}
                      </div>
                    </div>
                    <div style={styles.messagesDetailBadgeRow}>
                      <span
                        style={{
                          ...styles.messagesSenderBadge,
                          ...(selectedPortalMessage.senderRole === 'customer'
                            ? {}
                            : styles.messagesSenderBadgeSupport)
                        }}
                      >
                        {selectedPortalMessage.senderRole === 'customer' ? 'You' : 'Support'}
                      </span>
                      <span style={styles.messagesFocusBadge}>{getMessageFocusLabel(selectedPortalMessage)}</span>
                      {selectedPortalMessage.archivedByCustomerAt && (
                        <span style={styles.messagesStateBadge}>Archived</span>
                      )}
                    </div>
                  </div>
                  <div style={styles.messagesDetailBody}>{selectedPortalMessage.body}</div>
                  {selectedPortalMessage.attachments && selectedPortalMessage.attachments.length > 0 && (
                    <div style={styles.messagesAttachmentList}>
                      {selectedPortalMessage.attachments.map(item => (
                        <div key={item.id} style={styles.messagesAttachmentItem}>
                          <button
                            type='button'
                            style={styles.messagesAttachmentRemove}
                            onClick={() =>
                              void handleDownloadPortalAttachment(selectedPortalMessage.id, item)
                            }
                          >
                            {item.name} ({formatAttachmentSize(item.size)})
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <div style={styles.messagesReplyPanel}>
                  <div style={styles.messagesReplyTitle}>Reply</div>
                  <textarea
                    style={styles.messagesReplyInput}
                    placeholder='Write a reply to support...'
                    rows={4}
                    value={messageReplyBody}
                    onChange={(event) => setMessageReplyBody(event.target.value)}
                  />
                  <div style={styles.messagesField}>
                    <label style={styles.messagesLabel}>Attachments</label>
                    <div style={styles.messagesAttachmentRow}>
                      <input
                        type='file'
                        multiple
                        accept='image/*,video/*,audio/*'
                        onChange={(event) => handleMessageReplyAttachmentChange(event.target.files)}
                      />
                    </div>
                    {messageReplyAttachments.length > 0 && (
                      <div style={styles.messagesAttachmentList}>
                        {messageReplyAttachments.map(item => (
                          <div key={item.id} style={styles.messagesAttachmentItem}>
                            <div>
                              {item.name} ({formatAttachmentSize(item.size)})
                            </div>
                            <button
                              type='button'
                              style={styles.messagesAttachmentRemove}
                              onClick={() => handleRemoveMessageReplyAttachment(item.id)}
                            >
                              Remove
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  <div style={styles.messagesReplyActions}>
                    {messageView !== 'archived' && (
                      <button
                        type='button'
                        style={styles.messagesReplySecondaryButton}
                        onClick={() => void handleArchivePortalMessage(true)}
                      >
                        Archive
                      </button>
                    )}
                    {messageView === 'archived' && (
                      <button
                        type='button'
                        style={styles.messagesReplySecondaryButton}
                        onClick={() => void handleRestorePortalMessage()}
                      >
                        Restore
                      </button>
                    )}
                    <button
                      type='button'
                      style={styles.messagesReplyDangerButton}
                      onClick={() => void handleDeletePortalMessage()}
                    >
                      Delete for me
                    </button>
                    <button
                      type='button'
                      style={styles.messagesReplyButton}
                      onClick={handleSendMessageReply}
                      disabled={!messageReplyBody.trim()}
                    >
                      Send reply
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderPortalSurveyModal = () => {
    if (!selectedPortalSurveyId) return null;

    const activeSurveyCaseLabel = getPortalSurveyCaseLabel(selectedPortalSurvey?.case);
    const activeSurveyDeviceLabel = getPortalSurveyDeviceLabel(selectedPortalSurvey?.case);

    return (
      <div
        style={styles.historyOverlay}
        role='dialog'
        aria-modal='true'
        aria-label='Survey details'
        onClick={closePortalSurveyModal}
      >
        <div style={styles.deviceModalPanel} onClick={event => event.stopPropagation()}>
          <div style={styles.deviceModalHeader}>
            <div>
              <div style={styles.deviceModalTitle}>
                {selectedPortalSurvey?.surveyName || 'Customer survey'}
              </div>
              <div style={styles.deviceModalSubtitle}>
                Case {activeSurveyCaseLabel} | {activeSurveyDeviceLabel}
              </div>
            </div>
            <button
              type='button'
              style={styles.historyClose}
              onClick={closePortalSurveyModal}
              aria-label='Close survey details'
            >
              Close
            </button>
          </div>

          <div style={styles.historyBody}>
            {portalSurveyDetailLoading && <div style={styles.historyState}>Loading survey...</div>}
            {!portalSurveyDetailLoading && portalSurveyDetailError && (
              <div style={{ ...styles.historyState, color: '#b91c1c' }}>{portalSurveyDetailError}</div>
            )}
            {!portalSurveyDetailLoading && !portalSurveyDetailError && selectedPortalSurvey && (
              <div style={styles.surveyDetailBody}>
                <div style={styles.historyDetailGrid}>
                  <div style={styles.historyDetailCard}>
                    <div style={styles.historyDetailLabel}>Status</div>
                    <div style={styles.historyDetailValue}>
                      {selectedPortalSurvey.status === 'COMPLETED' ? 'Completed' : 'Awaiting feedback'}
                    </div>
                  </div>
                  <div style={styles.historyDetailCard}>
                    <div style={styles.historyDetailLabel}>Released</div>
                    <div style={styles.historyDetailValue}>
                      {formatPortalDate(selectedPortalSurvey.releasedAt || undefined)}
                    </div>
                  </div>
                  <div style={styles.historyDetailCard}>
                    <div style={styles.historyDetailLabel}>Technician</div>
                    <div style={styles.historyDetailValue}>
                      {selectedPortalSurvey.technicianName || 'Support team'}
                    </div>
                  </div>
                  <div style={styles.historyDetailCard}>
                    <div style={styles.historyDetailLabel}>Questions</div>
                    <div style={styles.historyDetailValue}>{selectedPortalSurvey.sections.length}</div>
                  </div>
                </div>

                {portalSurveySubmitNotice && (
                  <div
                    style={{
                      ...styles.surveyNotice,
                      ...(portalSurveySubmitNotice.type === 'success'
                        ? styles.surveyNoticeSuccess
                        : styles.surveyNoticeError)
                    }}
                  >
                    {portalSurveySubmitNotice.message}
                  </div>
                )}

                {selectedPortalSurvey.status !== 'COMPLETED' && (
                  <>
                    <div style={styles.surveyProgressRow}>
                      <div style={styles.messagesTitle}>Survey progress</div>
                      <div style={styles.messagesSubtitle}>
                        Answered {answeredPortalSurveyQuestions} of {selectedPortalSurvey.sections.length}
                      </div>
                    </div>

                    <div style={styles.surveyQuestionList}>
                      {selectedPortalSurvey.sections.map(section => {
                        const currentValue = portalSurveyAnswers[section.questionKey] ?? '';
                        const starValues = getPortalSurveyStarValues(section);
                        return (
                          <div key={section.questionKey} style={styles.surveyQuestionCard}>
                            <div style={styles.surveyQuestionTitle}>{section.title}</div>
                            {section.description && (
                              <div style={styles.surveyQuestionDescription}>{section.description}</div>
                            )}
                            <div style={styles.surveyQuestionScale}>
                              Score range: {getPortalSurveyScaleLabel(section)}
                            </div>
                            {starValues.length > 0 ? (
                              <div style={styles.surveyStarsRow}>
                                {starValues.map(value => {
                                  const selected = String(currentValue).trim() === String(value);
                                  return (
                                    <button
                                      key={value}
                                      type='button'
                                      style={{
                                        ...styles.surveyStarButton,
                                        ...(selected ? styles.surveyStarButtonActive : {})
                                      }}
                                      disabled={portalSurveySubmitting}
                                      onClick={() => handlePortalSurveyAnswerChange(section.questionKey, String(value))}
                                    >
                                      {value}
                                    </button>
                                  );
                                })}
                              </div>
                            ) : (
                              <input
                                type='number'
                                min={section.minScore}
                                max={section.maxScore}
                                step={1}
                                inputMode='numeric'
                                value={currentValue}
                                onChange={event =>
                                  handlePortalSurveyAnswerChange(section.questionKey, event.target.value)
                                }
                                style={styles.input}
                              />
                            )}
                          </div>
                        );
                      })}
                    </div>

                    <div style={styles.surveyCommentsBlock}>
                      <div style={styles.messagesLabel}>{selectedPortalSurvey.notesPrompt || 'Customer comments'}</div>
                      <textarea
                        style={styles.textarea}
                        placeholder={`${selectedPortalSurvey.notesPrompt || 'Customer comments'}...`}
                      value={portalSurveyComments}
                      onChange={event => {
                        setPortalSurveySubmitNotice(null);
                        setPortalSurveyComments(event.target.value);
                      }}
                        disabled={portalSurveySubmitting}
                      />
                    </div>
                  </>
                )}

                {selectedPortalSurvey.status === 'COMPLETED' ? (
                  <div style={styles.surveyFooterMeta}>
                    {selectedPortalSurveyScoreDisplay ? (
                      <div style={styles.surveyScoreRow}>
                        <span style={styles.surveyScoreText}>
                          Final score {selectedPortalSurveyScoreDisplay.scoreLabel}
                        </span>
                        <span
                          style={{
                            ...styles.surveyPercentBadge,
                            ...getPortalSurveyPercentageBadgeStyle(selectedPortalSurveyScoreDisplay)
                          }}
                        >
                          {selectedPortalSurveyScoreDisplay.percentageLabel}
                        </span>
                      </div>
                    ) : null}
                    {selectedPortalSurvey.completedAt ? (
                      <div>Submitted {formatPortalDateTime(selectedPortalSurvey.completedAt)}</div>
                    ) : null}
                  </div>
                ) : (
                  <div style={styles.surveyFooterActions}>
                    <div style={styles.surveyFooterMeta}>
                      Submit once when every answer is complete. Your responses will be stored as a fixed historical record.
                    </div>
                    <button
                      type='button'
                      style={{
                        ...styles.button,
                        ...styles.primaryButton,
                        ...(portalSurveySubmitting || !canSubmitSelectedPortalSurvey
                          ? styles.surveyPrimaryButtonDisabled
                          : {})
                      }}
                      onClick={() => {
                        void handleSubmitPortalSurvey();
                      }}
                      disabled={portalSurveySubmitting || !canSubmitSelectedPortalSurvey}
                    >
                      {portalSurveySubmitting ? 'Submitting...' : 'Submit survey'}
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderSurveys = () => {
    const hasSurveyFilters = Boolean(
      surveySearchQuery.trim() ||
        surveyStatusFilter !== 'all' ||
        surveyCaseFilter ||
        surveyDeviceFilter ||
        surveyWorkTypeFilter ||
        surveySortOrder !== 'newest'
    );

    const renderSurveyList = (
      title: string,
      description: string,
      surveys: PortalSurveySummary[],
      emptyMessage: string
    ) => (
      <div style={styles.surveySection}>
        <div style={styles.surveySectionHeader}>
          <div>
            <div style={styles.messagesTitle}>{title}</div>
            <div style={styles.messagesSubtitle}>{description}</div>
          </div>
        </div>
        {surveys.length === 0 ? (
          <div style={styles.overviewEmpty}>{emptyMessage}</div>
        ) : (
          <div style={styles.surveyList}>
            {surveys.map(survey => {
              const caseLabel = getPortalSurveyCaseLabel(survey.case);
              const deviceLabel = getPortalSurveyDeviceLabel(survey.case);
              const scoreDisplay = getPortalSurveyScoreDisplay(survey);
              return (
                <button
                  key={survey.id}
                  type='button'
                  style={styles.surveyCardButton}
                  onClick={() => {
                    void loadPortalSurveyDetail(survey.id);
                  }}
                >
                  <div style={styles.surveyCard}>
                    <div style={styles.surveyCardHeader}>
                      <div>
                        <div style={styles.surveyCardTitle}>{survey.surveyName}</div>
                        <div style={styles.surveyCardMeta}>Case {caseLabel} | {deviceLabel}</div>
                      </div>
                      <span
                        style={{
                          ...styles.surveyStatusBadge,
                          ...(survey.status === 'COMPLETED'
                            ? styles.surveyStatusCompleted
                            : styles.surveyStatusAvailable)
                        }}
                      >
                        {survey.status === 'COMPLETED' ? 'Completed' : 'Pending'}
                      </span>
                    </div>
                    <div style={styles.surveyCardFacts}>
                      <div style={styles.surveyFact}>
                        <div style={styles.surveyFactLabel}>Work type</div>
                        <div style={styles.surveyFactValue}>{getPortalSurveyWorkTypeLabel(survey.case)}</div>
                      </div>
                      <div style={styles.surveyFact}>
                        <div style={styles.surveyFactLabel}>Released</div>
                        <div style={styles.surveyFactValue}>{formatPortalDate(survey.releasedAt || undefined)}</div>
                      </div>
                      <div style={styles.surveyFact}>
                        <div style={styles.surveyFactLabel}>Questions</div>
                        <div style={styles.surveyFactValue}>{survey.questionCount}</div>
                      </div>
                      <div style={styles.surveyFact}>
                        <div style={styles.surveyFactLabel}>Technician</div>
                        <div style={styles.surveyFactValue}>{survey.technicianName || 'Support team'}</div>
                      </div>
                    </div>
                    {survey.status === 'COMPLETED' && scoreDisplay ? (
                      <div style={styles.surveyCardScore}>
                        <div style={styles.surveyScoreRow}>
                          <span style={styles.surveyScoreText}>Score {scoreDisplay.scoreLabel}</span>
                          <span
                            style={{
                              ...styles.surveyPercentBadge,
                              ...getPortalSurveyPercentageBadgeStyle(scoreDisplay)
                            }}
                          >
                            {scoreDisplay.percentageLabel}
                          </span>
                        </div>
                      </div>
                    ) : (
                      <div style={styles.surveyCardAction}>Open survey</div>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>
    );

    return (
      <div style={styles.messagesLayout}>
        <div style={styles.overviewCardGrid}>
          <div style={styles.overviewCard}>
            <div style={styles.overviewCardLabel}>Pending surveys</div>
            <div style={styles.overviewCardValue}>{pendingPortalSurveyCount}</div>
            <div style={styles.overviewCardMeta}>
              {pendingPortalSurveyCount === 0
                ? 'No surveys waiting for feedback'
                : `${pendingPortalSurveyCount} survey${pendingPortalSurveyCount === 1 ? '' : 's'} awaiting response`}
            </div>
          </div>
          <div style={styles.overviewCard}>
            <div style={styles.overviewCardLabel}>Completed surveys</div>
            <div style={styles.overviewCardValue}>{completedPortalSurveyCount}</div>
            <div style={styles.overviewCardMeta}>
              {completedPortalSurveyCount === 0
                ? 'No completed surveys yet'
                : `${completedPortalSurveyCount} survey${completedPortalSurveyCount === 1 ? '' : 's'} submitted`}
            </div>
          </div>
          <div style={styles.overviewCard}>
            <div style={styles.overviewCardLabel}>Latest release</div>
            <div style={styles.overviewCardValue}>
              {portalSurveys[0]?.releasedAt ? formatPortalDate(portalSurveys[0].releasedAt) : 'None'}
            </div>
            <div style={styles.overviewCardMeta}>
              {portalSurveys[0]?.surveyName || 'Surveys will appear here after case close'}
            </div>
          </div>
        </div>

        <div style={styles.messagesSection}>
          <div style={styles.messagesSectionHeader}>
            <div>
              <div style={styles.messagesTitle}>Filter surveys</div>
              <div style={styles.messagesSubtitle}>Search by case, device, work type, or status.</div>
            </div>
            {hasSurveyFilters && (
              <button
                type='button'
                style={styles.messagesNewButton}
                onClick={() => {
                  setSurveySearchQuery('');
                  setSurveyStatusFilter('all');
                  setSurveyCaseFilter('');
                  setSurveyDeviceFilter('');
                  setSurveyWorkTypeFilter('');
                  setSurveySortOrder('newest');
                }}
              >
                Reset filters
              </button>
            )}
          </div>
          <div style={styles.messagesFilterRow}>
            <input
              type='search'
              style={styles.input}
              placeholder='Search surveys'
              value={surveySearchQuery}
              onChange={event => setSurveySearchQuery(event.target.value)}
            />
            <select
              style={styles.select}
              value={surveyStatusFilter}
              onChange={event =>
                setSurveyStatusFilter(event.target.value as 'all' | 'available' | 'completed')
              }
            >
              <option value='all'>All statuses</option>
              <option value='available'>Waiting</option>
              <option value='completed'>Completed</option>
            </select>
            <select
              style={styles.select}
              value={surveyCaseFilter}
              onChange={event => setSurveyCaseFilter(event.target.value)}
            >
              <option value=''>All cases</option>
              {surveyCaseOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            <select
              style={styles.select}
              value={surveyDeviceFilter}
              onChange={event => setSurveyDeviceFilter(event.target.value)}
            >
              <option value=''>All devices</option>
              {surveyDeviceOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            <select
              style={styles.select}
              value={surveyWorkTypeFilter}
              onChange={event => setSurveyWorkTypeFilter(event.target.value)}
            >
              <option value=''>All work types</option>
              {surveyWorkTypeOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            <select
              style={styles.select}
              value={surveySortOrder}
              onChange={event => setSurveySortOrder(event.target.value as 'newest' | 'oldest')}
            >
              <option value='newest'>Newest first</option>
              <option value='oldest'>Oldest first</option>
            </select>
          </div>
        </div>

        {portalSurveysLoading && <div style={styles.overviewEmpty}>Loading surveys...</div>}
        {!portalSurveysLoading && portalSurveysError && <div style={styles.overviewEmpty}>{portalSurveysError}</div>}
        {!portalSurveysLoading && !portalSurveysError && portalSurveys.length === 0 && (
          <div style={styles.portalPanel}>
            <div style={styles.portalPanelTitle}>No surveys available</div>
            <div style={styles.portalPanelText}>
              Surveys will appear here once a service case is manually closed and the support team releases feedback.
            </div>
          </div>
        )}
        {!portalSurveysLoading &&
          !portalSurveysError &&
          portalSurveys.length > 0 && (
            <>
              {filteredPortalSurveys.length === 0 && (
                <div style={styles.portalPanel}>
                  <div style={styles.portalPanelTitle}>No surveys match the current filters</div>
                  <div style={styles.portalPanelText}>
                    Adjust or reset the survey filters to see released feedback records.
                  </div>
                </div>
              )}
              {renderSurveyList(
                'Pending feedback',
                'These surveys are ready to complete.',
                filteredPendingPortalSurveys,
                'No surveys are waiting for feedback.'
              )}
              {renderSurveyList(
                'Completed surveys',
                'Historical feedback stays available here once submitted.',
                filteredCompletedPortalSurveys,
                'No completed surveys yet.'
              )}
            </>
          )}

      </div>
    );
  };

  const showPortalHeader = activePortalTab !== 'support';
  const currentTabMeta = tabMeta[activePortalTab];

  return (
    <div style={styles.portalShell}>
      <aside style={styles.sidebar}>
        <div style={styles.sidebarBrand}>
          <img src={BRAND_WORDMARK_SRC} alt="bonzaiQ" style={styles.sidebarLogo} />
          <div style={styles.sidebarSubtitle}>{portalDisplayName}</div>
        </div>
        <div style={styles.sidebarNav}>
            {navItems.map(item => (
              <button
                key={item.id}
                type="button"
                style={{
                  ...styles.navButton,
                  ...(activePortalTab === item.id ? styles.navButtonActive : {})
                }}
                onClick={() => setActivePortalTab(item.id)}
              >
                <span>{item.label}</span>
                {item.id === 'messages' && unreadPortalCount > 0 && (
                  <span style={styles.navBadge}>{unreadPortalCount}</span>
                )}
              </button>
            ))}
        </div>
        <SupportTicketLauncher surface="CUSTOMER_PORTAL" pageLabel={currentTabMeta.title} />
      </aside>

      <div style={styles.portalContent}>
        {showPortalHeader && (
          <div style={styles.portalHeader}>
            <div>
              <div style={styles.portalTitle}>{currentTabMeta.title}</div>
              <div style={styles.portalSubtitle}>{currentTabMeta.subtitle}</div>
            </div>
            {activePortalTab === 'overview' && (
              <div style={styles.portalActions}>
                <button
                  type="button"
                  style={styles.portalPrimaryButton}
                  onClick={() => {
                    setActivePortalTab('support');
                    setCurrentStep(1);
                  }}
                >
                  Submit Support Request
                </button>
                <button
                  type="button"
                  style={styles.portalSecondaryButton}
                  onClick={() => setActivePortalTab('messages')}
                >
                  Message Support
                </button>
              </div>
            )}
          </div>
        )}

        {activePortalTab === 'overview' && renderOverview()}
        {activePortalTab === 'support' && <div style={styles.supportWrapper}>{supportRequestContent}</div>}
        {activePortalTab === 'messages' && renderMessages()}
        {activePortalTab === 'devices' && (
          <div style={styles.messagesLayout}>
            <div style={styles.overviewCardGrid}>
              <div style={styles.overviewCard}>
                <div style={styles.overviewCardLabel}>Registered devices</div>
                <div style={styles.overviewCardValue}>{customerDevices.length}</div>
                <div style={styles.overviewCardMeta}>
                  {customerDevices.length === 1 ? '1 device on file' : `${customerDevices.length} devices on file`}
                </div>
              </div>
              <div style={styles.overviewCard}>
                <div style={styles.overviewCardLabel}>Devices with open requests</div>
                <div style={styles.overviewCardValue}>{devicesWithOpenRequestsCount}</div>
                <div style={styles.overviewCardMeta}>
                  {devicesWithOpenRequestsCount === 0
                    ? 'No devices currently in service'
                    : `${devicesWithOpenRequestsCount} device${devicesWithOpenRequestsCount === 1 ? '' : 's'} need attention`}
                </div>
              </div>
              <div style={styles.overviewCard}>
                <div style={styles.overviewCardLabel}>Locations on file</div>
                <div style={styles.overviewCardValue}>{deviceLocationsCount}</div>
                <div style={styles.overviewCardMeta}>
                  {deviceLocationsCount === 0
                    ? 'No named locations recorded'
                    : `${deviceLocationsCount} location${deviceLocationsCount === 1 ? '' : 's'} linked`}
                </div>
              </div>
              <div style={styles.overviewCard}>
                <div style={styles.overviewCardLabel}>Devices with history</div>
                <div style={styles.overviewCardValue}>{devicesWithHistoryCount}</div>
                <div style={styles.overviewCardMeta}>
                  {devicesWithHistoryCount === 0
                    ? 'No previous service cases yet'
                    : `${devicesWithHistoryCount} device${devicesWithHistoryCount === 1 ? '' : 's'} have recorded cases`}
                </div>
              </div>
            </div>

            <div style={styles.deviceGrid}>
              {customerDevices.length === 0 && (
                <div style={styles.deviceEmpty}>No registered devices found for this customer.</div>
              )}
              {customerDevices.map(device => {
                const deviceLabel = getDeviceLabel(device);
                const deviceCases = getDeviceCases(device.id);
                const openDeviceCaseCount = deviceCases.filter(entry => !isClosedStatus(entry.status)).length;
                const latestDeviceCase = deviceCases[0] || null;
                const lifecycleLabel = formatDeviceLifecycle(device.lifecycleStatus);
                const lifecycleBadgeStyle = getDeviceLifecycleBadgeStyles(device.lifecycleStatus);
                const activeDeviceSlaAssignment = getCurrentDeviceSlaAssignment(device.slaAssignments);
                const activeDeviceWarrantyStatus = getDeviceWarrantyStatus(device);
                const latestActivityDate = latestDeviceCase
                  ? formatPortalDate(
                      latestDeviceCase.closedAt || latestDeviceCase.updatedAt || latestDeviceCase.createdAt
                    )
                  : 'None';
                const latestActivityCaseLabel = latestDeviceCase
                  ? `Case ${getCaseDisplay(latestDeviceCase)}`
                  : '';

                return (
                  <button
                    key={device.id}
                    type="button"
                    style={styles.deviceCardButton}
                    onClick={() => openPortalDeviceModal(device.id)}
                  >
                    <div style={styles.deviceCard}>
                      <div style={styles.deviceHeaderRow}>
                        <div style={styles.deviceSummaryIdentity}>
                          {getDeviceImageSrc(device) ? (
                            <img
                              src={getDeviceImageSrc(device)}
                              alt={deviceLabel}
                              style={styles.deviceThumbImage}
                            />
                          ) : (
                            <div style={styles.deviceThumb}>{getDeviceInitials(device)}</div>
                          )}
                          <div style={styles.deviceSummaryIdentityCopy}>
                            <div style={styles.deviceTitle}>{deviceLabel}</div>
                            <div style={styles.deviceSummarySerial}>
                              {formatDeviceIdentifier(device)}
                            </div>
                            {(activeDeviceSlaAssignment || activeDeviceWarrantyStatus.active) ? (
                              <div style={{ display: 'flex', alignItems: 'center', gap: '0.45rem', flexWrap: 'wrap', marginTop: '0.35rem' }}>
                                {activeDeviceWarrantyStatus.active ? (
                                  <span style={getDeviceWarrantyChipStyle(activeDeviceWarrantyStatus)}>
                                    {getDeviceWarrantyChipLabel(activeDeviceWarrantyStatus)}
                                  </span>
                                ) : null}
                                {activeDeviceWarrantyStatus.isReplacementPeriod ? (
                                  <span style={getDeviceWarrantyFirstMonthChipStyle()}>First month</span>
                                ) : null}
                                {activeDeviceSlaAssignment ? (
                                  <>
                                    <span
                                      style={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        borderRadius: '999px',
                                        padding: '0.15rem 0.55rem',
                                        fontSize: '0.72rem',
                                        fontWeight: 700,
                                        backgroundColor: '#E8F3EF',
                                        color: '#1B5E48',
                                        border: '1px solid #BFD8CB'
                                      }}
                                    >
                                      SLA
                                    </span>
                                    <span style={{ fontSize: '0.8rem', color: '#143f43', fontWeight: 600 }}>
                                      {getDeviceSlaLabel(activeDeviceSlaAssignment)}
                                    </span>
                                  </>
                                ) : null}
                              </div>
                            ) : null}
                          </div>
                        </div>

                        <div style={styles.deviceSummaryStatus}>
                          <span style={{ ...styles.deviceLifecycleBadge, ...lifecycleBadgeStyle }}>
                            {lifecycleLabel}
                          </span>
                          <div style={styles.deviceSummaryStatusMeta}>{formatPortalLocation(device)}</div>
                        </div>

                        <div style={styles.deviceSummarySignals}>
                          <div style={styles.deviceSummarySignal}>
                            <div style={styles.deviceSummarySignalLabel}>Open requests</div>
                            <div style={styles.deviceSummarySignalValue}>{openDeviceCaseCount}</div>
                          </div>
                          <div style={styles.deviceSummarySignal}>
                            <div style={styles.deviceSummarySignalLabel}>Last activity</div>
                            <div style={styles.deviceSummarySignalValue}>{latestActivityDate}</div>
                            {latestActivityCaseLabel && (
                              <div style={styles.deviceSummarySignalMeta}>{latestActivityCaseLabel}</div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>

            {selectedPortalDevice && (
              <div
                style={styles.historyOverlay}
                role="dialog"
                aria-modal="true"
                aria-label="Device quick view"
                onClick={event => event.stopPropagation()}
              >
                <div style={styles.deviceModalPanel} onClick={event => event.stopPropagation()}>
                  <div style={styles.deviceModalHeader}>
                    <div>
                      <div style={styles.deviceModalTitle}>{getDeviceLabel(selectedPortalDevice)}</div>
                      <div style={styles.deviceModalSubtitle}>
                        {formatDeviceIdentifier(selectedPortalDevice)} | {formatPortalLocation(selectedPortalDevice)}
                      </div>
                    </div>
                    <button
                      type="button"
                      style={styles.historyClose}
                      onClick={closePortalDeviceModal}
                      aria-label="Close device quick view"
                    >
                      Close
                    </button>
                  </div>

                  <div style={styles.deviceModalHero}>
                    <div style={styles.deviceModalHeroCard}>
                      {getDeviceImageSrc(selectedPortalDevice) ? (
                        <img
                          src={getDeviceImageSrc(selectedPortalDevice)}
                          alt={getDeviceLabel(selectedPortalDevice)}
                          style={styles.deviceModalThumbImage}
                        />
                      ) : (
                        <div style={styles.deviceModalThumb}>{getDeviceInitials(selectedPortalDevice)}</div>
                      )}
                      <div style={styles.deviceModalHeroCopy}>
                        <div style={styles.deviceTitle}>{getDeviceLabel(selectedPortalDevice)}</div>
                        <div style={styles.deviceSummarySerial}>
                          {formatDeviceIdentifier(selectedPortalDevice)}
                        </div>
                        <div style={styles.deviceSubtitle}>
                          {selectedPortalDevice.manufacturer || selectedPortalDevice.model?.manufacturer || 'Manufacturer not recorded'}
                        </div>
                        <div style={styles.deviceModalHeroMeta}>
                          <span style={styles.deviceMetaPill}>
                            Installed {selectedPortalDevice.installedAt ? formatPortalDate(selectedPortalDevice.installedAt) : 'Not on file'}
                          </span>
                          <span style={styles.deviceMetaPill}>
                            Warranty {selectedPortalDevice.warrantyEndAt ? formatPortalDate(selectedPortalDevice.warrantyEndAt) : 'Not on file'}
                          </span>
                          {selectedPortalDeviceWarrantyStatus.active ? (
                            <span style={getDeviceWarrantyChipStyle(selectedPortalDeviceWarrantyStatus)}>
                              {getDeviceWarrantyChipLabel(selectedPortalDeviceWarrantyStatus)}
                            </span>
                          ) : null}
                          {selectedPortalDeviceWarrantyStatus.isReplacementPeriod ? (
                            <span style={getDeviceWarrantyFirstMonthChipStyle()}>First month</span>
                          ) : null}
                        </div>
                      </div>
                    </div>

                    <div style={styles.deviceModalSideStack}>
                      <div style={styles.deviceModalStatusCard}>
                        <div style={styles.deviceModalStatusLabel}>Deployment</div>
                        <span
                          style={{
                            ...styles.deviceLifecycleBadge,
                            ...(selectedPortalDeviceLifecycleBadgeStyle || {})
                          }}
                        >
                          {selectedPortalDeviceLifecycleLabel}
                        </span>
                        <div style={styles.deviceModalStatusMeta}>
                          {formatPortalLocation(selectedPortalDevice)}
                        </div>
                      </div>
                      <div style={styles.deviceModalSlaCard}>
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: '0.5rem',
                            flexWrap: 'wrap'
                          }}
                        >
                          <div style={styles.deviceModalStatusLabel}>SLA</div>
                          {selectedPortalDeviceSlaAssignment ? (
                            <span
                              style={{
                                ...styles.deviceLifecycleBadge,
                                backgroundColor: '#E8F3EF',
                                borderColor: '#BFD8CB',
                                color: '#1B5E48'
                              }}
                            >
                              Active
                            </span>
                          ) : null}
                        </div>
                        {selectedPortalDeviceSlaAssignment ? (
                          <>
                            <div style={styles.deviceInfoValue}>{getDeviceSlaLabel(selectedPortalDeviceSlaAssignment)}</div>
                            <div style={styles.deviceModalHeroMeta}>
                              {selectedPortalDeviceSlaHasSavedReport ? (
                                <button
                                  type="button"
                                  style={{ ...styles.deviceActionButton, width: 'fit-content' }}
                                  onClick={openPortalDeviceSlaReport}
                                  disabled={portalDeviceSlaReportLoading}
                                >
                                  {portalDeviceSlaReportLoading ? 'Opening...' : 'View Agreement'}
                                </button>
                              ) : (
                                <div style={{ fontSize: '0.8rem', color: '#64748b' }}>
                                  Saved agreement not available.
                                </div>
                              )}
                            </div>
                            {portalDeviceSlaReportError ? (
                              <div style={{ fontSize: '0.78rem', color: '#b91c1c' }}>
                                {portalDeviceSlaReportError}
                              </div>
                            ) : null}
                          </>
                        ) : (
                          <div style={styles.deviceModalStatusMeta}>No active SLA assigned.</div>
                        )}
                      </div>
                    </div>

                    <div style={styles.deviceModalMetricStack}>
                      <div style={styles.deviceModalMetricCard}>
                        <div style={styles.deviceModalMetricLabel}>Open requests</div>
                        <div style={styles.deviceModalMetricValue}>{selectedPortalDeviceOpenRequestCount}</div>
                        <div style={styles.deviceModalMetricMeta}>
                          {selectedPortalDeviceOpenRequestCount === 0
                            ? 'No active service cases'
                            : `${selectedPortalDeviceOpenRequestCount} request${selectedPortalDeviceOpenRequestCount === 1 ? '' : 's'} currently in progress`}
                        </div>
                      </div>
                      <div style={styles.deviceModalMetricCard}>
                        <div style={styles.deviceModalMetricLabel}>Last activity</div>
                        <div style={styles.deviceModalMetricValue}>
                          {selectedPortalDeviceLastCase
                            ? formatPortalDate(
                                selectedPortalDeviceLastCase.closedAt ||
                                  selectedPortalDeviceLastCase.updatedAt ||
                                  selectedPortalDeviceLastCase.createdAt
                              )
                            : 'None'}
                        </div>
                        <div style={styles.deviceModalMetricMeta}>
                          {selectedPortalDeviceLastCase
                            ? `Case ${selectedPortalDeviceLastCase.caseNumber || selectedPortalDeviceLastCase.caseCode || selectedPortalDeviceLastCase.id}`
                            : 'No recorded service activity'}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div style={styles.deviceModalTabRow}>
                    <button
                      type="button"
                      style={{
                        ...styles.deviceModalTab,
                        ...(portalDeviceModalSection === 'details' ? styles.deviceModalTabActive : {})
                      }}
                      onClick={() => setPortalDeviceModalSection('details')}
                    >
                      Details
                    </button>
                    {!isLitePortal && (
                      <button
                        type="button"
                        style={{
                          ...styles.deviceModalTab,
                          ...(portalDeviceModalSection === 'history' ? styles.deviceModalTabActive : {})
                        }}
                        onClick={() => setPortalDeviceModalSection('history')}
                      >
                        History
                      </button>
                    )}
                    <button
                      type="button"
                      style={{
                        ...styles.deviceModalTab,
                        ...(portalDeviceModalSection === 'qr' ? styles.deviceModalTabActive : {})
                      }}
                      onClick={() => setPortalDeviceModalSection('qr')}
                    >
                      QR code
                    </button>
                  </div>

                  {portalDeviceModalSection === 'details' && (
                    <div style={styles.deviceSection}>
                      <div style={styles.deviceSectionHeader}>
                        <div style={styles.deviceSectionTitle}>Device details</div>
                        <div style={styles.deviceSectionText}>Reference information recorded for this device.</div>
                      </div>
                      <div style={styles.deviceInfoGrid}>
                        <div style={styles.deviceInfoCard}>
                          <div style={styles.deviceInfoLabel}>Model</div>
                          <div style={styles.deviceInfoValue}>
                            {selectedPortalDevice.modelNumber || selectedPortalDevice.model?.modelNumber || 'Unknown'}
                          </div>
                        </div>
                        <div style={styles.deviceInfoCard}>
                          <div style={styles.deviceInfoLabel}>Manufacturer</div>
                          <div style={styles.deviceInfoValue}>
                            {selectedPortalDevice.manufacturer || selectedPortalDevice.model?.manufacturer || 'Unknown'}
                          </div>
                        </div>
                        <div style={styles.deviceInfoCard}>
                          <div style={styles.deviceInfoLabel}>Lifecycle</div>
                          <div style={styles.deviceInfoValue}>{selectedPortalDeviceLifecycleLabel}</div>
                        </div>
                        <div style={styles.deviceInfoCard}>
                          <div style={styles.deviceInfoLabel}>Location</div>
                          <div style={styles.deviceInfoValue}>{formatPortalLocation(selectedPortalDevice)}</div>
                        </div>
                        <div style={styles.deviceInfoCard}>
                          <div style={styles.deviceInfoLabel}>Installed</div>
                          <div style={styles.deviceInfoValue}>
                            {selectedPortalDevice.installedAt ? formatPortalDate(selectedPortalDevice.installedAt) : 'Not on file'}
                          </div>
                        </div>
                        <div style={styles.deviceInfoCard}>
                          <div style={styles.deviceInfoLabel}>Warranty starts</div>
                          <div style={styles.deviceInfoValue}>
                            {selectedPortalDevice.warrantyStartAt ? formatPortalDate(selectedPortalDevice.warrantyStartAt) : 'Not on file'}
                          </div>
                        </div>
                        <div style={styles.deviceInfoCard}>
                          <div style={styles.deviceInfoLabel}>Warranty ends</div>
                          <div style={styles.deviceInfoValue}>
                            {selectedPortalDevice.warrantyEndAt ? formatPortalDate(selectedPortalDevice.warrantyEndAt) : 'Not on file'}
                          </div>
                        </div>
                        <div style={styles.deviceInfoCard}>
                          <div style={styles.deviceInfoLabel}>Warranty status</div>
                          <div style={styles.deviceInfoValue}>{getDeviceWarrantySummaryLabel(selectedPortalDeviceWarrantyStatus)}</div>
                        </div>
                        {selectedPortalDeviceSlaAssignment ? (
                          <>
                            <div style={styles.deviceInfoCard}>
                              <div style={styles.deviceInfoLabel}>SLA starts</div>
                              <div style={styles.deviceInfoValue}>{selectedPortalDeviceSlaStartLabel}</div>
                            </div>
                            <div style={styles.deviceInfoCard}>
                              <div style={styles.deviceInfoLabel}>SLA ends</div>
                              <div style={styles.deviceInfoValue}>{selectedPortalDeviceSlaEndLabel}</div>
                            </div>
                          </>
                        ) : null}
                      </div>
                      {isLitePortal && (
                        <div style={{ ...styles.deviceSection, marginTop: '1rem' }}>
                          <div style={styles.deviceSectionHeader}>
                            <div style={styles.deviceSectionTitle}>Current service cases</div>
                            <div style={styles.deviceSectionText}>
                              Basic open service information for this device.
                            </div>
                          </div>
                          <div style={styles.deviceHistoryList}>
                            {portalCasesLoading && <div style={styles.deviceEmpty}>Loading current cases...</div>}
                            {!portalCasesLoading && selectedPortalDeviceOpenCases.length === 0 && (
                              <div style={styles.deviceEmpty}>No active service cases for this device.</div>
                            )}
                            {!portalCasesLoading &&
                              selectedPortalDeviceOpenCases.slice(0, 4).map(entry => {
                                const statusStyle = getStatusBadgeStyles(entry.status);
                                return (
                                  <button
                                    key={entry.id}
                                    type="button"
                                    style={styles.deviceHistoryRowButton}
                                    onClick={() => openPortalHistoryCaseModal(entry.id)}
                                  >
                                    <div style={styles.deviceHistoryItem}>
                                      <div style={styles.deviceHistoryHeader}>
                                        <div style={styles.deviceHistoryTitle}>
                                          Case {entry.caseNumber || entry.caseCode || entry.id}
                                        </div>
                                        <span
                                          style={{
                                            ...styles.deviceHistoryStatusBadge,
                                            backgroundColor: statusStyle.backgroundColor,
                                            color: statusStyle.color,
                                            borderColor: statusStyle.backgroundColor
                                          }}
                                        >
                                          {formatPortalCaseStatusLabel(entry.status, 'Pending')}
                                        </span>
                                      </div>
                                      <div style={styles.deviceHistoryMeta}>
                                        Updated {formatPortalDate(entry.updatedAt || entry.createdAt)}
                                      </div>
                                    </div>
                                  </button>
                                );
                              })}
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {!isLitePortal && portalDeviceModalSection === 'history' && (
                    <div style={styles.deviceSection}>
                      <div style={styles.deviceSectionHeader}>
                        <div style={styles.deviceSectionTitle}>Recent service history</div>
                        <div style={styles.historyModalActions}>
                          <div style={styles.deviceSectionText}>
                            Latest customer-visible cases connected to this device.
                          </div>
                          <button
                            type="button"
                            style={styles.deviceActionButton}
                            onClick={openHistoryWorkspace}
                          >
                            Open Service History tab
                          </button>
                        </div>
                      </div>
                      <div style={styles.deviceHistoryList}>
                        {portalCasesLoading && (
                          <div style={styles.deviceEmpty}>Loading service history...</div>
                        )}
                        {!portalCasesLoading && selectedPortalDeviceCases.length === 0 && (
                          <div style={styles.deviceEmpty}>No service history for this device yet.</div>
                        )}
                        {!portalCasesLoading &&
                          selectedPortalDeviceCases.slice(0, 6).map(entry => {
                            const statusStyle = getStatusBadgeStyles(entry.status);
                            return (
                              <button
                                key={entry.id}
                                type="button"
                                style={styles.deviceHistoryRowButton}
                                onClick={() => openPortalHistoryCaseModal(entry.id)}
                              >
                                <div style={styles.deviceHistoryItem}>
                                  <div style={styles.deviceHistoryHeader}>
                                    <div style={styles.deviceHistoryTitle}>
                                      Case {entry.caseNumber || entry.caseCode || entry.id}
                                    </div>
                                    <span
                                      style={{
                                        ...styles.deviceHistoryStatusBadge,
                                        backgroundColor: statusStyle.backgroundColor,
                                        color: statusStyle.color,
                                        borderColor: statusStyle.backgroundColor
                                      }}
                                    >
                                      {formatPortalCaseStatusLabel(entry.status, 'Pending')}
                                    </span>
                                  </div>
                                  <div style={styles.deviceHistoryMeta}>
                                    Updated {formatPortalDate(entry.closedAt || entry.updatedAt || entry.createdAt)}
                                  </div>
                                </div>
                              </button>
                            );
                          })}
                      </div>
                    </div>
                  )}

                  {portalDeviceModalSection === 'qr' && (
                    <div style={styles.deviceQrPanel}>
                      <div style={styles.deviceSectionHeader}>
                        <div style={styles.deviceSectionTitle}>Device QR access</div>
                        <div style={styles.deviceSectionText}>Scan or open this link to start a support request for this device.</div>
                      </div>
                      <div style={styles.deviceQrLayout}>
                        <div style={styles.deviceQrCanvas}>
                          {selectedPortalDeviceQrUrl ? (
                            <QRCodeCanvas
                              id={`qr-canvas-${selectedPortalDevice.id}`}
                              value={selectedPortalDeviceQrUrl}
                              size={180}
                              includeMargin
                            />
                          ) : (
                            <div style={styles.deviceQrText}>
                              {deviceQrLoadingId === selectedPortalDevice.id ? 'Preparing QR link...' : 'QR link not ready.'}
                            </div>
                          )}
                        </div>
                        <div style={{ display: 'grid', gap: '0.75rem' }}>
                          <div style={styles.deviceQrMeta}>
                            {selectedPortalDeviceQrUrl ? 'Support link ready' : 'Preparing support link'}
                          </div>
                          <div style={styles.deviceQrText}>
                            Scan to open the customer portal support request with this device preselected.
                          </div>
                          {deviceQrError && (
                            <div style={{ color: '#b42318', fontWeight: 700 }}>
                              {deviceQrError}
                            </div>
                          )}
                          <div style={styles.deviceQrLink}>{selectedPortalDeviceQrUrl || 'Preparing secure device link...'}</div>
                          <div style={styles.deviceQrActions}>
                            <button
                              type="button"
                              style={styles.deviceActionButton}
                              disabled={!selectedPortalDeviceQrUrl}
                              onClick={() => selectedPortalDeviceQrUrl && window.open(selectedPortalDeviceQrUrl, '_blank')}
                            >
                              Open link
                            </button>
                            <button
                              type="button"
                              style={styles.deviceActionButton}
                              disabled={!selectedPortalDeviceQrUrl}
                              onClick={() => handlePrintQr(selectedPortalDevice.id, selectedPortalDeviceQrToken)}
                            >
                              Print QR label
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {portalDeviceSlaReport && (
              <div
                style={styles.historyOverlay}
                role="dialog"
                aria-modal="true"
                aria-label="Device SLA agreement"
                onClick={event => event.stopPropagation()}
              >
                <div
                  style={{
                    ...styles.deviceModalPanel,
                    maxWidth: '980px',
                    maxHeight: '88vh',
                    overflow: 'hidden',
                    display: 'flex',
                    flexDirection: 'column'
                  }}
                  onClick={event => event.stopPropagation()}
                >
                  <div style={styles.deviceModalHeader}>
                    <div>
                      <div style={styles.deviceModalTitle}>{portalDeviceSlaReport.title}</div>
                      <div style={styles.deviceModalSubtitle}>
                        {portalDeviceSlaReport.subtitle || 'Saved customer-facing SLA agreement'}
                      </div>
                    </div>
                    <button
                      type="button"
                      style={styles.historyClose}
                      onClick={closePortalDeviceSlaReport}
                      aria-label="Close SLA agreement"
                    >
                      Close
                    </button>
                  </div>
                  <div
                    style={{
                      display: 'grid',
                      gap: '1rem',
                      overflowY: 'auto',
                      paddingRight: '0.2rem',
                      minHeight: 0,
                      flex: '1 1 auto'
                    }}
                  >
                    <CustomerFacingSlaReportView report={portalDeviceSlaReport} />
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
        {activePortalTab === 'history' && (
          <div style={styles.messagesLayout}>
            {!portalCasesError && (
              <div style={styles.overviewCardGrid}>
                <div style={styles.overviewCard}>
                  <div style={styles.overviewCardLabel}>Service cases</div>
                  <div style={styles.overviewCardValue}>{sortedPortalCases.length}</div>
                  <div style={styles.overviewCardMeta}>
                    {sortedPortalCases.length === 0
                      ? 'No recorded service visits yet'
                      : `${sortedPortalCases.length} case${sortedPortalCases.length === 1 ? '' : 's'} on file`}
                  </div>
                </div>
                <div style={styles.overviewCard}>
                  <div style={styles.overviewCardLabel}>Open requests</div>
                  <div style={styles.overviewCardValue}>{openRequestsCount}</div>
                  <div style={styles.overviewCardMeta}>
                    {openRequestsCount === 0
                      ? 'No active service work right now'
                      : `${openRequestsCount} request${openRequestsCount === 1 ? '' : 's'} still in progress`}
                  </div>
                </div>
                <div style={styles.overviewCard}>
                  <div style={styles.overviewCardLabel}>Devices serviced</div>
                  <div style={styles.overviewCardValue}>{historyDeviceOptions.length}</div>
                  <div style={styles.overviewCardMeta}>
                    {historyDeviceOptions.length === 0
                      ? 'No device service history yet'
                      : `${historyDeviceOptions.length} device${historyDeviceOptions.length === 1 ? '' : 's'} with recorded visits`}
                  </div>
                </div>
                <div style={styles.overviewCard}>
                  <div style={styles.overviewCardLabel}>Shared reports</div>
                  <div style={styles.overviewCardValue}>{sharedServiceReportsCount}</div>
                  <div style={styles.overviewCardMeta}>
                    {lastServiceCase
                      ? `Latest activity ${formatPortalDate(lastServiceCase.closedAt || lastServiceCase.updatedAt || lastServiceCase.createdAt)}`
                      : 'Reports appear here once service visits are completed'}
                  </div>
                </div>
              </div>
            )}

            <div style={styles.historyGrid}>
              {!portalCasesLoading && !portalCasesError && (
                <div style={styles.historyFilters}>
                  <input
                    type='text'
                    value={historySearchQuery}
                    onChange={(event) => setHistorySearchQuery(event.target.value)}
                    placeholder='Search case, device, or issue'
                    style={styles.historyFilterInput}
                  />
                  <select
                    value={historyStatusFilter}
                    onChange={(event) =>
                      setHistoryStatusFilter(event.target.value as 'all' | 'open' | 'closed')
                    }
                    style={styles.historyFilterSelect}
                  >
                    <option value='all'>All statuses</option>
                    <option value='open'>Open requests</option>
                    <option value='closed'>Closed requests</option>
                  </select>
                  <select
                    value={historyDeviceFilter}
                    onChange={(event) => setHistoryDeviceFilter(event.target.value)}
                    style={styles.historyFilterSelect}
                  >
                    <option value='all'>All devices</option>
                    {historyDeviceOptions.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                  <button
                    type='button'
                    style={styles.historyFilterButton}
                    onClick={() => {
                      setHistorySearchQuery('');
                      setHistoryStatusFilter('all');
                      setHistoryDeviceFilter('all');
                    }}
                  >
                    Reset filters
                  </button>
                </div>
              )}
              {portalCasesLoading && (
                <div style={styles.historyEmpty}>Loading service history...</div>
              )}
              {!portalCasesLoading && portalCasesError && (
                <div style={styles.historyEmpty}>{portalCasesError}</div>
              )}
              {!portalCasesLoading && !portalCasesError && filteredPortalCases.length === 0 && (
                <div style={styles.historyEmpty}>
                  {sortedPortalCases.length === 0
                    ? 'No service history available yet.'
                    : 'No cases match your current filters.'}
                </div>
              )}
              {!portalCasesLoading &&
                !portalCasesError &&
                filteredPortalCases.map(entry => {
                  const caseId = entry.id;
                  const { caseLabel, resolvedDevice, deviceLabel, statusStyle } =
                    getPortalCaseModalData(entry);
                  const issuePreview = getPortalCaseIssuePreview(entry.problemDescription);
                  const openedDate = formatPortalDate(entry.createdAt);
                  const identifierLabel = resolvedDevice
                    ? formatDeviceIdentifier(resolvedDevice)
                    : 'Serial not recorded';
                  return (
                    <button
                      key={caseId}
                      type='button'
                      style={styles.historyCardButton}
                      onClick={() => openPortalHistoryCaseModal(caseId)}
                    >
                      <div
                        style={styles.historyCard}
                        ref={node => {
                          historyCaseRefs.current[caseId] = node;
                        }}
                      >
                        <div style={styles.historySummaryLayout}>
                          <div style={styles.historySummaryMain}>
                            <div style={styles.historyTitle}>Case {caseLabel}</div>
                            <div style={styles.historySummarySnippet}>{issuePreview}</div>
                          </div>

                          <div style={styles.historySummaryDeviceColumn}>
                            <div style={styles.historySummaryDeviceName}>{deviceLabel}</div>
                            <div style={styles.historySummaryDeviceMeta}>{identifierLabel}</div>
                            <div style={styles.historySummaryDeviceMeta}>Opened {openedDate}</div>
                          </div>

                          <div style={styles.historySummaryMetaGrid}>
                            <div style={styles.historySummaryMetaCard}>
                              <div style={styles.historyDetailLabel}>Status</div>
                              <div style={styles.historyActionRow}>
                                <span
                                  style={{
                                    ...styles.historyStatusPill,
                                    backgroundColor: statusStyle.backgroundColor,
                                    color: statusStyle.color,
                                    borderColor: statusStyle.backgroundColor
                                  }}
                                >
                                  {formatPortalCaseStatusLabel(entry.status, 'Status pending')}
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </button>
                  );
                })}
            </div>
          </div>
        )}
        {activePortalTab === 'surveys' && renderSurveys()}
        {activePortalTab === 'account' && renderAccount()}

        {selectedPortalHistoryCase && selectedPortalHistoryCaseData && (
          <div
            style={styles.historyOverlay}
            role='dialog'
            aria-modal='true'
            aria-label='Service case details'
            onClick={closePortalHistoryCaseModal}
          >
            <div style={styles.deviceModalPanel} onClick={event => event.stopPropagation()}>
              <div style={styles.deviceModalHeader}>
                <div>
                  <div style={styles.deviceModalTitle}>Case {selectedPortalHistoryCaseData.caseLabel}</div>
                  <div style={styles.deviceModalSubtitle}>
                    {selectedPortalHistoryCaseData.deviceLabel} | Opened {formatPortalDate(selectedPortalHistoryCase.createdAt)}
                  </div>
                </div>
                <div style={styles.historyModalActions}>
                  {!isLitePortal && activePortalTab !== 'history' && (
                    <button
                      type='button'
                      style={styles.deviceActionButton}
                      onClick={() => openCaseInServiceHistory(selectedPortalHistoryCase.id)}
                    >
                      Open Service History tab
                    </button>
                  )}
                  <button
                    type='button'
                    style={styles.historyClose}
                    onClick={closePortalHistoryCaseModal}
                    aria-label='Close service case details'
                  >
                    Close
                  </button>
                </div>
              </div>

              <div
                style={
                  selectedPortalHistoryCaseData.slaAssignment
                    ? styles.caseModalHeroWithSla
                    : styles.deviceModalHero
                }
              >
                <div style={styles.deviceModalHeroCard}>
                  {selectedPortalHistoryCaseData.resolvedDevice &&
                  getDeviceImageSrc(selectedPortalHistoryCaseData.resolvedDevice) ? (
                    <img
                      src={getDeviceImageSrc(selectedPortalHistoryCaseData.resolvedDevice)}
                      alt={selectedPortalHistoryCaseData.deviceLabel}
                      style={styles.deviceModalThumbImage}
                    />
                  ) : (
                    <div style={styles.deviceModalThumb}>
                      {selectedPortalHistoryCaseData.resolvedDevice
                        ? getDeviceInitials(selectedPortalHistoryCaseData.resolvedDevice)
                        : 'DV'}
                    </div>
                  )}
                  <div style={styles.deviceModalHeroCopy}>
                    <div style={styles.deviceTitle}>{selectedPortalHistoryCaseData.deviceLabel}</div>
                    <div style={styles.deviceSummarySerial}>
                      {selectedPortalHistoryCaseData.resolvedDevice
                        ? formatDeviceIdentifier(selectedPortalHistoryCaseData.resolvedDevice)
                        : selectedPortalHistoryCase.device
                        ? formatDeviceIdentifier(selectedPortalHistoryCase.device as any)
                        : 'Serial not recorded'}
                    </div>
                    {selectedPortalHistoryCase.problemDescription && (
                      <div style={styles.deviceSubtitle}>{selectedPortalHistoryCase.problemDescription}</div>
                    )}
                  </div>
                </div>

                <div style={selectedPortalHistoryCaseData.slaAssignment ? styles.deviceModalSideStack : undefined}>
                  <div style={styles.deviceModalStatusCard}>
                    <div style={styles.deviceModalStatusLabel}>Case status</div>
                    <span
                      style={{
                        ...styles.historyStatusPill,
                        backgroundColor: selectedPortalHistoryCaseData.statusStyle.backgroundColor,
                        color: selectedPortalHistoryCaseData.statusStyle.color,
                        borderColor: selectedPortalHistoryCaseData.statusStyle.backgroundColor,
                        width: 'fit-content'
                      }}
                    >
                      {selectedPortalHistoryCase.status || 'Status pending'}
                    </span>
                    <div style={styles.deviceModalStatusMeta}>
                      Updated{' '}
                      {formatPortalDate(
                        selectedPortalHistoryCase.closedAt ||
                          selectedPortalHistoryCase.updatedAt ||
                          selectedPortalHistoryCase.createdAt
                      )}
                    </div>
                  </div>

                  {selectedPortalHistoryCaseData.slaAssignment ? (
                    <div style={styles.deviceModalSlaCard}>
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          gap: '0.5rem',
                          flexWrap: 'wrap'
                        }}
                      >
                        <div style={styles.deviceModalStatusLabel}>SLA</div>
                        <span
                          style={{
                            ...styles.deviceLifecycleBadge,
                            backgroundColor: '#E8F3EF',
                            borderColor: '#BFD8CB',
                            color: '#1B5E48'
                          }}
                        >
                          Active
                        </span>
                      </div>
                      <div style={styles.deviceInfoValue}>
                        {getDeviceSlaLabel(selectedPortalHistoryCaseData.slaAssignment)}
                      </div>
                      <div style={styles.deviceModalSlaRange}>
                        <div style={styles.deviceModalSlaFact}>
                          <div style={styles.deviceModalSlaFactLabel}>Starts</div>
                          <div style={styles.deviceModalSlaFactValue}>
                            {selectedPortalHistoryCaseSlaStartLabel}
                          </div>
                        </div>
                        <div style={styles.deviceModalSlaFact}>
                          <div style={styles.deviceModalSlaFactLabel}>Ends</div>
                          <div style={styles.deviceModalSlaFactValue}>
                            {selectedPortalHistoryCaseSlaEndLabel}
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : null}
                </div>

                <div style={styles.deviceModalMetricStack}>
                  <div style={styles.deviceModalMetricCard}>
                    <div style={styles.deviceModalMetricLabel}>Attachments</div>
                    <div style={styles.deviceModalMetricValue}>
                      {selectedPortalHistoryCase._count?.media || selectedPortalHistoryCase.media?.length || 0}
                    </div>
                    <div style={styles.deviceModalMetricMeta}>Files linked to this case</div>
                  </div>
                  <div style={styles.deviceModalMetricCard}>
                    <div style={styles.deviceModalMetricLabel}>Reports</div>
                    <div style={styles.deviceModalMetricValue}>
                      {selectedPortalHistoryCaseData.visibleServiceReports.length}
                    </div>
                    <div style={styles.deviceModalMetricMeta}>Customer-shared service reports</div>
                  </div>
                  <div style={styles.deviceModalMetricCard}>
                    <div style={styles.deviceModalMetricLabel}>Surveys</div>
                    <div style={styles.deviceModalMetricValue}>
                      {selectedPortalHistoryCaseData.visibleCaseSurveys.length}
                    </div>
                    <div style={styles.deviceModalMetricMeta}>Feedback records linked to this case</div>
                  </div>
                </div>
              </div>

              <div
                style={{
                  border: `1px solid ${BRAND_BORDER}`,
                  borderRadius: '1rem',
                  backgroundColor: '#fffdfa',
                  padding: '1rem',
                  display: 'grid',
                  gap: '0.85rem'
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    gap: '0.75rem',
                    flexWrap: 'wrap',
                    alignItems: 'flex-start'
                  }}
                >
                  <div style={{ fontSize: '1rem', fontWeight: 700, color: BRAND_TEXT }}>Schedule</div>
                  <div
                    style={{
                      display: 'flex',
                      gap: '0.45rem',
                      flexWrap: 'wrap',
                      alignItems: 'center',
                      justifyContent: 'flex-end'
                    }}
                  >
                    <span
                      style={{
                        padding: '0.34rem 0.72rem',
                        borderRadius: '999px',
                        border: `1px solid ${selectedPortalHasCommittedSchedule ? '#bdd4c7' : '#d7dfde'}`,
                        backgroundColor: selectedPortalHasCommittedSchedule ? '#e7f2ec' : '#f1f5f4',
                        color: selectedPortalHasCommittedSchedule ? '#25624d' : BRAND_TEXT,
                        fontSize: '0.84rem',
                        fontWeight: 700
                      }}
                    >
                      {selectedPortalHasCommittedSchedule ? 'Scheduled' : 'Unscheduled'}
                    </span>
                    {selectedPortalHasScheduleRequest ? (
                      <span
                        style={{
                          padding: '0.34rem 0.72rem',
                          borderRadius: '999px',
                          border: '1px solid rgba(166, 59, 50, 0.2)',
                          backgroundColor: '#FDEDEC',
                          color: '#A63B32',
                          fontSize: '0.84rem',
                          fontWeight: 700
                        }}
                      >
                        Requested
                      </span>
                    ) : null}
                    {canPortalConfirmSchedule ? (
                      <button
                        type='button'
                        style={{
                          padding: '0.72rem 1rem',
                          borderRadius: '0.55rem',
                          border: `1px solid ${BRAND_BORDER}`,
                          backgroundColor: BRAND_TEXT,
                          color: '#fff',
                          fontSize: '0.92rem',
                          fontWeight: 700,
                          cursor: portalScheduleActionBusy ? 'wait' : 'pointer'
                        }}
                        onClick={handlePortalConfirmSchedule}
                        disabled={portalScheduleActionBusy !== null}
                      >
                        {portalScheduleActionBusy === 'confirm' ? 'Confirming...' : 'Confirm schedule'}
                      </button>
                    ) : null}
                    {canPortalRequestSchedule ? (
                      <button
                        type='button'
                        style={{
                          padding: '0.72rem 1rem',
                          borderRadius: '0.55rem',
                          border: `1px solid ${BRAND_BORDER}`,
                          backgroundColor: BRAND_TEXT,
                          color: '#fff',
                          fontSize: '0.92rem',
                          fontWeight: 700,
                          cursor: portalScheduleActionBusy ? 'wait' : 'pointer'
                        }}
                        onClick={openPortalScheduleRequestModal}
                        disabled={portalScheduleActionBusy !== null}
                      >
                        {selectedPortalHasCommittedSchedule ? 'Request schedule change' : 'Suggest schedule'}
                      </button>
                    ) : null}
                  </div>
                </div>

                {selectedPortalHistoryTask ? (
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
                      gap: '0.7rem'
                    }}
                  >
                    <div
                      style={{
                        border: `1px solid ${selectedPortalHasScheduleRequest ? 'rgba(166, 59, 50, 0.22)' : BRAND_BORDER}`,
                        borderRadius: '0.75rem',
                        backgroundColor: selectedPortalHasScheduleRequest ? '#FFF7F5' : BRAND_SURFACE_SOFT,
                        padding: '0.7rem 0.78rem',
                        display: 'grid',
                        gap: '0.2rem'
                      }}
                    >
                      <div style={{ fontSize: '0.72rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.08em', color: BRAND_MUTED }}>
                        Planned Start
                      </div>
                      <div style={{ fontSize: '0.94rem', fontWeight: 700, color: selectedPortalHasScheduleRequest ? '#A63B32' : BRAND_TEXT }}>
                        {selectedPortalScheduleDisplayStartAt
                          ? formatPortalDateTime(selectedPortalScheduleDisplayStartAt.toISOString())
                          : 'Not set'}
                      </div>
                    </div>
                    <div
                      style={{
                        border: `1px solid ${selectedPortalHasScheduleRequest ? 'rgba(166, 59, 50, 0.22)' : BRAND_BORDER}`,
                        borderRadius: '0.75rem',
                        backgroundColor: selectedPortalHasScheduleRequest ? '#FFF7F5' : BRAND_SURFACE_SOFT,
                        padding: '0.7rem 0.78rem',
                        display: 'grid',
                        gap: '0.2rem'
                      }}
                    >
                      <div style={{ fontSize: '0.72rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.08em', color: BRAND_MUTED }}>
                        Planned Finish
                      </div>
                      <div style={{ fontSize: '0.94rem', fontWeight: 700, color: selectedPortalHasScheduleRequest ? '#A63B32' : BRAND_TEXT }}>
                        {selectedPortalScheduleDisplayEndAt
                          ? formatPortalDateTime(selectedPortalScheduleDisplayEndAt.toISOString())
                          : 'Not set'}
                      </div>
                    </div>
                    <div
                      style={{
                        border: `1px solid ${selectedPortalHasScheduleRequest ? 'rgba(166, 59, 50, 0.22)' : BRAND_BORDER}`,
                        borderRadius: '0.75rem',
                        backgroundColor: selectedPortalHasScheduleRequest ? '#FFF7F5' : BRAND_SURFACE_SOFT,
                        padding: '0.7rem 0.78rem',
                        display: 'grid',
                        gap: '0.2rem'
                      }}
                    >
                      <div style={{ fontSize: '0.72rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.08em', color: BRAND_MUTED }}>
                        Duration
                      </div>
                      <div style={{ fontSize: '0.94rem', fontWeight: 700, color: selectedPortalHasScheduleRequest ? '#A63B32' : BRAND_TEXT }}>
                        {selectedPortalScheduleDurationLabel}
                      </div>
                    </div>
                    <div
                      style={{
                        border: `1px solid ${selectedPortalHasScheduleRequest ? 'rgba(166, 59, 50, 0.22)' : BRAND_BORDER}`,
                        borderRadius: '0.75rem',
                        backgroundColor: selectedPortalHasScheduleRequest ? '#FFF7F5' : BRAND_SURFACE_SOFT,
                        padding: '0.7rem 0.78rem',
                        display: 'grid',
                        gap: '0.2rem'
                      }}
                    >
                      <div style={{ fontSize: '0.72rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.08em', color: BRAND_MUTED }}>
                        Planner Status
                      </div>
                      <div style={{ fontSize: '0.94rem', fontWeight: 700, color: BRAND_TEXT }}>
                        {selectedPortalPlannerStatusLabel}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div
                    style={{
                      padding: '0.85rem 0.95rem',
                      borderRadius: '0.8rem',
                      border: `1px dashed ${BRAND_BORDER}`,
                      backgroundColor: BRAND_SURFACE_SOFT,
                      color: BRAND_MUTED,
                      lineHeight: 1.5
                    }}
                  >
                    No active task is available for schedule confirmation yet.
                  </div>
                )}

                {portalScheduleNotice ? (
                  <div
                    style={{
                      padding: '0.85rem 0.95rem',
                      borderRadius: '0.8rem',
                      border: `1px solid ${portalScheduleNotice.type === 'error' ? '#f0c9c2' : '#bfd8cb'}`,
                      backgroundColor: portalScheduleNotice.type === 'error' ? '#fff7f4' : '#f3fbf5',
                      color: portalScheduleNotice.type === 'error' ? '#9f3f2d' : '#1b5e48',
                      lineHeight: 1.5
                    }}
                  >
                    {portalScheduleNotice.message}
                  </div>
                ) : null}
              </div>

              <div style={styles.historyDetailGrid}>
                <div style={styles.historyDetailCard}>
                  <div style={styles.historyDetailLabel}>Service date</div>
                  <div style={styles.historyDetailValue}>
                    {formatPortalDate(
                      selectedPortalHistoryCase.closedAt ||
                        selectedPortalHistoryCase.updatedAt ||
                        selectedPortalHistoryCase.createdAt
                    )}
                  </div>
                </div>
                <div style={styles.historyDetailCard}>
                  <div style={styles.historyDetailLabel}>Technician</div>
                  <div style={styles.historyDetailValue}>
                    {getPortalCaseTechnicianLabel(selectedPortalHistoryCase)}
                  </div>
                </div>
                <div style={styles.historyDetailCard}>
                  <div style={styles.historyDetailLabel}>Opened</div>
                  <div style={styles.historyDetailValue}>
                    {formatPortalDate(selectedPortalHistoryCase.createdAt)}
                  </div>
                </div>
                <div style={styles.historyDetailCard}>
                  <div style={styles.historyDetailLabel}>Case reference</div>
                  <div style={styles.historyDetailValue}>Case {selectedPortalHistoryCaseData.caseLabel}</div>
                </div>
              </div>

              {!isLitePortal && (
                <>
                  <div style={styles.historySectionTitle}>Customer-visible activity</div>
                  <div style={styles.historyTimeline}>
                    {selectedPortalHistoryCaseData.eventList.length === 0 && (
                      <div style={styles.historyEmpty}>No timeline updates recorded yet.</div>
                    )}
                    {selectedPortalHistoryCaseData.eventList.map(event => (
                      <div key={event.id} style={styles.historyTimelineItem}>
                        <div>{formatTimelineEventLabel(event)}</div>
                        <div style={styles.historyMeta}>
                          {event.actorName || 'Support team'} - {formatPortalDateTime(event.occurredAt)}
                        </div>
                      </div>
                    ))}
                  </div>

                  <div style={styles.historySectionTitle}>Surveys</div>
                  <div style={styles.historySubSection}>
                {selectedPortalHistoryCaseData.visibleCaseSurveys.length === 0 ? (
                  <>
                    <div>No survey linked to this case yet.</div>
                    <div style={styles.historySubMeta}>
                      Surveys appear here after the case is manually closed by the service team.
                    </div>
                  </>
                ) : (
                  selectedPortalHistoryCaseData.visibleCaseSurveys.map((survey: CaseSurveySummary) => {
                    const status = String(survey.status || '').trim().toUpperCase();
                    const isCompleted = status === 'COMPLETED';
                    const scoreDisplay = getPortalSurveyScoreDisplay(survey);
                    return (
                      <button
                        key={survey.id}
                        type='button'
                        style={styles.historyQuoteButton}
                        onClick={() => openSurveyFromCaseHistory(survey.id)}
                      >
                        <div>{survey.surveyName || 'Customer Satisfaction Survey'}</div>
                        <div style={styles.historySubMeta}>
                          {isCompleted
                            ? `Submitted ${formatPortalDateTime(survey.completedAt || survey.releasedAt)}`
                            : `Released ${formatPortalDateTime(survey.releasedAt)}`}
                          {survey.technicianName ? ` - ${survey.technicianName}` : ''}
                        </div>
                        <div
                          style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            gap: '0.75rem',
                            flexWrap: 'wrap'
                          }}
                        >
                          <span
                            style={{
                              ...styles.quoteDecisionPill,
                              ...(isCompleted ? styles.quoteDecisionApproved : styles.quoteDecisionPending)
                            }}
                          >
                            {isCompleted ? 'Completed' : 'Waiting for feedback'}
                          </span>
                          <span
                            style={{
                              ...styles.historySubMeta,
                              ...(isCompleted && scoreDisplay ? styles.surveyScoreRow : {})
                            }}
                          >
                            {isCompleted && scoreDisplay ? (
                              <>
                                <span>Score {scoreDisplay.scoreLabel}</span>
                                <span
                                  style={{
                                    ...styles.surveyPercentBadge,
                                    ...getPortalSurveyPercentageBadgeStyle(scoreDisplay)
                                  }}
                                >
                                  {scoreDisplay.percentageLabel}
                                </span>
                              </>
                            ) : (
                              `${survey.questionCount} question${survey.questionCount === 1 ? '' : 's'}`
                            )}
                          </span>
                        </div>
                      </button>
                    );
                  })
                )}
              </div>

              <div style={styles.historySectionTitle}>Quotes</div>
              <div style={styles.historySubSection}>
                {selectedPortalHistoryCaseData.visibleQuoteEvents.length === 0 && (
                  <>
                    <div>No quotes shared yet.</div>
                    <div style={styles.historySubMeta}>
                      Quotes will appear here once a service estimate is prepared.
                    </div>
                  </>
                )}
                {selectedPortalHistoryCaseData.visibleQuoteEvents.map(event => {
                  const quoteMeta = resolveQuoteEventMeta(event);
                  const quoteDecision = getQuoteDecisionState(selectedPortalHistoryCaseData.quoteDecisionByKey, event);
                  const quoteTitle = quoteMeta.title || 'Service quote';
                  const grandTotal = quoteMeta.grandTotal;
                  const quoteDecisionStatus: 'PENDING' | 'ACCEPT' | 'DENY' | 'ADJUST' | 'INACTIVE' =
                    quoteDecision?.decision || 'PENDING';
                  const quoteBadge = quoteDecisionBadge(quoteDecisionStatus);
                  return (
                    <button
                      key={event.id}
                      type='button'
                      style={styles.historyQuoteButton}
                      onClick={() => openPortalQuote(event, selectedPortalHistoryCase)}
                    >
                      <div>{quoteTitle}</div>
                      <div style={styles.historySubMeta}>
                        {event.actorName || 'Support team'} - {formatPortalDateTime(event.occurredAt)}
                        {grandTotal !== null ? ` - ${formatPortalCurrency(Number(grandTotal))}` : ''}
                      </div>
                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          gap: '0.75rem',
                          flexWrap: 'wrap'
                        }}
                      >
                        <span style={quoteBadge.style}>{quoteBadge.label}</span>
                        {quoteDecision && (
                          <span style={styles.historySubMeta}>
                            {quoteDecision.occurredAt ? formatPortalDateTime(quoteDecision.occurredAt) : ''}
                            {quoteDecision.actorName ? ` - ${quoteDecision.actorName}` : ''}
                          </span>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>

              <div style={styles.historySectionTitle}>Service reports</div>
              <div style={styles.historySubSection}>
                {selectedPortalHistoryCaseData.visibleServiceReports.length === 0 ? (
                  <>
                    <div>No service reports available yet.</div>
                    <div style={styles.historySubMeta}>
                      Sent service reports and visit summaries will be listed here.
                    </div>
                  </>
                ) : (
                  selectedPortalHistoryCaseData.visibleServiceReports.map(report => (
                    <button
                      key={report.id}
                      type='button'
                      style={styles.historyQuoteButton}
                      onClick={() => openPortalServiceReport(report, selectedPortalHistoryCase)}
                    >
                      <div>{report.title || 'Service report'}</div>
                      <div style={styles.historySubMeta}>
                        {report.savedAt ? formatPortalDateTime(report.savedAt) : 'Saved service report'}
                        {report.templateName ? ` - ${report.templateName}` : ''}
                      </div>
                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          gap: '0.75rem',
                          flexWrap: 'wrap'
                        }}
                      >
                        <span style={{ ...styles.quoteDecisionPill, ...styles.quoteDecisionApproved }}>
                          Shared with customer
                        </span>
                        {report.savedByName && (
                          <span style={styles.historySubMeta}>{report.savedByName}</span>
                        )}
                      </div>
                    </button>
                  ))
                )}
              </div>
                </>
              )}
            </div>
          </div>
        )}

        {portalScheduleRequestModalOpen && portalScheduleRequestDraft && selectedPortalHistoryCase && (
          <div
            style={styles.historyOverlay}
            role='dialog'
            aria-modal='true'
            aria-label='Request schedule'
          >
            <div
              style={{
                ...styles.quotePanel,
                maxWidth: '760px',
                width: 'min(760px, calc(100vw - 2rem))',
                padding: '1.3rem'
              }}
            >
              <div style={styles.historyHeader}>
                <div>
                  <div style={styles.quoteModalTitle}>Request Schedule</div>
                  <div style={styles.quoteModalMeta}>
                    {selectedPortalHistoryCase.caseNumber || selectedPortalHistoryCase.caseCode || selectedPortalHistoryCase.id}
                    {selectedPortalHistoryCase.device?.modelNumber
                      ? ` | ${selectedPortalHistoryCase.device.modelNumber}`
                      : ''}
                  </div>
                </div>
                <button
                  type='button'
                  style={styles.historyClose}
                  onClick={closePortalScheduleRequestModal}
                  disabled={portalScheduleActionBusy === 'request'}
                >
                  Close
                </button>
              </div>

              <div style={{ display: 'grid', gap: '0.9rem' }}>
                <div
                  style={{
                    border: `1px solid ${BRAND_BORDER}`,
                    borderRadius: '0.9rem',
                    backgroundColor: BRAND_SURFACE_SOFT,
                    padding: '0.95rem 1rem',
                    display: 'grid',
                    gap: '0.35rem'
                  }}
                >
                  <div style={{ fontSize: '0.76rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.08em', color: BRAND_MUTED }}>
                    Suggested Window
                  </div>
                  <div style={{ fontSize: '1.15rem', fontWeight: 700, color: BRAND_TEXT }}>
                    {portalScheduleRequestStartPreview && portalScheduleRequestEndPreview
                      ? `${formatPortalDateTime(portalScheduleRequestStartPreview.toISOString())} to ${formatPortalDateTime(portalScheduleRequestEndPreview.toISOString())}`
                      : 'Not set'}
                  </div>
                </div>

                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
                    gap: '0.85rem'
                  }}
                >
                  <div
                    style={{
                      border: `1px solid ${BRAND_BORDER}`,
                      borderRadius: '0.9rem',
                      backgroundColor: '#fffdfa',
                      padding: '0.95rem 1rem',
                      display: 'grid',
                      gap: '0.8rem'
                    }}
                  >
                    <div style={{ display: 'grid', gap: '0.15rem' }}>
                      <div style={{ fontSize: '1rem', fontWeight: 700, color: BRAND_TEXT }}>Requested Start</div>
                    </div>
                    <div style={{ display: 'grid', gap: '0.35rem' }}>
                      <label style={{ fontSize: '0.78rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.08em', color: BRAND_TEXT }}>
                        Date
                      </label>
                      <input
                        type='date'
                        value={portalScheduleRequestDraft.startDate}
                        onChange={event => updatePortalScheduleRequestStartField('startDate', event.target.value)}
                        style={{
                          minHeight: '3.1rem',
                          padding: '0.82rem 0.9rem',
                          borderRadius: '0.72rem',
                          border: `1px solid ${BRAND_BORDER}`,
                          fontSize: '1rem',
                          fontWeight: 700,
                          color: BRAND_TEXT
                        }}
                      />
                    </div>
                    <div style={{ display: 'grid', gap: '0.35rem' }}>
                      <label style={{ fontSize: '0.78rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.08em', color: BRAND_TEXT }}>
                        Time
                      </label>
                      <input
                        type='time'
                        value={portalScheduleRequestDraft.startTime}
                        onChange={event => updatePortalScheduleRequestStartField('startTime', event.target.value)}
                        style={{
                          minHeight: '3.1rem',
                          padding: '0.82rem 0.9rem',
                          borderRadius: '0.72rem',
                          border: `1px solid ${BRAND_BORDER}`,
                          fontSize: '1rem',
                          fontWeight: 700,
                          color: BRAND_TEXT
                        }}
                      />
                    </div>
                  </div>

                  <div
                    style={{
                      border: `1px solid ${BRAND_BORDER}`,
                      borderRadius: '0.9rem',
                      backgroundColor: '#fffdfa',
                      padding: '0.95rem 1rem',
                      display: 'grid',
                      gap: '0.8rem'
                    }}
                  >
                    <div style={{ display: 'grid', gap: '0.15rem' }}>
                      <div style={{ fontSize: '1rem', fontWeight: 700, color: BRAND_TEXT }}>Requested Finish</div>
                    </div>
                    <div style={{ display: 'grid', gap: '0.35rem' }}>
                      <label style={{ fontSize: '0.78rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.08em', color: BRAND_TEXT }}>
                        Date
                      </label>
                      <input
                        type='date'
                        value={portalScheduleRequestDraft.endDate}
                        min={portalScheduleRequestDraft.startDate || undefined}
                        onChange={event => updatePortalScheduleRequestEndField('endDate', event.target.value)}
                        style={{
                          minHeight: '3.1rem',
                          padding: '0.82rem 0.9rem',
                          borderRadius: '0.72rem',
                          border: `1px solid ${BRAND_BORDER}`,
                          fontSize: '1rem',
                          fontWeight: 700,
                          color: BRAND_TEXT
                        }}
                      />
                    </div>
                    <div style={{ display: 'grid', gap: '0.35rem' }}>
                      <label style={{ fontSize: '0.78rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.08em', color: BRAND_TEXT }}>
                        Time
                      </label>
                      <input
                        type='time'
                        value={portalScheduleRequestDraft.endTime}
                        onChange={event => updatePortalScheduleRequestEndField('endTime', event.target.value)}
                        style={{
                          minHeight: '3.1rem',
                          padding: '0.82rem 0.9rem',
                          borderRadius: '0.72rem',
                          border: `1px solid ${BRAND_BORDER}`,
                          fontSize: '1rem',
                          fontWeight: 700,
                          color: BRAND_TEXT
                        }}
                      />
                    </div>
                  </div>
                </div>

                {portalScheduleRequestDraft.validationMessage ? (
                  <div
                    style={{
                      padding: '0.85rem 0.95rem',
                      borderRadius: '0.8rem',
                      border: '1px solid #f0c9c2',
                      backgroundColor: '#fff7f4',
                      color: '#9f3f2d',
                      lineHeight: 1.5
                    }}
                  >
                    {portalScheduleRequestDraft.validationMessage}
                  </div>
                ) : null}

                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem', flexWrap: 'wrap' }}>
                  <button
                    type='button'
                    style={{
                      padding: '0.75rem 1rem',
                      borderRadius: '0.55rem',
                      border: `1px solid ${BRAND_BORDER}`,
                      backgroundColor: '#fffdfa',
                      color: BRAND_TEXT,
                      fontSize: '0.92rem',
                      fontWeight: 700
                    }}
                    onClick={closePortalScheduleRequestModal}
                    disabled={portalScheduleActionBusy === 'request'}
                  >
                    Cancel
                  </button>
                  <button
                    type='button'
                    style={{
                      padding: '0.75rem 1rem',
                      borderRadius: '0.55rem',
                      border: `1px solid ${BRAND_TEXT}`,
                      backgroundColor: BRAND_TEXT,
                      color: '#fff',
                      fontSize: '0.92rem',
                      fontWeight: 700
                    }}
                    onClick={handlePortalRequestSchedule}
                    disabled={portalScheduleActionBusy === 'request'}
                  >
                    {portalScheduleActionBusy === 'request' ? 'Saving...' : 'Request schedule'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {(portalQuoteLoading || portalQuoteError || selectedPortalQuote) && (
          <div style={styles.historyOverlay} role='dialog' aria-modal='true' aria-label='Quote details'>
            <div style={styles.quotePanel}>
              <div style={styles.historyHeader}>
                <div>
                  <div style={styles.quoteModalTitle}>
                    {selectedPortalQuote?.title || 'Service quote'}
                  </div>
                  <div style={styles.quoteModalMeta}>
                    {selectedPortalQuote?.actorName || 'Support team'}
                    {selectedPortalQuote?.occurredAt
                      ? ` - ${formatPortalDateTime(selectedPortalQuote.occurredAt)}`
                      : ''}
                  </div>
                </div>
                <button
                  type='button'
                  style={styles.historyClose}
                  onClick={() => {
                    setSelectedPortalQuote(null);
                    setSelectedPortalQuoteCase(null);
                    setPortalQuoteError('');
                    setPortalQuoteNotice('');
                    setPortalQuoteAdjustOpen(false);
                    setPortalQuoteAdjustNote('');
                    setPortalQuoteAdjustError('');
                    setPortalQuoteLoading(false);
                    setPortalQuoteResponding(false);
                  }}
                  aria-label='Close quote details'
                >
                  Close
                </button>
              </div>
              <div style={styles.historyBody}>
                {portalQuoteLoading && <div style={styles.historyState}>Loading quote...</div>}
                {!portalQuoteLoading && portalQuoteError && (
                  <div style={{ ...styles.historyState, color: '#b91c1c' }}>{portalQuoteError}</div>
                )}
                {!portalQuoteLoading && !portalQuoteError && portalQuoteNotice && (
                  <div style={{ ...styles.historyState, color: '#166534', padding: '0.75rem 0' }}>{portalQuoteNotice}</div>
                )}
                {!portalQuoteLoading && !portalQuoteError && selectedPortalQuote && (
                  <>
                    <div style={styles.quoteModalSection}>
                      <div style={styles.historySectionTitle}>
                        Print preview: {selectedPortalQuote?.templateName || 'Standard Service Quote'}
                      </div>
                      <div style={styles.quoteModalMeta}>Email / print quote layout</div>
                    </div>

                    <div style={styles.quoteModalSection}>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: '0.75rem' }}>
                        <div style={{ border: '1px solid #cbd5e1', borderRadius: '0.65rem', padding: '0.7rem 0.75rem' }}>
                          <div style={{ fontWeight: 700, marginBottom: '0.25rem' }}>Customer Information</div>
                          <div style={{ fontSize: '0.92rem', lineHeight: 1.45 }}>
                            <span style={{ color: '#64748b' }}>Customer Name: </span>
                            <span>{selectedPortalQuoteCase?.customer?.companyName || selectedPortalQuoteCase?.customer?.company || 'Not provided'}</span>
                          </div>
                          <div style={{ fontSize: '0.92rem', lineHeight: 1.45 }}>
                            <span style={{ color: '#64748b' }}>Contact Name: </span>
                            <span>{selectedPortalQuoteCase?.customer?.contacts?.[0]?.fullName || 'Not provided'}</span>
                          </div>
                          <div style={{ fontSize: '0.92rem', lineHeight: 1.45 }}>
                            <span style={{ color: '#64748b' }}>Contact Email: </span>
                            <span>{selectedPortalQuoteCase?.customer?.contacts?.[0]?.email || 'Not provided'}</span>
                          </div>
                        </div>
                        <div style={{ border: '1px solid #cbd5e1', borderRadius: '0.65rem', padding: '0.7rem 0.75rem' }}>
                          <div style={{ fontWeight: 700, marginBottom: '0.25rem' }}>Device Information</div>
                          <div style={{ fontSize: '0.92rem', lineHeight: 1.45 }}>
                            <span style={{ color: '#64748b' }}>Model Number: </span>
                            <span>{selectedPortalQuoteCase?.device?.modelNumber || 'Not provided'}</span>
                          </div>
                          <div style={{ fontSize: '0.92rem', lineHeight: 1.45 }}>
                            <span style={{ color: '#64748b' }}>Identifier: </span>
                            <span>
                              {selectedPortalQuoteCase?.device
                                ? formatDeviceIdentifier(selectedPortalQuoteCase.device as any)
                                : 'Not provided'}
                            </span>
                          </div>
                        </div>
                        <div style={{ border: '1px solid #cbd5e1', borderRadius: '0.65rem', padding: '0.7rem 0.75rem' }}>
                          <div style={{ fontWeight: 700, marginBottom: '0.25rem' }}>Case Information</div>
                          <div style={{ fontSize: '0.92rem', lineHeight: 1.45 }}>
                            <span style={{ color: '#64748b' }}>Case Number: </span>
                            <span>{selectedPortalQuoteCase?.caseCode || selectedPortalQuoteCase?.caseNumber || 'Not provided'}</span>
                          </div>
                          <div style={{ fontSize: '0.92rem', lineHeight: 1.45 }}>
                            <span style={{ color: '#64748b' }}>Case Priority: </span>
                            <span>{selectedPortalQuoteCase?.priority || 'Not provided'}</span>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div style={styles.quoteModalSection}>
                      <div style={{ border: '1px solid #d1d5db', borderRadius: '0.65rem', padding: '0.75rem', whiteSpace: 'pre-wrap' as const }}>
                        {selectedPortalQuote?.description || selectedPortalQuoteCase?.problemDescription || 'Quote generated from technician findings and suggested parts/labor.'}
                      </div>
                    </div>

                    <div style={styles.quoteModalSection}>
                      {(() => {
                        const quoteLineColumns = getIncludedQuoteLineColumns((selectedPortalQuote as any)?.lineColumns);
                        return (
                          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                            <thead>
                              <tr>
                                {quoteLineColumns.map(column => (
                                  <th
                                    key={`portal-quote-head-${column.id}`}
                                    style={{
                                      textAlign: column.align,
                                      padding: '0.45rem',
                                      borderBottom: '1px solid #cbd5e1',
                                      verticalAlign: 'top'
                                    }}
                                  >
                                    {column.label}
                                  </th>
                                ))}
                              </tr>
                            </thead>
                            <tbody>
                              {!Array.isArray(selectedPortalQuote?.lineItems) ||
                              selectedPortalQuote.lineItems.length === 0 ? (
                                <tr>
                                  <td
                                    colSpan={Math.max(quoteLineColumns.length, 1)}
                                    style={{ padding: '0.45rem', borderBottom: '1px solid #e5e7eb', color: '#64748b' }}
                                  >
                                    No line-item details available.
                                  </td>
                                </tr>
                              ) : (
                                selectedPortalQuote.lineItems.map((line: any, index: number) => (
                                  <tr key={line.id || `${line.description || 'line'}-${index}`}>
                                    {quoteLineColumns.map(column => (
                                      <td
                                        key={`${line.id || 'line'}-${index}-${column.id}`}
                                        style={{
                                          padding: '0.45rem',
                                          textAlign: column.align,
                                          borderBottom: '1px solid #e5e7eb',
                                          verticalAlign: 'top'
                                        }}
                                      >
                                        {getQuoteLineColumnDisplayValue(line, column.id, { formatCurrency: formatPortalCurrency })}
                                      </td>
                                    ))}
                                  </tr>
                                ))
                              )}
                            </tbody>
                          </table>
                        );
                      })()}
                    </div>

                    {(() => {
                      const approvalSettings =
                        selectedPortalQuote?.totals?.approvalSettings &&
                        typeof selectedPortalQuote.totals.approvalSettings === 'object'
                          ? selectedPortalQuote.totals.approvalSettings
                          : {};
                      const placementRaw = String(
                        (selectedPortalQuote as any)?.approvalPlacement ||
                          (approvalSettings as any)?.approvalPlacement ||
                          ''
                      ).trim();
                      const placement =
                        placementRaw === 'footer_c1' || placementRaw === 'footer_c2' || placementRaw === 'footer_c3'
                          ? placementRaw
                          : placementRaw === 'footer'
                            ? 'footer_c1'
                            : 'footer_c3';
                      return null;
                      const requireCustomerSignature =
                        typeof (selectedPortalQuote as any)?.requireSignature === 'boolean'
                          ? (selectedPortalQuote as any).requireSignature
                          : typeof (approvalSettings as any)?.requireSignature === 'boolean'
                            ? (approvalSettings as any).requireSignature
                            : true;
                      const requireTechnicianSignature =
                        typeof (selectedPortalQuote as any)?.requireTechnicianSignature === 'boolean'
                          ? (selectedPortalQuote as any).requireTechnicianSignature
                          : typeof (approvalSettings as any)?.requireTechnicianSignature === 'boolean'
                            ? (approvalSettings as any).requireTechnicianSignature
                            : false;
                      if (!requireCustomerSignature && !requireTechnicianSignature) return null;
                      return (
                        <div style={{ ...styles.quoteModalSection, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '0.7rem' }}>
                          {requireCustomerSignature && (
                            <div>
                              <div style={{ fontWeight: 700, marginBottom: '0.35rem' }}>
                                {(selectedPortalQuote as any)?.signatureLabel ||
                                  (approvalSettings as any)?.signatureLabel ||
                                  'Customer approval signature'}
                              </div>
                              <div style={{ fontSize: '0.95rem', lineHeight: 1.45 }}>
                                <div>Name: {selectedPortalQuote.customerApprovalName || '__________________'}</div>
                                <div style={{ paddingTop: '1.35rem' }}>Signature: {selectedPortalQuote.customerApprovalSignature || '__________________'}</div>
                              </div>
                            </div>
                          )}
                          {requireTechnicianSignature && (
                            <div>
                              <div style={{ fontWeight: 700, marginBottom: '0.35rem' }}>
                                {(selectedPortalQuote as any)?.technicianSignatureLabel ||
                                  (approvalSettings as any)?.technicianSignatureLabel ||
                                  'Technician signature'}
                              </div>
                              <div style={{ fontSize: '0.95rem', lineHeight: 1.45 }}>
                                <div>Name: __________________</div>
                                <div style={{ paddingTop: '1.35rem' }}>Signature: __________________</div>
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })()}

                    <div style={styles.quoteModalSection}>
                      <div style={{ display: 'grid', justifyContent: 'end', gap: '0.25rem', marginTop: '0.2rem' }}>
                        {(() => {
                          const totalRows = buildQuoteTotalDisplayRowsFromSummary(
                            selectedPortalQuote?.totals,
                            (selectedPortalQuote as any)?.totalSettings || selectedPortalQuote?.totals?.totalSettings
                          );
                          const rows =
                            totalRows.length > 0
                              ? totalRows
                              : [
                                  {
                                    id: 'grandTotal',
                                    label: 'Total',
                                    value:
                                      typeof selectedPortalQuote?.totals?.grandTotal === 'number'
                                        ? Number(selectedPortalQuote.totals.grandTotal)
                                        : 0,
                                    emphasised: true
                                  }
                                ];
                          return rows.map(row => (
                            <div
                              key={`portal-quote-total-${row.id}`}
                              style={{
                                display: 'grid',
                                gridTemplateColumns: 'auto minmax(90px, auto)',
                                gap: '1rem',
                                textAlign: 'right',
                                ...(row.emphasised
                                  ? {
                                      borderTop: '1px solid #cbd5e1',
                                      paddingTop: '0.35rem',
                                      fontWeight: 800,
                                      fontSize: '1.05rem'
                                    }
                                  : { fontSize: '0.95rem' })
                              }}
                            >
                              <span>{row.label}</span>
                              <strong>{formatQuoteTotalRowValue(row, formatPortalCurrency)}</strong>
                            </div>
                          ));
                        })()}
                      </div>
                      {selectedPortalQuote?.customerDecision && (
                        <div style={{ marginTop: '0.45rem', color: '#0f172a', fontWeight: 600 }}>
                          {selectedPortalQuote.customerDecision === 'ACCEPT'
                            ? 'Customer accepted'
                            : selectedPortalQuote.customerDecision === 'DENY'
                            ? 'Customer denied'
                            : 'Customer requested adjustment'}
                          {selectedPortalQuote.decisionAt
                            ? ` on ${formatPortalDateTime(selectedPortalQuote.decisionAt)}`
                            : ''}
                          {selectedPortalQuote.decisionBy ? ` by ${selectedPortalQuote.decisionBy}` : ''}
                        </div>
                      )}
                      {String(selectedPortalQuote?.status || '').trim().toUpperCase() === 'INACTIVE' && (
                        <div style={{ marginTop: '0.45rem', color: '#92400e', fontWeight: 600 }}>
                          Quote is inactive.
                          {selectedPortalQuote?.inactiveReason
                            ? ` Reason: ${selectedPortalQuote.inactiveReason}`
                            : ''}
                        </div>
                      )}
                      {(() => {
                        const responseActions = Array.isArray((selectedPortalQuote as any)?.responseActions)
                          ? (selectedPortalQuote as any).responseActions
                              .map((action: any) => String(action || '').trim().toLowerCase())
                              .filter((action: string) => ['accept', 'deny', 'adjust'].includes(action))
                          : Array.isArray(selectedPortalQuote?.totals?.approvalSettings?.responseActions)
                            ? selectedPortalQuote.totals.approvalSettings.responseActions
                                .map((action: any) => String(action || '').trim().toLowerCase())
                                .filter((action: string) => ['accept', 'deny', 'adjust'].includes(action))
                            : ['accept', 'deny', 'adjust'];
                        if (
                          selectedPortalQuote?.customerDecision ||
                          String(selectedPortalQuote?.status || '').trim().toUpperCase() === 'INACTIVE' ||
                          responseActions.length === 0
                        ) {
                          return null;
                        }
                        return (
                          <div style={{ display: 'flex', gap: '0.6rem', marginTop: '0.75rem', marginBottom: '0.1rem', flexWrap: 'wrap' as const }}>
                            {responseActions.includes('accept') && (
                              <button
                                type='button'
                                style={{ ...styles.historyActionButton, borderRadius: '999px' }}
                                onClick={() => handlePortalQuoteDecision('ACCEPT')}
                                disabled={portalQuoteResponding}
                              >
                                {portalQuoteResponding ? 'Saving...' : 'ACCEPT'}
                              </button>
                            )}
                            {responseActions.includes('deny') && (
                              <button
                                type='button'
                                style={{ ...styles.historyActionButton, borderRadius: '999px' }}
                                onClick={() => handlePortalQuoteDecision('DENY')}
                                disabled={portalQuoteResponding}
                              >
                                DENY
                              </button>
                            )}
                            {responseActions.includes('adjust') && (
                              <button
                                type='button'
                                style={{ ...styles.historyActionButton, borderRadius: '999px' }}
                                onClick={() => {
                                  setPortalQuoteAdjustOpen(true);
                                  setPortalQuoteAdjustError('');
                                }}
                                disabled={portalQuoteResponding}
                              >
                                ADJUST
                              </button>
                            )}
                          </div>
                        );
                      })()}
                      {portalQuoteAdjustOpen &&
                        !selectedPortalQuote?.customerDecision &&
                        String(selectedPortalQuote?.status || '').trim().toUpperCase() !== 'INACTIVE' &&
                        (Array.isArray((selectedPortalQuote as any)?.responseActions)
                          ? (selectedPortalQuote as any).responseActions
                          : Array.isArray(selectedPortalQuote?.totals?.approvalSettings?.responseActions)
                            ? selectedPortalQuote.totals.approvalSettings.responseActions
                            : ['adjust']
                        ).map((action: any) => String(action || '').trim().toLowerCase()).includes('adjust') && (
                          <div
                            style={{
                              marginTop: '0.75rem',
                              border: '1px solid #cbd5e1',
                              borderRadius: '0.65rem',
                              padding: '0.75rem',
                              background: '#f8fafc'
                            }}
                          >
                            <div style={{ fontWeight: 700, marginBottom: '0.4rem' }}>Adjustment details</div>
                            <textarea
                              value={portalQuoteAdjustNote}
                              onChange={(event) => {
                                setPortalQuoteAdjustNote(event.target.value);
                                if (portalQuoteAdjustError) setPortalQuoteAdjustError('');
                              }}
                              placeholder='What changes are needed for this quote?'
                              style={{
                                width: '100%',
                                minHeight: '90px',
                                resize: 'vertical',
                                border: '1px solid #cbd5e1',
                                borderRadius: '0.5rem',
                                padding: '0.55rem 0.6rem',
                                fontFamily: 'inherit',
                                fontSize: '0.92rem'
                              }}
                              disabled={portalQuoteResponding}
                            />
                            {portalQuoteAdjustError && (
                              <div style={{ marginTop: '0.45rem', color: '#b91c1c', fontSize: '0.84rem' }}>
                                {portalQuoteAdjustError}
                              </div>
                            )}
                            <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.6rem', flexWrap: 'wrap' }}>
                              <button
                                type='button'
                                style={{ ...styles.historyActionButton, borderRadius: '999px' }}
                                onClick={() => handlePortalQuoteDecision('ADJUST', portalQuoteAdjustNote)}
                                disabled={portalQuoteResponding}
                              >
                                {portalQuoteResponding ? 'Saving...' : 'Submit Adjustment'}
                              </button>
                              <button
                                type='button'
                                style={{ ...styles.historyActionButton, borderRadius: '999px' }}
                                onClick={() => {
                                  if (portalQuoteResponding) return;
                                  setPortalQuoteAdjustOpen(false);
                                  setPortalQuoteAdjustNote('');
                                  setPortalQuoteAdjustError('');
                                }}
                                disabled={portalQuoteResponding}
                              >
                                Cancel
                              </button>
                            </div>
                          </div>
                        )}
                    </div>

                    {(() => {
                      const approvalSettings =
                        selectedPortalQuote?.totals?.approvalSettings &&
                        typeof selectedPortalQuote.totals.approvalSettings === 'object'
                          ? selectedPortalQuote.totals.approvalSettings
                          : {};
                      const placementRaw = String(
                        (selectedPortalQuote as any)?.approvalPlacement ||
                          (approvalSettings as any)?.approvalPlacement ||
                          ''
                      ).trim();
                      const placement =
                        placementRaw === 'footer_c1' || placementRaw === 'footer_c2' || placementRaw === 'footer_c3'
                          ? placementRaw
                          : placementRaw === 'footer'
                            ? 'footer_c1'
                            : 'footer_c3';
                      if (!placement.startsWith('footer_')) return null;
                      const requireCustomerSignature =
                        typeof (selectedPortalQuote as any)?.requireSignature === 'boolean'
                          ? (selectedPortalQuote as any).requireSignature
                          : typeof (approvalSettings as any)?.requireSignature === 'boolean'
                            ? (approvalSettings as any).requireSignature
                            : true;
                      const requireTechnicianSignature =
                        typeof (selectedPortalQuote as any)?.requireTechnicianSignature === 'boolean'
                          ? (selectedPortalQuote as any).requireTechnicianSignature
                          : typeof (approvalSettings as any)?.requireTechnicianSignature === 'boolean'
                            ? (approvalSettings as any).requireTechnicianSignature
                            : false;
                      if (!requireCustomerSignature && !requireTechnicianSignature) return null;
                      const footerSlotOrder = ['footer_c1', 'footer_c2', 'footer_c3'];
                      const selectedSlots: string[] = [];
                      const addSlot = (value: any) => {
                        const raw = String(value || '').trim();
                        if ((raw === 'footer_c1' || raw === 'footer_c2' || raw === 'footer_c3') && !selectedSlots.includes(raw)) {
                          selectedSlots.push(raw);
                        }
                      };
                      const rawSlots = (selectedPortalQuote as any)?.approvalFooterSlots || (approvalSettings as any)?.approvalFooterSlots;
                      if (Array.isArray(rawSlots)) rawSlots.forEach(addSlot);
                      if (selectedSlots.length === 0) addSlot(placement);
                      selectedSlots.sort((a, b) => footerSlotOrder.indexOf(a) - footerSlotOrder.indexOf(b));
                      const selectedColumns = selectedSlots.map(slot => footerSlotOrder.indexOf(slot) + 1);
                      const startColumn = Math.min(...selectedColumns);
                      const endColumn = Math.max(...selectedColumns);
                      const span = endColumn - startColumn + 1;
                      const signatureBlock = (
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '0.7rem' }}>
                          {requireCustomerSignature && (
                            <div>
                              <div style={{ fontWeight: 700, marginBottom: '0.35rem' }}>
                                {(selectedPortalQuote as any)?.signatureLabel ||
                                  (approvalSettings as any)?.signatureLabel ||
                                  'Customer approval signature'}
                              </div>
                              <div style={{ fontSize: '0.95rem', lineHeight: 1.45 }}>
                                <div>Name: {selectedPortalQuote.customerApprovalName || '__________________'}</div>
                                <div style={{ paddingTop: '1.35rem' }}>Signature: {selectedPortalQuote.customerApprovalSignature || '__________________'}</div>
                              </div>
                            </div>
                          )}
                          {requireTechnicianSignature && (
                            <div>
                              <div style={{ fontWeight: 700, marginBottom: '0.35rem' }}>
                                {(selectedPortalQuote as any)?.technicianSignatureLabel ||
                                  (approvalSettings as any)?.technicianSignatureLabel ||
                                  'Technician signature'}
                              </div>
                              <div style={{ fontSize: '0.95rem', lineHeight: 1.45 }}>
                                <div>Name: __________________</div>
                                <div style={{ paddingTop: '1.35rem' }}>Signature: __________________</div>
                              </div>
                            </div>
                          )}
                        </div>
                      );
                      return (
                        <div style={{ ...styles.quoteModalSection, display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: '0.7rem' }}>
                          {[1, 2, 3].map(column => {
                            if (column < startColumn || column > endColumn) {
                              return <div key={`portal-quote-footer-${column}`} />;
                            }
                            if (column !== startColumn) return null;
                            return (
                              <div key={`portal-quote-footer-${column}`} style={{ gridColumn: `span ${span}` }}>
                                {signatureBlock}
                              </div>
                            );
                          })}
                        </div>
                      );
                    })()}

                    {selectedPortalQuote?.notes && (
                      <div style={styles.quoteModalSection}>
                        <div style={styles.historyDetailLabel}>Notes</div>
                        <div style={styles.historySubMeta}>{selectedPortalQuote.notes}</div>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        )}
        {renderOverviewQueueModal()}
        {renderPortalSurveyModal()}
        <AppNotificationPopups
          notifications={portalNotificationPopups}
          formatDateTime={(value) => formatPortalDateTime(value ? String(value) : undefined)}
          formatAttachmentSize={formatAttachmentSize}
          onDismiss={handleDismissPortalNotification}
          onOpenAttachment={handleOpenPortalNotificationAttachment}
          styles={styles}
        />
        <RepairServiceReportModal
          open={Boolean(selectedPortalServiceReport)}
          document={selectedPortalServiceReport?.document || null}
          saving={false}
          error={null}
          mode="view"
          canSend={false}
          subtitle={
            selectedPortalServiceReport
              ? `${selectedPortalServiceReport.title || 'Service report'} | Template: ${selectedPortalServiceReport.templateName || 'Service Report'} | Shared service report${
                  selectedPortalServiceReportCase?.caseCode || selectedPortalServiceReportCase?.caseNumber
                    ? ` for ${selectedPortalServiceReportCase.caseCode || selectedPortalServiceReportCase.caseNumber}`
                    : ''
                }`
              : undefined
          }
          hint="This report snapshot was shared by your service team for this case."
          onClose={() => {
            setSelectedPortalServiceReport(null);
            setSelectedPortalServiceReportCase(null);
          }}
          onSave={() => {}}
        />
      </div>
    </div>
  );
}
